// frontend/src/i18n/translations/en.ts
/**
 * English (en) - Source Translation Dictionary
 *
 * This is the PRIMARY source of truth for all translatable strings.
 * All other language files are derived from this one.
 *
 * TRANSLATION GUIDELINES:
 * - Every entry must have a detailed `context` description
 * - Use dot notation for keys: category.subcategory.identifier
 * - Include maxLength for space-constrained UI elements
 * - Document all placeholders with clear descriptions
 * - Add translatorNotes for cultural/medical terms
 */

import { TranslationDictionary } from "../types";

export const en: TranslationDictionary = {
  // ============================================================================
  // NAVIGATION
  // ============================================================================
  "nav.dashboard": {
    text: "Dashboard",
    context:
      "Main navigation link in the top navbar. Takes user to the main dashboard showing patient overview and quick actions.",
    category: "navigation",
    maxLength: 15,
    needsTranslation: true,
  },
  "nav.patients": {
    text: "Patients",
    context:
      "Navigation link to the patient list/management page. Shows all patients the user has access to.",
    category: "navigation",
    maxLength: 15,
    needsTranslation: true,
  },
  "nav.billing": {
    text: "Billing",
    context:
      "Navigation link to billing/subscription management. Shows usage, invoices, and plan options.",
    category: "navigation",
    maxLength: 15,
    needsTranslation: true,
  },
  "nav.settings": {
    text: "Settings",
    context: "Navigation link to user/account settings page.",
    category: "navigation",
    maxLength: 15,
    needsTranslation: true,
  },
  "nav.logout": {
    text: "Log out",
    context:
      "Action link in user menu dropdown. Ends the current session and returns to login.",
    category: "navigation",
    maxLength: 15,
    needsTranslation: true,
  },
  "nav.theme": {
    text: "Theme",
    context:
      "Label for theme selection submenu in user dropdown. Allows switching between visual themes.",
    category: "navigation",
    maxLength: 10,
    needsTranslation: true,
  },
  "nav.select_theme": {
    text: "SELECT THEME",
    context: "Header label in theme selection dropdown menu.",
    category: "navigation",
    maxLength: 15,
    needsTranslation: true,
  },

  // ============================================================================
  // ACTIONS (Buttons, CTAs)
  // ============================================================================
  "action.save": {
    text: "Save",
    context:
      "Primary action button to persist changes. Used in forms, modals, and editors.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.cancel": {
    text: "Cancel",
    context:
      "Secondary button to discard changes and close the current form/modal without saving.",
    category: "actions",
    maxLength: 10,
    relatedKeys: ["action.save"],
    needsTranslation: true,
  },
  "action.delete": {
    text: "Delete",
    context:
      "Destructive action button. Removes the selected item permanently. Usually requires confirmation.",
    category: "actions",
    maxLength: 10,
    translatorNotes: "Use a word that clearly conveys permanent removal.",
    needsTranslation: true,
  },
  "action.edit": {
    text: "Edit",
    context:
      "Button to enter edit mode for an existing item. Opens editor or enables inline editing.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.create": {
    text: "Create",
    context:
      "Button to create a new item (patient, record, etc.). Opens creation form.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.add": {
    text: "Add",
    context:
      "Button to add a new item to a list or collection. Similar to create but for nested items.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.submit": {
    text: "Submit",
    context:
      "Button to submit a form for processing. Used when data needs backend validation.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.confirm": {
    text: "Confirm",
    context:
      "Button in confirmation dialogs. Affirms the user wants to proceed with an action.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.close": {
    text: "Close",
    context:
      "Button to close a modal/dialog without any action. Does not save changes.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.done": {
    text: "Done",
    context:
      "Button to dismiss a multi-select popover/dialog after the user has finished selecting. Equivalent to 'OK' but warmer.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.remove": {
    text: "Remove",
    context:
      "Aria-label for the small ✕ icon button that removes an item (a patient card, a file chip, an environment selection).",
    category: "actions",
    maxLength: 12,
    needsTranslation: true,
  },
  "action.back": {
    text: "Back",
    context:
      "Navigation button to return to previous page or step in a wizard.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.next": {
    text: "Next",
    context:
      "Navigation button to proceed to next step in a multi-step form/wizard.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.search": {
    text: "Search",
    context:
      "Button or placeholder for search functionality. Triggers search query.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.filter": {
    text: "Filter",
    context: "Button to open filter options or apply filters to a list/table.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.export": {
    text: "Export",
    context: "Button to export data to file (CSV, PDF, etc.).",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.import": {
    text: "Import",
    context: "Button to import data from external file.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.refresh": {
    text: "Refresh",
    context: "Button to reload current data from server.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.upload": {
    text: "Upload",
    context: "Button to upload files (documents, images, etc.).",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.download": {
    text: "Download",
    context: "Button to download a file to user's device.",
    category: "actions",
    maxLength: 12,
    needsTranslation: true,
  },
  "action.copy": {
    text: "Copy",
    context: "Button to copy content to clipboard.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.share": {
    text: "Share",
    context: "Button to share item with other users or generate share link.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.view": {
    text: "View",
    context: "Button to view details of an item in read-only mode.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.expand": {
    text: "Expand",
    context: "Button to expand a collapsed section or show more content.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.collapse": {
    text: "Collapse",
    context: "Button to collapse an expanded section or hide content.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.select_all": {
    text: "Select all",
    context: "Checkbox/button to select all items in a list.",
    category: "actions",
    maxLength: 15,
    needsTranslation: true,
  },
  "action.clear": {
    text: "Clear",
    context: "Button to clear input field or reset filters.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.retry": {
    text: "Retry",
    context: "Button to retry a failed operation.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "action.send": {
    text: "Send",
    context: "Button to send a message or submit communication.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // AUTHENTICATION
  // ============================================================================
  "auth.login": {
    text: "Log in",
    context:
      "Primary button on login page. Submits credentials for authentication.",
    category: "auth",
    maxLength: 15,
    needsTranslation: true,
  },
  "auth.logout": {
    text: "Log out",
    context: "Action to end session and return to login page.",
    category: "auth",
    maxLength: 15,
    needsTranslation: true,
  },
  "auth.register": {
    text: "Register",
    context: "Link/button to create new account.",
    category: "auth",
    maxLength: 15,
    needsTranslation: true,
  },
  "auth.forgot_password": {
    text: "Forgot password?",
    context: "Link on login page to initiate password reset flow.",
    category: "auth",
    maxLength: 20,
    needsTranslation: true,
  },
  "auth.reset_password": {
    text: "Reset password",
    context: "Button to submit password reset request.",
    category: "auth",
    maxLength: 20,
    needsTranslation: true,
  },
  "auth.email": {
    text: "Email",
    context: "Label for email input field on login/register forms.",
    category: "auth",
    maxLength: 15,
    needsTranslation: true,
  },
  "auth.password": {
    text: "Password",
    context: "Label for password input field.",
    category: "auth",
    maxLength: 15,
    needsTranslation: true,
  },
  "auth.remember_me": {
    text: "Remember me",
    context: "Checkbox label to keep user logged in across sessions.",
    category: "auth",
    maxLength: 15,
    needsTranslation: true,
  },
  "auth.welcome_back": {
    text: "Welcome back",
    context: "Greeting text on login page header.",
    category: "auth",
    maxLength: 20,
    needsTranslation: true,
  },
  "auth.sign_in_to_continue": {
    text: "Sign in to continue",
    context: "Subtitle on login page below the welcome message.",
    category: "auth",
    maxLength: 25,
    needsTranslation: true,
  },

  // ============================================================================
  // PATIENTS
  // ============================================================================
  "patients.title": {
    text: "Patients",
    context: "Page title for patient list/management page.",
    category: "patients",
    maxLength: 15,
    needsTranslation: true,
  },
  "patients.add_patient": {
    text: "Add Patient",
    context: "Button to open form for creating a new patient record.",
    category: "patients",
    maxLength: 15,
    needsTranslation: true,
  },
  "patients.patient_name": {
    text: "Patient Name",
    context: "Column header in patient list table. Shows patient's full name.",
    category: "patients",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients.patient_id": {
    text: "Patient ID",
    context: "Column header for unique patient identifier.",
    category: "patients",
    maxLength: 15,
    needsTranslation: true,
  },
  "patients.date_of_birth": {
    text: "Date of Birth",
    context: "Field label for patient's birth date.",
    category: "patients",
    maxLength: 15,
    needsTranslation: true,
  },
  "patients.no_patients": {
    text: "No patients found",
    context: "Empty state message when patient list has no results.",
    category: "patients",
    maxLength: 25,
    needsTranslation: true,
  },
  "patients.search_label": {
    text: "Search Patients",
    context: "Label for patient search input field.",
    category: "patients",
    maxLength: 25,
    needsTranslation: true,
  },
  "patients.search_placeholder": {
    text: "Search patients...",
    context: "Placeholder text in patient search input field.",
    category: "patients",
    maxLength: 25,
    needsTranslation: true,
  },
  "patients.details": {
    text: "Patient Details",
    context: "Page title when viewing individual patient information.",
    category: "patients",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients.documents": {
    text: "Documents",
    context: "Section header for patient's uploaded documents.",
    category: "patients",
    maxLength: 15,
    needsTranslation: true,
  },
  "patients.drop_files": {
    text: "Drop files here, or click to browse",
    context:
      "Prompt text in the drag-and-drop upload zone for single patient mode. Shown on non-touch devices. Files only, no folders.",
    category: "patients",
    maxLength: 50,
    needsTranslation: true,
  },
  "patients.tap_to_upload": {
    text: "Tap to upload files",
    context:
      "Touch-device variant of patients.drop_files. Shown when 'ontouchstart' is detected on the window, since drag-and-drop on mobile is unreliable.",
    category: "patients",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients.file_types_short": {
    text: "PDF · Word · Excel · Images · WSI",
    context:
      "Compact one-line list of accepted file formats under the single-patient dropzone.",
    category: "patients",
    maxLength: 60,
    needsTranslation: true,
  },
  "patients.folder_hint_inline": {
    text: "folder? toggle Bulk above",
    context:
      "Inline micro-hint appended after the file-types list pointing folder uploaders to the Bulk toggle. Lowercase by design — sits inline.",
    category: "patients",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients.folder_hint": {
    text: "To upload a folder, switch to Bulk Upload mode",
    context:
      "Hint shown in single patient drop zone explaining that folder upload requires bulk mode.",
    category: "patients",
    maxLength: 55,
    needsTranslation: true,
  },
  "patients.intent_dialog_title": {
    text: "You added {count} files",
    context:
      "Title of the dialog that opens after dropping or picking 2+ files in single patient mode. Asks whether they all belong to one patient or are separate patients. {count} is substituted at runtime.",
    category: "patients",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients.intent_dialog_question": {
    text: "Are they all for the same patient, or one patient per file?",
    context:
      "Body text of the intent dialog. Plain language to disambiguate the user's intent.",
    category: "patients",
    maxLength: 90,
    needsTranslation: true,
  },
  "patients.intent_one_patient": {
    text: "Combine into ONE patient",
    context:
      "Primary action button in the intent dialog. All dropped files become attachments on a single patient record.",
    category: "patients",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients.intent_separate_patients": {
    text: "Separate patients (switch to Bulk)",
    context:
      "Secondary action button in the intent dialog. Switches the form to Bulk Upload mode so the user can drop folders (one per patient).",
    category: "patients",
    maxLength: 50,
    needsTranslation: true,
  },
  "patients.intent_bulk_hint": {
    text: "Put each patient's files inside their own folder, then drop the folders here.",
    context:
      "Snackbar tip shown after the user chooses 'Separate patients' in the intent dialog and gets switched to bulk mode.",
    category: "patients",
    maxLength: 120,
    needsTranslation: true,
  },
  "patients.intent_dialog_tip": {
    text: "Tip: to upload several patients at once, organize each into its own folder and drop the folders in Bulk Upload mode.",
    context:
      "Footnote inside the intent dialog teaching the bulk-by-folder convention. Caption style.",
    category: "patients",
    maxLength: 160,
    needsTranslation: true,
  },
  // ── V2 cards-UI preview (cursor_logs/030 § 2026-05-22) ──────────────
  "patients.form_version_v1": {
    text: "V1 (Stable)",
    context:
      "Toggle label shown when the stable AddPatientForm is mounted. Dev toggle visible only when the Add Patient panel is expanded.",
    category: "patients",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients.form_version_v2": {
    text: "V2 (Preview)",
    context:
      "Toggle label shown when the V2 cards-UI AddPatientFormV2 is mounted. Marks the form as a preview surface under construction.",
    category: "patients",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients.form_version_tooltip": {
    text: "Switch between the stable form (V1) and the new cards-UI preview (V2).",
    context:
      "Tooltip text on the V1/V2 toggle in the Add Patient panel. Explains the consequence of flipping the switch.",
    category: "patients",
    maxLength: 120,
    needsTranslation: true,
  },
  "patients.v2_preview_title": {
    text: "Patient creation — V2 preview",
    context:
      "Heading inside the V2 placeholder panel during Phase 2 of the cards-UI build. Reassures the user that this is intentional.",
    category: "patients",
    maxLength: 60,
    needsTranslation: true,
  },
  "patients.v2_preview_body": {
    text: "The new cards-based creation flow is being built. Flip the toggle above back to V1 to use the stable form.",
    context:
      "Body text inside the V2 placeholder panel during Phase 2. Tells the user how to return to V1 if needed.",
    category: "patients",
    maxLength: 160,
    needsTranslation: true,
  },
  "patients.folder_not_supported_single": {
    text: "Folders are supported in Bulk Upload mode. Toggle the switch above to upload a folder.",
    context:
      "Snackbar message shown when user drops a folder in single patient mode.",
    category: "patients",
    needsTranslation: true,
  },
  // ── patients_v2.* — V2 cards-UI namespace (cursor_logs/030 § 2026-05-22) ──
  "patients_v2.card_index": {
    text: "Patient {n}",
    context:
      "Header label on each patient card in the V2 cards UI. {n} is the 1-based card index. Always shown.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.card_placeholder_label": {
    text: "Patient {n}",
    context:
      "Fallback name shown in a collapsed card's summary when the user hasn't typed a name yet. {n} is the 1-based card index.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.card_file_summary": {
    text: "{count} file(s)",
    context:
      "Compact file-count summary in a collapsed card header. {count} is the number of files attached to the card.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.card_no_files": {
    text: "no files yet",
    context:
      "Shown in a collapsed card header when the user has added the card but not yet attached any files.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.per_card_dropzone_caption": {
    text: "Drop files for this patient, or click to browse",
    context:
      "Caption inside the per-card dropzone in the V2 cards UI. Files dropped here are attached to THIS patient only.",
    category: "patients_v2",
    maxLength: 80,
    needsTranslation: true,
  },
  "patients_v2.per_card_excel_warning": {
    text: "You added a spreadsheet for this patient only. It will be treated as a single document. To create multiple patients from a spreadsheet, drop it in the 'Add patients' area below.",
    context:
      "Snackbar warning when a CSV/Excel is dropped on a single patient's dropzone. Joan, 2026-05-22: accept the drop, warn the user, do not block.",
    category: "patients_v2",
    maxLength: 240,
    needsTranslation: true,
  },
  "patients_v2.show_more_details": {
    text: "Add more details",
    context:
      "Toggle text to expand the optional fields panel on a patient card. When collapsed (default state).",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.hide_more_details": {
    text: "Hide more details",
    context:
      "Toggle text to collapse the optional fields panel on a patient card. When expanded.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.external_id_label": {
    text: "External ID (NHC / MRN)",
    context:
      "Label of the optional external_id field inside the 'Add more details' panel. NHC = Numero de Historia Clinica (Spain); MRN = Medical Record Number (international).",
    category: "patients_v2",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients_v2.external_id_help": {
    text: "Hospital identifier. Add several separated by a space or comma.",
    context:
      "Helper text under the external_id field. Tells the user it can hold multiple MRNs, separated by a space or comma.",
    category: "patients_v2",
    maxLength: 80,
    needsTranslation: true,
  },
  "patients_v2.external_id_chip_placeholder": {
    text: "External ID",
    context:
      "Placeholder inside the V2 patient-create External ID field when it has no chips yet. Kept short — the dropdown of existing IDs makes the multi-value behaviour self-evident.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.external_id_duplicate_card": {
    text: "External ID {id} is also on another patient in this form. Remove the duplicate.",
    context:
      "Error banner in the V2 patient-create card when the SAME external_id is entered on two different cards in the same form — submitting both would create/attach two patients to one id. {id} is the duplicated identifier(s). Submit is blocked until one is removed.",
    category: "patients_v2",
    maxLength: 90,
    needsTranslation: true,
  },
  "patients_v2.external_id_conflict": {
    text: "ID {id} already belongs to {name} (#{pid}). Remove it to attach.",
    context:
      "Error banner in the V2 patient-create card when one of the typed external_id chips already belongs to a DIFFERENT patient than the one being attached to. {id} is the conflicting identifier, {name} the anonymised owner, {pid} that patient's id. Submit is blocked until the chip is removed.",
    category: "patients_v2",
    maxLength: 90,
    needsTranslation: true,
  },
  "patients_v2.external_id_duplicate_chip": {
    text: "{token} already used by {name}",
    context:
      "Warning chip shown under the external_id field when the typed identifier already exists on another patient in the selected environment. Non-blocking — clicking the chip opens that patient's detail page in a new tab.",
    category: "patients_v2",
    maxLength: 60,
    needsTranslation: true,
  },
  "patients_v2.external_id_duplicate_more": {
    text: "+{count} more",
    context:
      "Caption shown next to the duplicate-id warning chips when more than 4 matches exist. {count} is the remainder beyond the first 4 rendered as chips.",
    category: "patients_v2",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients_v2.description_label": {
    text: "Description",
    context:
      "Label of the optional description textarea inside the 'Add more details' panel. Acts as a free-text note that auto-sends as the patient's first chat message.",
    category: "patients_v2",
    maxLength: 25,
    needsTranslation: true,
  },
  "patients_v2.description_autosend_hint": {
    text: "This will be the patient's first chat message and is processed automatically.",
    context:
      "Information caption under the description textarea when CREATING a new patient. Tells the user that the description will trigger LLM extraction as soon as the patient is created.",
    category: "patients_v2",
    maxLength: 130,
    needsTranslation: true,
  },
  "patients_v2.description_autosend_hint_existing": {
    text: "This will be added as a new chat message to {name} and processed automatically.",
    context:
      "Information caption under the description textarea when the card is UPDATING an existing patient (matched via external_id). {name} is the anonymised matched patient name. Tells the user the text becomes a new chat message on that patient and is extracted automatically.",
    category: "patients_v2",
    maxLength: 140,
    needsTranslation: true,
  },
  "patients_v2.mode_updating": {
    text: "Updating {name}",
    context:
      "Small uppercase caption in the patient-card header (next to the #N counter) shown when the typed external_id matched an existing patient — the card will UPDATE that patient rather than create a new one. {name} is the anonymised matched patient name.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.mode_new_patient": {
    text: "New patient",
    context:
      "Small uppercase caption in the patient-card header (next to the #N counter) shown when the card will CREATE a brand-new patient (no external_id match). Neutral/info tone.",
    category: "patients_v2",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients_v2.bulk_checking_ids": {
    text: "Checking external IDs…",
    context:
      "Inline hint next to the bulk 'Create N patients' button while one or more cards are still probing their external_id for collisions. Tells the user it is a brief wait, not an error.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.checking_external_id": {
    text: "Checking external ID…",
    context:
      "Tooltip on the single-card Create button while its external_id is being checked for a collision (the button shows a spinner and is disabled until the check resolves).",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.global_dropzone_title": {
    text: "Add more patients",
    context:
      "Title of the global 'Add patients' drop area at the bottom of the V2 cards stack. Always visible.",
    category: "patients_v2",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients_v2.global_dropzone_hint": {
    text: "Drop folders, ZIPs, multiple PDFs, or a CSV/Excel here.",
    context:
      "One-line hint under the global drop area explaining what kinds of drops trigger multi-patient creation.",
    category: "patients_v2",
    maxLength: 80,
    needsTranslation: true,
  },
  "patients_v2.global_dropzone_browse_files": {
    text: "Browse files",
    context:
      "Button label that opens the OS file picker (multiple PDFs/spreadsheets) for the global drop area.",
    category: "patients_v2",
    maxLength: 25,
    needsTranslation: true,
  },
  "patients_v2.global_dropzone_browse_folder": {
    text: "Browse folder",
    context:
      "Button label that opens the OS folder picker (one patient per top-level folder) for the global drop area.",
    category: "patients_v2",
    maxLength: 25,
    needsTranslation: true,
  },
  "patients_v2.add_another_manual": {
    text: "Add another patient",
    context:
      "Text-link button that appends an empty patient card to the form without requiring any drop.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.folders_appended": {
    text: "{count} patient cards added from folders.",
    context:
      "Snackbar confirmation after a multi-folder drop creates {count} cards. Auto-dismisses.",
    category: "patients_v2",
    maxLength: 60,
    needsTranslation: true,
  },
  "patients_v2.zip_added_as_card": {
    text: "ZIP added as a single patient card. Hand-edit if you intended one patient per folder inside.",
    context:
      "Info snackbar shown when a ZIP is dropped in the global zone. Phase-3 scaffold accepts ZIP as a single card; deeper unpack-then-one-card-per-folder is deferred.",
    category: "patients_v2",
    maxLength: 140,
    needsTranslation: true,
  },
  "patients_v2.tabular_wizard_pending": {
    text: "Spreadsheet import opens a wizard (under construction). Use V1 → Bulk → CSV/Excel for now.",
    context:
      "Info snackbar shown when a CSV/Excel is dropped in the global zone during Phase 3/4. Phase 5 wires the actual TabularWizard modal.",
    category: "patients_v2",
    maxLength: 140,
    needsTranslation: true,
  },
  "patients_v2.tabular_modal_title": {
    text: "Import patients from spreadsheet",
    context:
      "Title of the modal that wraps the TabularWizard component in the V2 cards UI. Opened when a CSV/Excel file is dropped on the global 'Add patients' zone.",
    category: "patients_v2",
    maxLength: 50,
    needsTranslation: true,
  },
  "patients_v2.tabular_complete": {
    text: "Spreadsheet import finished. Patients are being processed.",
    context:
      "Success snackbar shown when the TabularWizard's Celery job completes inside the V2 modal. Triggers onBulkComplete on the parent dashboard.",
    category: "patients_v2",
    maxLength: 100,
    needsTranslation: true,
  },
  // ── V2 per-card lifecycle + editable wizard cards (cursor_logs/030 § 2026-05-22) ──
  "patients_v2.from_spreadsheet": {
    text: "from spreadsheet",
    context:
      "Small chip in a card header marking a card that came from the wizard's 'Review & edit in cards' flow. Distinguishes tabular-origin cards from manual ones.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.review_cards_appended": {
    text: "{count} patients ready for review. Edit any card, then click Create.",
    context:
      "Snackbar shown after the wizard's 'Review & edit in cards' materializes N cards in the V2 form.",
    category: "patients_v2",
    maxLength: 120,
    needsTranslation: true,
  },
  "patients_v2.card_status_creating": {
    text: "Creating {name}…",
    context:
      "Loader text inside a patient card while it's mid-flight. {name} is the typed patient name; falls back to 'Patient N' if blank.",
    category: "patients_v2",
    maxLength: 60,
    needsTranslation: true,
  },
  "patients_v2.card_status_created": {
    text: "Created — ",
    context:
      "Prefix for the success Alert in a succeeded card; followed by a clickable patient-name link. Keep the trailing em dash + space.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.card_status_created_no_link": {
    text: "Patient created.",
    context:
      "Fallback for the succeeded card's Alert when the backend didn't return a created_patient_id. Rare; defensive copy.",
    category: "patients_v2",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients_v2.card_status_locked": {
    text: "Locked",
    context:
      "Label of the green Lock chip in the navy header of a succeeded patient card. Re-uses the trust-hierarchy 'locked' vocabulary from PatientDetails — tells the user the row is now read-only.",
    category: "patients_v2",
    maxLength: 14,
    needsTranslation: true,
  },
  "patients_v2.card_status_failed": {
    text: "Creation failed. Edit fields and retry.",
    context:
      "Fallback message inside the failed-card Alert when the backend didn't return a specific error string.",
    category: "patients_v2",
    maxLength: 60,
    needsTranslation: true,
  },
  "patients_v2.card_create_one": {
    text: "Create patient",
    context:
      "Per-card 'Create patient' button at the bottom-right of an idle card body. Fires the single-card streaming submit.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.card_retry": {
    text: "Retry",
    context:
      "Per-card retry button shown when a card is in `failed` status. Re-fires the streaming submit with the user's current edits.",
    category: "patients_v2",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients_v2.succeeded_card_drop_blocked": {
    text: "This patient was already created. To add more documents, open the patient's detail page from the link in this card.",
    context:
      "Snackbar when the user tries to drop a file on a card already in `succeeded` status.",
    category: "patients_v2",
    maxLength: 180,
    needsTranslation: true,
  },
  "patients_v2.midflight_card_drop_blocked": {
    text: "This patient is still being created. Once it finishes, add more documents from its detail page.",
    context:
      "Snackbar when files are dropped on a card mid-creation (submitting/polling). Distinct from succeeded_card_drop_blocked: the patient is NOT created yet, so it must NOT say 'already created'.",
    category: "patients_v2",
    maxLength: 160,
    needsTranslation: false,
  },
  "patients_v2.create_needs_name_or_id": {
    text: "Enter a patient name or an external ID to create the patient.",
    context:
      "Hover tooltip on the disabled per-card Create button when neither a name nor an external ID has been entered (the backend needs at least one of the two).",
    category: "patients_v2",
    maxLength: 100,
    needsTranslation: false,
  },
  "patients_v2.drop_no_files": {
    text: "That drag didn't carry a file. On Linux, dragging from the Files manager sometimes drops only a text path. Use the Upload button to pick the files instead.",
    context:
      "Snackbar shown when a drop lands with no file payload (the Wayland/XWayland Nautilus drag bug). Guides the user to the upload button.",
    category: "patients_v2",
    maxLength: 200,
    needsTranslation: true,
  },
  "patients_v2.submit_no_remaining": {
    text: "No patients to create",
    context:
      "Global batch-submit button label when every card is mid-flight, succeeded, or failed (none in `idle`).",
    category: "patients_v2",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients_v2.submit_one_remaining": {
    text: "Create 1 patient",
    context:
      "Global batch-submit button label when exactly one card is in `idle` state.",
    category: "patients_v2",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients_v2.submit_many_remaining": {
    text: "Create {count} patients",
    context:
      "Global batch-submit button label when 2+ cards are in `idle` state. {count} is the count of idle cards. Skips succeeded/failed/mid-flight cards.",
    category: "patients_v2",
    maxLength: 40,
    needsTranslation: true,
  },
  // ── V2-Rev2 compact redesign (cursor_logs/030 § 2026-05-23) ───────────
  "patients_v2.env_chip_none": {
    text: "Select environments",
    context:
      "Label of the environment multi-select chip when no environment is selected. Tap opens the multi-checkbox popover.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.env_chip_count": {
    text: "{count} environments",
    context:
      "Label of the environment chip when 4+ environments are selected. {count} is the selection count. For 1 env we show the name; for 2-3 we show a comma-truncated list; for 4+ we show this count.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.env_chip_aria": {
    text: "Select environments",
    context:
      "Accessibility label (aria-label) for the environment chip + popover title on mobile.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.env_search_placeholder": {
    text: "Search environments",
    context:
      "Placeholder of the search field at the top of the environment multi-select popover. Filters the list by name.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.env_search_no_results": {
    text: "No environments match.",
    context:
      "Shown inside the environment popover when the search query matches no environment names.",
    category: "patients_v2",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients_v2.add_patients_drop_hint": {
    text: "or drop folders / ZIP / CSV here for multiple",
    context:
      "Caption inside the single-row 'Add patients' affordance at the bottom of the cards list. Sits next to the '+ Add patient' button. The whole row also accepts drops.",
    category: "patients_v2",
    maxLength: 90,
    needsTranslation: true,
  },
  "patients_v2.card_file_summary_collapsed": {
    text: "{count} files — show all",
    context:
      "Collapsed-state chip shown when a card has 5+ files attached. Click expands the chip back into individual file chips. {count} is the file count.",
    category: "patients_v2",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients_v2.card_file_summary_collapse": {
    text: "collapse",
    context:
      "Small button shown next to expanded file chips (5+ files) to collapse them back to the summary chip.",
    category: "patients_v2",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients_v2.card_no_name": {
    text: "(no name)",
    context:
      "Fallback shown when a succeeded card's name is somehow empty. Defensive copy; should never happen if name validation worked.",
    category: "patients_v2",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients_v2.upload_affordance_desktop": {
    text: "Upload files or drop anywhere",
    context:
      "Subtle inline link in the per-card affordance row. Clicking opens the file picker; the whole card is also a drop target. Shown on non-touch devices.",
    category: "patients_v2",
    maxLength: 50,
    needsTranslation: true,
  },
  "patients_v2.upload_affordance_touch": {
    text: "Tap to upload files",
    context:
      "Touch-device variant of upload_affordance_desktop. Drag-and-drop is unreliable on mobile, so the affordance is tap-only.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.upload_affordance_tooltip": {
    text: "Click here to browse, or drop files anywhere on this card.",
    context:
      "Tooltip shown on hover/long-press of the upload affordance link. Reinforces that the whole card is a drop target.",
    category: "patients_v2",
    maxLength: 100,
    needsTranslation: true,
  },
  // Short variant of the patient-name field label, used inline as a
  // placeholder inside the card's editable header (no floating label).
  "patients.name_label_short": {
    text: "Patient name *",
    context:
      "Placeholder text inside the editable-header name field of a V2 patient card. The asterisk indicates required. Falls back to the regular floating label form in V1.",
    category: "patients",
    maxLength: 25,
    needsTranslation: true,
  },
  "patients_v2.mixed_drop_blocked": {
    text: "Drop one type at a time: either PDFs, or folders, or a spreadsheet — not a mix.",
    context:
      "Warning snackbar when the user drops a heterogeneous batch (e.g. PDF + folder, or CSV + PDF) in the global zone.",
    category: "patients_v2",
    maxLength: 140,
    needsTranslation: true,
  },
  "patients_v2.pdf_intent_title": {
    text: "You added {count} PDFs",
    context:
      "Title of the PDF-intent dialog when 2+ PDFs are dropped in the global zone. {count} is the number of dropped PDFs.",
    category: "patients_v2",
    maxLength: 60,
    needsTranslation: true,
  },
  "patients_v2.pdf_intent_question": {
    text: "Create one patient per PDF, or combine them into a single patient?",
    context:
      "Question text in the PDF-intent dialog (V2 cards UI). The user picks one of the two large buttons below.",
    category: "patients_v2",
    maxLength: 120,
    needsTranslation: true,
  },
  "patients_v2.pdf_intent_split": {
    text: "{count} patients (one per PDF)",
    context:
      "Primary button in the PDF-intent dialog. Default action: create one card per PDF.",
    category: "patients_v2",
    maxLength: 50,
    needsTranslation: true,
  },
  "patients_v2.pdf_intent_combine": {
    text: "1 patient (combine all PDFs)",
    context:
      "Secondary button in the PDF-intent dialog. Combine all dropped PDFs onto a single new card.",
    category: "patients_v2",
    maxLength: 50,
    needsTranslation: true,
  },
  "patients_v2.submit_one": {
    text: "Create patient",
    context:
      "Submit button text when the V2 form has exactly one card. Single-patient case.",
    category: "patients_v2",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients_v2.submit_many": {
    text: "Create {count} patients",
    context:
      "Submit button text when the V2 form has multiple cards. {count} is the number of cards being submitted.",
    category: "patients_v2",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients_v2.summary_chip": {
    text: "{patients} patient(s) · {files} file(s)",
    context:
      "Caption shown next to the submit button summarizing total cards + total files about to be submitted.",
    category: "patients_v2",
    maxLength: 50,
    needsTranslation: true,
  },
  "patients_v2.submit_success": {
    text: "{count} patient(s) submitted for processing.",
    context:
      "Snackbar shown after a successful POST /api/patients/bulk-upload/start/ from the V2 form. {count} = total_patients returned by the backend.",
    category: "patients_v2",
    maxLength: 80,
    needsTranslation: true,
  },
  "patients_v2.submit_failed": {
    text: "Submit failed. Please try again.",
    context:
      "Snackbar + inline error shown when the bulk Create-all POST fails to start a job (network or backend error).",
    category: "patients_v2",
    maxLength: 80,
    needsTranslation: true,
  },
  "patients_v2.cross_env_locked": {
    text: "This patient is in {env}. Activate it to update this patient. All patients created in this session will then be added to {env}.",
    context:
      "Warning banner on a patient card when the picked Patient-Name suggestion lives in an environment that is not the form's current creation environment. {env} = environment name (appears twice).",
    category: "patients_v2",
    maxLength: 200,
    needsTranslation: true,
  },
  "patients_v2.cross_env_activate": {
    text: "Activate {env}",
    context:
      "Action button inside the cross-environment warning banner. Clicking it makes that environment the creation environment. {env} = environment name.",
    category: "patients_v2",
    maxLength: 40,
    needsTranslation: true,
  },
  "patients_v2.cross_env_activated": {
    text: "Now creating in {env}. Every patient created in this session will be added to it.",
    context:
      "Snackbar shown after the user activates a cross-environment patient's environment. {env} = environment name.",
    category: "patients_v2",
    maxLength: 120,
    needsTranslation: true,
  },
  "patients_v2.no_environment_error": {
    text: "Select an environment before creating patients.",
    context:
      "Inline error shown above the cards stack if the user clicks submit with no environment selected.",
    category: "patients_v2",
    maxLength: 80,
    needsTranslation: true,
  },
  "patients_v2.name_missing_error": {
    text: "Every patient needs a name. Fill the highlighted cards.",
    context:
      "Inline error shown above the cards stack if the user clicks submit with one or more cards missing their name field.",
    category: "patients_v2",
    maxLength: 80,
    needsTranslation: true,
  },
  "patients.single_detection_files": {
    text: "1 patient · {{count}} document(s)",
    context:
      "Summary shown in single patient mode for added files. {{count}} is the number of files.",
    category: "patients",
    needsTranslation: true,
  },
  "patients.mode_single": {
    text: "Single Patient",
    context:
      "Label for the active mode when single patient upload is selected.",
    category: "patients",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients.mode_bulk": {
    text: "Bulk Upload",
    context: "Label for the active mode when bulk upload is selected.",
    category: "patients",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients.mode_single_hint": {
    text: "Upload individual files for one patient",
    context: "Subtitle description shown below the Single Patient mode label.",
    category: "patients",
    maxLength: 50,
    needsTranslation: true,
  },
  "patients.mode_bulk_hint": {
    text: "Upload folders or archives with multiple patients",
    context: "Subtitle description shown below the Bulk Upload mode label.",
    category: "patients",
    maxLength: 55,
    needsTranslation: true,
  },
  "patients.upload_document": {
    text: "Upload Document",
    context: "Button to upload clinical documents for a patient.",
    category: "patients",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients.record_voice": {
    text: "Record Voice Note",
    context: "Button to start voice recording for clinical notes.",
    category: "patients",
    maxLength: 20,
    needsTranslation: true,
  },
  "patients.take_photo": {
    text: "Take Photo",
    context: "Button to capture photo using device camera.",
    category: "patients",
    maxLength: 15,
    needsTranslation: true,
  },
  "patients.no_results": {
    text: "No patients found",
    context: "Message shown in the patient search panel when the search query returns zero results.",
    category: "patients",
    maxLength: 30,
    needsTranslation: true,
  },
  "patients.no_results_hint": {
    text: "Try a different name or description",
    context: "Secondary hint shown below 'No patients found' to guide the user to refine their search.",
    category: "patients",
    maxLength: 50,
    needsTranslation: true,
  },

  // ============================================================================
  // CLINICAL / MEDICAL
  // ============================================================================
  "clinical.event_table": {
    text: "Event Table",
    context:
      "Header for clinical events table showing patient timeline (diagnoses, treatments, etc.).",
    category: "clinical",
    maxLength: 15,
    translatorNotes: "Medical terminology - refers to clinical event tracking.",
    needsTranslation: true,
  },
  "clinical.static_table": {
    text: "Diagnosis Data",
    context:
      "Header for static/baseline patient data table (demographics, diagnosis info).",
    category: "clinical",
    maxLength: 20,
    needsTranslation: true,
  },
  "clinical.event_date": {
    text: "Event Date",
    context: "Column header for when a clinical event occurred.",
    category: "clinical",
    maxLength: 15,
    needsTranslation: true,
  },
  "clinical.event_type": {
    text: "Event Type",
    context: "Column header for type/category of clinical event.",
    category: "clinical",
    maxLength: 15,
    needsTranslation: true,
  },
  "clinical.treatment": {
    text: "Treatment",
    context: "Label for treatment-related information.",
    category: "clinical",
    maxLength: 15,
    translatorNotes: "Medical term for therapeutic intervention.",
    needsTranslation: true,
  },
  "clinical.diagnosis": {
    text: "Diagnosis",
    context: "Label for diagnosis information.",
    category: "clinical",
    maxLength: 15,
    translatorNotes: "Medical term for identified condition/disease.",
    needsTranslation: true,
  },
  "clinical.notes": {
    text: "Clinical Notes",
    context: "Section header for free-text clinical observations.",
    category: "clinical",
    maxLength: 20,
    needsTranslation: true,
  },
  "clinical.extracting": {
    text: "Extracting clinical data...",
    context: "Loading message shown while AI extracts data from documents.",
    category: "clinical",
    maxLength: 30,
    needsTranslation: true,
  },
  "clinical.domain_selector": {
    text: "Select Cancer Types",
    context: "Label for dropdown to choose which cancer domains to analyze.",
    category: "clinical",
    maxLength: 25,
    translatorNotes: "Cancer type selection for clinical data extraction.",
    needsTranslation: true,
  },
  "clinical.molecular_profile": {
    text: "Molecular Profile",
    context: "Section header for genetic/molecular test results.",
    category: "clinical",
    maxLength: 20,
    translatorNotes: "Medical term for genetic/biomarker analysis.",
    needsTranslation: true,
  },
  "clinical.cancer_profile": {
    text: "Cancer Profile",
    context:
      "Section header for the universal cancer-specific profile table (risk factors, phenotype and disease-specific attributes for this patient's cancer, e.g. Fitzpatrick phototype, nevi count and sun-exposure history for melanoma). Shown in patient details alongside the Patient and Molecular profiles.",
    category: "clinical",
    maxLength: 20,
    translatorNotes: "Medical term for the cancer-specific risk-factor / phenotype profile.",
    needsTranslation: true,
  },
  "clinical.cancer_profile_skin": {
    text: "Melanoma Profile & Risk Factors",
    context:
      "Per-cancer title for the Cancer Profile panel when the patient's cancer is melanoma (skin). Replaces the generic 'Cancer Profile' header and covers melanoma-specific risk factors and phenotype (Fitzpatrick phototype, nevi count, sun-exposure history).",
    category: "clinical",
    maxLength: 40,
    translatorNotes: "Melanoma-specific variant of the cancer risk-factor / phenotype profile title.",
    needsTranslation: true,
  },
  "clinical.cancer_profile_section": {
    text: "Risk Factors & Baseline",
    context:
      "Sub-heading for the FIRST section inside the merged Cancer Profile panel: the single-record baseline form (risk factors, phenotype and disease-specific baseline attributes for this patient's cancer). Sits above the 'Findings & Surveillance' section.",
    category: "clinical",
    maxLength: 30,
    translatorNotes: "Section label for the cancer baseline / risk-factor form.",
    needsTranslation: true,
  },
  "clinical.cancer_observation": {
    text: "Findings & Surveillance",
    context:
      "Sub-heading for the SECOND section inside the merged Cancer Profile panel: a dated series of clinical findings and surveillance observations for this patient's cancer (e.g. lesion measurements, follow-up examination findings). Sits below the 'Risk Factors & Baseline' section.",
    category: "clinical",
    maxLength: 30,
    translatorNotes: "Section label for the dated cancer findings / surveillance list.",
    needsTranslation: true,
  },
  "clinical.cancer_observation_skin": {
    text: "Melanoma Findings & Surveillance",
    context:
      "Per-cancer heading for the 'Findings & Surveillance' section when the patient's cancer is melanoma (skin). Replaces the generic 'Findings & Surveillance' label and covers dated melanoma findings and follow-up surveillance observations.",
    category: "clinical",
    maxLength: 40,
    translatorNotes: "Melanoma-specific variant of the findings / surveillance section label.",
    needsTranslation: true,
  },
  "clinical.patient_profile": {
    text: "Patient Profile",
    context: "Section header for basic patient demographics and info.",
    category: "clinical",
    maxLength: 20,
    needsTranslation: true,
  },
  "clinical.hematological_profile": {
    text: "Hematological Profile",
    context:
      "Section header for blood cancer-specific markers (BCR-ABL, FLT3, CD markers, MRD, etc). Shown in patient details for hematological malignancies.",
    category: "clinical",
    maxLength: 25,
    translatorNotes:
      "Medical term for blood/bone marrow cancer biomarkers. May need localization of 'hematological' vs 'haematological' in British English.",
    needsTranslation: true,
  },
  "clinical.universal_data": {
    text: "Universal Patient Data",
    context: "Section header for data common across all cancer types.",
    category: "clinical",
    maxLength: 25,
    needsTranslation: true,
  },

  // ============================================================================
  // ANALYTICS DASHBOARD
  // ============================================================================
  "dashboard.add_patient": {
    text: "Add patient",
    context:
      "Label on the dashboard's primary create action. In the proposal it is a labelled button rather than a bare + circle.",
    category: "dashboard",
    maxLength: 24,
    needsTranslation: true,
  },
  "dashboard.search_placeholder": {
    text: "Search patients to switch to list view...",
    context:
      "Search bar placeholder on the main dashboard. Typing triggers transition from analytics view to patient list.",
    category: "dashboard",
    maxLength: 50,
    needsTranslation: true,
  },
  "dashboard.tabs.cohort_overview": {
    text: "Cohort Overview",
    context:
      "Tab label for the first analytics tab showing demographics, staging, and completeness charts.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.tabs.molecular_treatment": {
    text: "Molecular & Treatment",
    context:
      "Tab label for the second analytics tab showing mutation prevalence, biomarkers, and treatment data.",
    category: "dashboard",
    maxLength: 25,
    needsTranslation: true,
  },
  "dashboard.analytics.total_patients": {
    text: "Total Patients",
    context:
      "KPI card label showing the total number of patients in the user's environments.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.analytics.added_this_month": {
    text: "Added This Month",
    context:
      "KPI label. Counts Patient.created_at, which is auto_now_add: the row INSERT time, not a clinical date. It measures records added to Athena (including bulk sync), NOT newly diagnosed or enrolled patients. Do not relabel it as \"New patients\".",
    category: "dashboard",
    maxLength: 24,
    needsTranslation: true,
  },
  "dashboard.analytics.added_this_month_sub": {
    text: "records added to Athena",
    context:
      "Subtitle under Added This Month. Replaced a \"% growth\" figure that read 100% for any bulk-synced tenant and implied clinical growth.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },
  "dashboard.analytics.new_this_month": {
    text: "New This Month",
    context:
      "KPI card label showing how many patients were created in the current month.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.analytics.total_documents": {
    text: "Documents",
    context: "KPI card label for total number of uploaded clinical documents.",
    category: "dashboard",
    maxLength: 15,
    needsTranslation: true,
  },
  "dashboard.analytics.top_domain": {
    text: "Top Domain",
    context:
      "KPI card label showing the most frequent cancer type or domain in the cohort.",
    category: "dashboard",
    maxLength: 15,
    needsTranslation: true,
  },
  "dashboard.analytics.cancer_distribution": {
    text: "Cancer Type Distribution",
    context:
      "Chart title for horizontal bar chart showing patient counts per cancer domain.",
    category: "dashboard",
    maxLength: 30,
    needsTranslation: true,
  },
  "dashboard.analytics.age_distribution": {
    text: "Age Distribution",
    context: "Chart title for the age histogram in the demographics panel.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.analytics.gender_split": {
    text: "Gender",
    context:
      "Chart title for the gender donut chart in the demographics panel.",
    category: "dashboard",
    maxLength: 10,
    needsTranslation: true,
  },
  "dashboard.analytics.staging_overview": {
    text: "TNM Staging",
    context:
      "Chart title for the sunburst chart showing T/N/M staging distribution.",
    category: "dashboard",
    maxLength: 15,
    translatorNotes:
      "TNM is a universal oncology classification system (Tumor/Nodes/Metastasis). Keep TNM as-is in most languages.",
    needsTranslation: true,
  },
  "dashboard.analytics.mutation_prevalence": {
    text: "Mutation Prevalence",
    context:
      "Chart title for grouped bar chart showing gene mutation positive/negative/unknown counts.",
    category: "dashboard",
    maxLength: 25,
    needsTranslation: true,
  },
  "dashboard.analytics.biomarker_radar": {
    text: "Biomarker Profile",
    context:
      "Chart title for the radar chart displaying average biomarker values (TMB, PD-L1, Ki-67, etc).",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.analytics.treatment_distribution": {
    text: "Treatment Distribution",
    context:
      "Chart title for treemap showing therapy type distribution across events.",
    category: "dashboard",
    maxLength: 25,
    needsTranslation: true,
  },
  "dashboard.analytics.event_timeline": {
    text: "Clinical Events Timeline",
    context:
      "Chart title for multi-line chart showing monthly event counts by type.",
    category: "dashboard",
    maxLength: 30,
    needsTranslation: true,
  },
  "dashboard.analytics.data_completeness": {
    text: "How Complete Is Your Data?",
    context:
      "Chart title for horizontal bar chart showing how much of each clinical table has been filled in. Green = >75%, yellow = 40-75%, red = <40%. Subtitle shows '% of fields filled per table'.",
    category: "dashboard",
    maxLength: 30,
    needsTranslation: true,
  },
  "dashboard.analytics.data_completeness_sub": {
    text: "% of fields filled per table",
    context:
      "Subtitle under the data completeness chart title. Explains the metric being measured.",
    category: "dashboard",
    maxLength: 35,
    needsTranslation: true,
  },
  "dashboard.analytics.no_staging_data": {
    text: "No staging data available",
    context:
      "Empty state message shown when there is no TNM staging data to display.",
    category: "dashboard",
    maxLength: 35,
    needsTranslation: true,
  },
  "dashboard.analytics.no_mutation_data": {
    text: "No mutation data available",
    context:
      "Empty state message shown when there is no molecular mutation data.",
    category: "dashboard",
    maxLength: 35,
    needsTranslation: true,
  },
  "dashboard.analytics.no_biomarker_data": {
    text: "No biomarker data available",
    context: "Empty state message shown when there is no biomarker data.",
    category: "dashboard",
    maxLength: 35,
    needsTranslation: true,
  },
  "dashboard.analytics.no_treatment_data": {
    text: "No treatment data available",
    context: "Empty state message shown when there is no treatment event data.",
    category: "dashboard",
    maxLength: 35,
    needsTranslation: true,
  },
  "dashboard.analytics.no_timeline_data": {
    text: "No event timeline data available",
    context:
      "Empty state message shown when there are no dated clinical events.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },
  "dashboard.analytics.no_completeness_data": {
    text: "No completeness data available",
    context:
      "Empty state message shown when there is no data to calculate completeness.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },

  // --- Export Dialog ---
  "dashboard.export.title": {
    text: "Export Patient Data",
    context:
      "Title of the export dialog and tooltip on the export/download button.",
    category: "dashboard",
    maxLength: 25,
    needsTranslation: true,
  },
  "dashboard.export.universal_profiles": {
    text: "Universal Profiles",
    context:
      "Section header in the export dialog for universal table types (Patient Profile, Molecular, Hematological).",
    category: "dashboard",
    maxLength: 25,
    needsTranslation: true,
  },
  "dashboard.export.cancer_domains": {
    text: "Cancer Domains",
    context:
      "Section header in the export dialog for cancer-specific data selection.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.export.include_events": {
    text: "Include Events",
    context:
      "Checkbox label in export dialog to include event (treatment/surgery) tables.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.export.events_info": {
    text: "Treatment events for each selected cancer domain will appear in their own Excel sheet, with one row per event.",
    context:
      "Info alert explaining that events are exported as separate sheets with one row per event.",
    category: "dashboard",
    maxLength: 120,
    needsTranslation: true,
  },
  "dashboard.export.multi_sheet_info": {
    text: "The Excel file will contain a separate sheet for each selected table (profiles, cancer summaries, events, etc.), linked by a patient ID column.",
    context:
      "Info alert explaining the multi-sheet Excel export structure.",
    category: "dashboard",
    maxLength: 160,
    needsTranslation: true,
  },
  "dashboard.export.format": {
    text: "Format",
    context: "Label for the CSV/JSON format selector in the export dialog.",
    category: "dashboard",
    maxLength: 10,
    needsTranslation: true,
  },
  "dashboard.export.matching_patients": {
    text: "Matching patients",
    context: "Label above the patient count preview in the export dialog.",
    category: "dashboard",
    maxLength: 25,
    needsTranslation: true,
  },
  "dashboard.export.download": {
    text: "Download",
    context: "Button label to trigger the cohort data download.",
    category: "dashboard",
    maxLength: 12,
    needsTranslation: true,
  },
  "dashboard.export.exporting": {
    text: "Exporting...",
    context: "Button label shown while the export is being processed.",
    category: "dashboard",
    maxLength: 15,
    needsTranslation: true,
  },

  // --- Environment Filter ---
  "dashboard.environment_filter.label": {
    text: "Environments",
    context: "Label before the environment filter chip row on the dashboard.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.environment_filter.add": {
    text: "Add",
    context:
      "Chip label to open dropdown of unselected environments in the filter.",
    category: "dashboard",
    maxLength: 10,
    needsTranslation: true,
  },
  "dashboard.environment_filter.select_env": {
    text: "SELECT ENVIRONMENT",
    context:
      "Header text in the environment selection dropdown/popover, styled like the language selector.",
    category: "dashboard",
    maxLength: 25,
    needsTranslation: true,
  },
  "dashboard.environment_filter.select_all": {
    text: "Select All",
    context:
      "Action in the environment dropdown that selects all environments at once.",
    category: "dashboard",
    maxLength: 15,
    needsTranslation: true,
  },
  "dashboard.environment_filter.deselect_all": {
    text: "Deselect All",
    context:
      "Action in the collapsed multi-select popover header that deselects all environments (reverts to selecting just the first).",
    category: "dashboard",
    maxLength: 15,
    needsTranslation: true,
  },
  "dashboard.environment_filter.all_envs": {
    text: "All environments",
    context:
      "Label on the collapsed summary chip when all environments are selected.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.environment_filter.n_selected": {
    text: "{{count}} environments selected",
    context:
      "Label on the collapsed summary chip showing how many environments are currently active. {{count}} is the number.",
    category: "dashboard",
    needsTranslation: true,
  },
  "dashboard.environment_filter.reset_to_all": {
    text: "Reset to all",
    context:
      "Tooltip on the delete icon of the collapsed summary chip when filtered.",
    category: "dashboard",
    maxLength: 15,
    needsTranslation: true,
  },
  "dashboard.environment_filter.search_placeholder": {
    text: "Search environments...",
    context:
      "Placeholder text in the searchable multi-select input for environment filtering.",
    category: "dashboard",
    maxLength: 30,
    needsTranslation: true,
  },
  "dashboard.environment_filter.count_footer": {
    text: "{{selected}} of {{total}} selected",
    context:
      "Footer text in the multi-select popover showing selection count. {{selected}} and {{total}} are numbers.",
    category: "dashboard",
    needsTranslation: true,
  },
  "dashboard.environment_filter.no_results": {
    text: "No environments found",
    context:
      "Shown in the environment multi-select dropdown when search yields no results.",
    category: "dashboard",
    maxLength: 25,
    needsTranslation: true,
  },
  "dashboard.export.environments": {
    text: "Environments",
    context:
      "Section header in the export dialog for environment scope selection.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },

  // --- NL Query Chat ---
  "dashboard.nl.placeholder": {
    text: "Ask a question about your patients...",
    context:
      "Placeholder text in the natural language query input on the dashboard.",
    category: "dashboard",
    maxLength: 50,
    needsTranslation: true,
  },
  "dashboard.nl.send": {
    text: "Send",
    context: "Button to submit a natural language query.",
    category: "dashboard",
    maxLength: 8,
    needsTranslation: true,
  },
  "dashboard.nl.thinking": {
    text: "Analyzing your question...",
    context:
      "Loading message shown while the LLM processes the natural language query.",
    category: "dashboard",
    maxLength: 35,
    needsTranslation: true,
  },
  "dashboard.nl.error": {
    text: "Could not process your question. Please try rephrasing.",
    context: "Error message when the NL query fails or is deemed unsafe.",
    category: "dashboard",
    maxLength: 60,
    needsTranslation: true,
  },
  "dashboard.nl.rate_limit": {
    text: "Daily query limit reached. Please try again tomorrow.",
    context: "Error shown when user exceeds their daily NL query allowance.",
    category: "dashboard",
    maxLength: 60,
    needsTranslation: true,
  },

  // ============================================================================
  // BILLING & SUBSCRIPTION
  // ============================================================================
  "billing.title": {
    text: "Billing & Usage",
    context: "Page title for billing management section.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.current_plan": {
    text: "Current Plan",
    context: "Section header showing user's active subscription tier.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.usage": {
    text: "Usage",
    context: "Section header for token/API usage statistics.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.upgrade": {
    text: "Upgrade",
    context: "Button to upgrade subscription plan.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.downgrade": {
    text: "Downgrade",
    context: "Button to switch to lower subscription tier.",
    category: "billing",
    maxLength: 12,
    needsTranslation: true,
  },
  "billing.invoices": {
    text: "Invoices",
    context: "Section header for billing history/invoices list.",
    category: "billing",
    maxLength: 12,
    needsTranslation: true,
  },
  "billing.payment_method": {
    text: "Payment Method",
    context: "Section header for credit card/payment info.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.tokens_used": {
    text: "Tokens Used",
    context: "Label showing AI token consumption.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.tokens_remaining": {
    text: "Tokens Remaining",
    context: "Label showing remaining AI token quota.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.monthly": {
    text: "Monthly",
    context: "Label for monthly billing cycle option.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.yearly": {
    text: "Yearly",
    context: "Label for yearly billing cycle option.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.free_tier": {
    text: "Free",
    context: "Name of free subscription tier.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.starter_tier": {
    text: "Professional",
    context: "Name of the manual entry paid subscription tier (internal DB key: starter).",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.professional_tier": {
    text: "AI Professional",
    context: "Name of the AI-enabled subscription tier (internal DB key: professional).",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.team_tier": {
    text: "Network",
    context: "Name of the multi-environment subscription tier (internal DB key: team).",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.enterprise_tier": {
    text: "Enterprise",
    context: "Name of highest subscription tier for organizations.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },

  // ── Beta User Management (Admin Pricing tab) ──────────────────────────
  "billing.beta_users.title": {
    text: "Beta Programme Users",
    context:
      "Header for the beta user management section in admin pricing tab.",
    category: "billing",
    maxLength: 30,
    needsTranslation: true,
  },
  "billing.beta_users.subtitle": {
    text: "Manage who has beta access. Beta users bypass tier restrictions and quotas.",
    context: "Subheader explaining the beta user management feature.",
    category: "billing",
    maxLength: 100,
    needsTranslation: true,
  },
  "billing.beta_users.count": {
    text: "{count} users",
    context:
      "Chip showing the total number of beta users. {count} is a number.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.beta_users.add_title": {
    text: "Add Beta User",
    context: "Label above the autocomplete for adding a new beta user.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.beta_users.search_hint": {
    text: "Type at least 3 characters to search...",
    context:
      "Placeholder hint in the beta user search autocomplete when no results.",
    category: "billing",
    maxLength: 50,
    needsTranslation: true,
  },
  "billing.beta_users.search_placeholder": {
    text: "Search by username or email...",
    context: "Placeholder text in the beta user search input field.",
    category: "billing",
    maxLength: 40,
    needsTranslation: true,
  },
  "billing.beta_users.empty": {
    text: "No beta users configured.",
    context: "Shown when there are no beta users in the system.",
    category: "billing",
    maxLength: 40,
    needsTranslation: true,
  },
  "billing.beta_users.from_settings_tooltip": {
    text: "This user is in the BETA_ALLOWED_EMAILS settings list and cannot be removed from the UI.",
    context:
      "Tooltip on the lock icon for beta users that come from the environment variable.",
    category: "billing",
    maxLength: 120,
    needsTranslation: true,
  },
  "billing.beta_users.remove_tooltip": {
    text: "Remove beta access",
    context: "Tooltip on the delete button to remove a user's beta access.",
    category: "billing",
    maxLength: 25,
    needsTranslation: true,
  },

  // ── Processing / Extraction banners ────────────────────────────────────
  "data_panel.processing_documents": {
    text: "Processing documents. Clinical data will appear automatically.",
    context:
      "Banner shown while documents are being extracted and no clinical tables exist yet.",
    category: "patients",
    maxLength: 80,
    needsTranslation: true,
  },

  // ============================================================================
  // FORMS & INPUTS
  // ============================================================================
  "form.required": {
    text: "Required",
    context: "Label indicating a form field must be filled.",
    category: "forms",
    maxLength: 10,
    needsTranslation: true,
  },
  "form.optional": {
    text: "Optional",
    context: "Label indicating a form field is not required.",
    category: "forms",
    maxLength: 10,
    needsTranslation: true,
  },
  "form.select_option": {
    text: "Select an option",
    context: "Default placeholder in dropdown/select inputs.",
    category: "forms",
    maxLength: 20,
    needsTranslation: true,
  },
  "form.no_results": {
    text: "No results found",
    context: "Message when autocomplete/search has no matches.",
    category: "forms",
    maxLength: 20,
    needsTranslation: true,
  },
  "form.loading": {
    text: "Loading...",
    context: "Text shown while data is being fetched.",
    category: "forms",
    maxLength: 15,
    needsTranslation: true,
  },
  "form.error_required": {
    text: "This field is required",
    context: "Validation error for empty required fields.",
    category: "forms",
    maxLength: 25,
    needsTranslation: true,
  },
  "form.error_invalid_email": {
    text: "Please enter a valid email",
    context: "Validation error for malformed email addresses.",
    category: "forms",
    maxLength: 30,
    needsTranslation: true,
  },
  "form.error_min_length": {
    text: "Must be at least {min} characters",
    context:
      "Validation error for too-short input. {min} is the minimum length.",
    category: "forms",
    maxLength: 35,
    placeholders: ["min"],
    needsTranslation: true,
  },
  "form.error_max_length": {
    text: "Must be no more than {max} characters",
    context:
      "Validation error for too-long input. {max} is the maximum length.",
    category: "forms",
    maxLength: 40,
    placeholders: ["max"],
    needsTranslation: true,
  },
  "form.characters_remaining": {
    text: "{count} characters remaining",
    context:
      "Helper text showing remaining character limit. {count} is the number.",
    category: "forms",
    maxLength: 30,
    placeholders: ["count"],
    needsTranslation: true,
  },

  // ============================================================================
  // TABLES & DATA DISPLAY
  // ============================================================================
  "table.no_data": {
    text: "No data available",
    context: "Empty state message for tables with no rows.",
    category: "tables",
    maxLength: 20,
    needsTranslation: true,
  },
  "table.rows_per_page": {
    text: "Rows per page",
    context: "Label for pagination control showing items per page.",
    category: "tables",
    maxLength: 15,
    needsTranslation: true,
  },
  "table.showing": {
    text: "Showing {start} to {end} of {total}",
    context:
      "Pagination info text. Variables show current range and total count.",
    category: "tables",
    maxLength: 35,
    placeholders: ["start", "end", "total"],
    needsTranslation: true,
  },
  "table.sort_ascending": {
    text: "Sort ascending",
    context: "Tooltip for column sort button (A-Z, 0-9).",
    category: "tables",
    maxLength: 15,
    needsTranslation: true,
  },
  "table.sort_descending": {
    text: "Sort descending",
    context: "Tooltip for column sort button (Z-A, 9-0).",
    category: "tables",
    maxLength: 15,
    needsTranslation: true,
  },
  "table.actions": {
    text: "Actions",
    context: "Column header for row action buttons (edit, delete, etc.).",
    category: "tables",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // MODALS & DIALOGS
  // ============================================================================
  "modal.confirm_delete": {
    text: "Confirm Delete",
    context: "Title for delete confirmation dialog.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },
  "modal.delete_message": {
    text: "Are you sure you want to delete this item? This action cannot be undone.",
    context: "Warning message in delete confirmation dialog.",
    category: "modals",
    maxLength: 80,
    translatorNotes: "Emphasize the irreversible nature of the action.",
    needsTranslation: true,
  },
  "modal.unsaved_changes": {
    text: "Unsaved Changes",
    context: "Title for dialog warning about unsaved work.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },
  "modal.unsaved_message": {
    text: "You have unsaved changes. Are you sure you want to leave?",
    context: "Warning message when navigating away from unsaved form.",
    category: "modals",
    maxLength: 60,
    needsTranslation: true,
  },
  "modal.success": {
    text: "Success",
    context: "Title for success confirmation dialog.",
    category: "modals",
    maxLength: 10,
    needsTranslation: true,
  },
  "modal.error": {
    text: "Error",
    context: "Title for error dialog.",
    category: "modals",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // NOTIFICATIONS & ALERTS
  // ============================================================================
  "notification.success": {
    text: "Success",
    context: "Toast notification title for successful operations.",
    category: "notifications",
    maxLength: 10,
    needsTranslation: true,
  },
  "notification.error": {
    text: "Error",
    context: "Toast notification title for failed operations.",
    category: "notifications",
    maxLength: 10,
    needsTranslation: true,
  },
  "notification.warning": {
    text: "Warning",
    context: "Toast notification title for warnings.",
    category: "notifications",
    maxLength: 10,
    needsTranslation: true,
  },
  "notification.info": {
    text: "Information",
    context: "Toast notification title for informational messages.",
    category: "notifications",
    maxLength: 15,
    needsTranslation: true,
  },
  "notification.saved": {
    text: "Changes saved successfully",
    context: "Success message after saving data.",
    category: "notifications",
    maxLength: 30,
    needsTranslation: true,
  },
  "notification.deleted": {
    text: "Item deleted successfully",
    context: "Success message after deleting an item.",
    category: "notifications",
    maxLength: 30,
    needsTranslation: true,
  },
  "notification.copied": {
    text: "Copied to clipboard",
    context: "Success message after copying to clipboard.",
    category: "notifications",
    maxLength: 20,
    needsTranslation: true,
  },

  // ============================================================================
  // ERRORS
  // ============================================================================
  "error.generic": {
    text: "Something went wrong. Please try again.",
    context: "Generic error message for unexpected failures.",
    category: "errors",
    maxLength: 50,
    needsTranslation: true,
  },
  "error.network": {
    text: "Network error. Please check your connection.",
    context: "Error message when network request fails.",
    category: "errors",
    maxLength: 50,
    needsTranslation: true,
  },
  "error.unauthorized": {
    text: "You are not authorized to perform this action.",
    context: "Error when user lacks permission.",
    category: "errors",
    maxLength: 50,
    needsTranslation: true,
  },
  "error.not_found": {
    text: "The requested resource was not found.",
    context: "Error for 404/missing resources.",
    category: "errors",
    maxLength: 50,
    needsTranslation: true,
  },
  "error.session_expired": {
    text: "Your session has expired. Please log in again.",
    context: "Error when authentication token expires.",
    category: "errors",
    maxLength: 50,
    needsTranslation: true,
  },
  "error.rate_limit": {
    text: "Too many requests. Please wait a moment.",
    context: "Error when user hits API rate limits.",
    category: "errors",
    maxLength: 50,
    needsTranslation: true,
  },
  "error.quota_exceeded": {
    text: "You have exceeded your usage quota.",
    context: "Error when subscription limits are reached.",
    category: "errors",
    maxLength: 50,
    needsTranslation: true,
  },

  // ============================================================================
  // DASHBOARD
  // ============================================================================
  "dashboard.welcome": {
    text: "Welcome, {name}",
    context: "Greeting on dashboard header. {name} is user's first name.",
    category: "dashboard",
    maxLength: 30,
    placeholders: ["name"],
    needsTranslation: true,
  },
  "dashboard.recent_patients": {
    text: "Recent Patients",
    context: "Section header for recently accessed patient list.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.quick_actions": {
    text: "Quick Actions",
    context: "Section header for shortcut buttons.",
    category: "dashboard",
    maxLength: 15,
    needsTranslation: true,
  },
  "dashboard.manage_patients": {
    text: "Manage Patients",
    context: "Expandable section header for patient management tools.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.activity": {
    text: "Recent Activity",
    context: "Section header for activity feed.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.no_activity": {
    text: "No recent activity",
    context: "Empty state when no activity to show.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },

  // ============================================================================
  // SETTINGS
  // ============================================================================
  "settings.title": {
    text: "Settings",
    context: "Page title for settings section.",
    category: "settings",
    maxLength: 15,
    needsTranslation: true,
  },
  "settings.profile": {
    text: "Profile",
    context: "Tab/section for user profile settings.",
    category: "settings",
    maxLength: 10,
    needsTranslation: true,
  },
  "settings.account": {
    text: "Account",
    context: "Tab/section for account settings.",
    category: "settings",
    maxLength: 10,
    needsTranslation: true,
  },
  "settings.notifications": {
    text: "Notifications",
    context: "Tab/section for notification preferences.",
    category: "settings",
    maxLength: 15,
    needsTranslation: true,
  },
  "settings.language": {
    text: "Language",
    context: "Label for language selector.",
    category: "settings",
    maxLength: 10,
    needsTranslation: true,
  },
  "settings.timezone": {
    text: "Timezone",
    context: "Label for timezone selector.",
    category: "settings",
    maxLength: 10,
    needsTranslation: true,
  },
  "settings.dark_mode": {
    text: "Dark Mode",
    context: "Toggle label for dark/light theme preference.",
    category: "settings",
    maxLength: 12,
    needsTranslation: true,
  },

  // ============================================================================
  // COMMON / MISC
  // ============================================================================
  "common.yes": {
    text: "Yes",
    context: "Affirmative response in dialogs.",
    category: "common",
    maxLength: 5,
    needsTranslation: true,
  },
  "common.no": {
    text: "No",
    context: "Negative response in dialogs.",
    category: "common",
    maxLength: 5,
    needsTranslation: true,
  },
  "common.or": {
    text: "or",
    context: "Conjunction between options.",
    category: "common",
    maxLength: 5,
    needsTranslation: true,
  },
  "common.and": {
    text: "and",
    context: "Conjunction connecting items.",
    category: "common",
    maxLength: 5,
    needsTranslation: true,
  },
  "common.all": {
    text: "All",
    context: "Option to select/show all items.",
    category: "common",
    maxLength: 5,
    needsTranslation: true,
  },
  "common.none": {
    text: "None",
    context: "Option indicating no selection.",
    category: "common",
    maxLength: 5,
    needsTranslation: true,
  },
  "common.date": {
    text: "Date",
    context: "Generic label for date fields.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.time": {
    text: "Time",
    context: "Generic label for time fields.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.name": {
    text: "Name",
    context: "Generic label for name fields.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.description": {
    text: "Description",
    context: "Generic label for description fields.",
    category: "common",
    maxLength: 15,
    needsTranslation: true,
  },
  "common.status": {
    text: "Status",
    context: "Generic label for status indicators.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.type": {
    text: "Type",
    context: "Generic label for type/category fields.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.active": {
    text: "Active",
    context: "Status label for active items.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.inactive": {
    text: "Inactive",
    context: "Status label for inactive items.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.pending": {
    text: "Pending",
    context: "Status label for items awaiting action.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.completed": {
    text: "Completed",
    context: "Status label for finished items.",
    category: "common",
    maxLength: 12,
    needsTranslation: true,
  },
  "common.processing": {
    text: "Processing",
    context: "Status label for items being processed.",
    category: "common",
    maxLength: 12,
    needsTranslation: true,
  },
  "common.failed": {
    text: "Failed",
    context: "Status label for failed operations.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.total": {
    text: "Total",
    context: "Label for sum/total values.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.more": {
    text: "More",
    context: "Link/button to show more items.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "common.less": {
    text: "Less",
    context: "Link/button to show fewer items.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // CHAT INTERFACE
  // ============================================================================
  "chat.type_message": {
    text: "Type a message...",
    context: "Placeholder in chat input field.",
    category: "chat",
    maxLength: 30,
    needsTranslation: true,
  },
  "chat.edit_transcription": {
    text: "Edit transcription and press Enter to send...",
    context: "Placeholder when editing voice transcription.",
    category: "chat",
    maxLength: 50,
    needsTranslation: true,
  },
  "chat.submitted_documents": {
    text: "Submitted",
    context: "Label for submitted documents section header.",
    category: "chat",
    maxLength: 15,
    needsTranslation: true,
  },
  "chat.attach_files": {
    text: "Attach files",
    context: "Tooltip for file attachment button.",
    category: "chat",
    maxLength: 15,
    needsTranslation: true,
  },
  "chat.take_photo": {
    text: "Take photo",
    context: "Tooltip for camera capture button.",
    category: "chat",
    maxLength: 15,
    needsTranslation: true,
  },
  "chat.send_message": {
    text: "Send message (Enter)",
    context: "Tooltip for send button.",
    category: "chat",
    maxLength: 25,
    needsTranslation: true,
  },
  "chat.stop_recording": {
    text: "Stop recording",
    context: "Tooltip for stopping voice recording.",
    category: "chat",
    maxLength: 20,
    needsTranslation: true,
  },
  "chat.record_voice": {
    text: "Record voice note",
    context: "Tooltip for starting voice recording.",
    category: "chat",
    maxLength: 20,
    needsTranslation: true,
  },

  // ============================================================================
  // DOMAIN SELECTOR
  // ============================================================================
  "clinical.search_cancer_types": {
    text: "Search cancer types...",
    context: "Placeholder in cancer type search field.",
    category: "clinical",
    maxLength: 30,
    needsTranslation: true,
  },
  "clinical.detected_by_ai": {
    text: "Detected by AI from documents",
    context: "Tooltip for AI-detected cancer types.",
    category: "clinical",
    maxLength: 35,
    needsTranslation: true,
  },
  "clinical.no_cancer_types": {
    text: "No cancer types found",
    context: "Message when search returns no results.",
    category: "clinical",
    maxLength: 25,
    needsTranslation: true,
  },

  // ============================================================================
  // BILLING & SETTINGS
  // ============================================================================
  "billing.organization_name": {
    text: "Organization name",
    context: "Placeholder for organization name input.",
    category: "billing",
    maxLength: 25,
    needsTranslation: true,
  },
  "billing.download_pdf": {
    text: "Download PDF",
    context: "Tooltip for PDF download button.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.view_online": {
    text: "View Online",
    context: "Tooltip for online viewing button.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.revoke_access": {
    text: "Revoke access",
    context: "Tooltip for removing shared access.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },

  // ============================================================================
  // FORMS & FIELDS
  // ============================================================================
  "form.search_fields": {
    text: "Search fields...",
    context: "Placeholder in field search input.",
    category: "forms",
    maxLength: 20,
    needsTranslation: true,
  },
  "form.folder_patient_info": {
    text: "Each folder becomes one patient. Folder name = Patient name.",
    context: "Tooltip explaining folder upload behavior.",
    category: "forms",
    maxLength: 60,
    needsTranslation: true,
  },
  "form.how_it_works": {
    text: "How it works?",
    context: "Label for help tooltip trigger.",
    category: "forms",
    maxLength: 15,
    needsTranslation: true,
  },
  "form.add_field": {
    text: "Add field",
    context: "Tooltip for button to add a new field.",
    category: "forms",
    maxLength: 15,
    needsTranslation: true,
  },

  // ============================================================================
  // ADD CLINICAL DATA (+) BUTTON
  // ============================================================================
  "add_data.title": {
    text: "Add Clinical Data",
    context:
      "Title of the searchable popover for adding tables, fields, or events.",
    category: "clinical",
    maxLength: 25,
    needsTranslation: true,
  },
  "add_data.search_placeholder": {
    text: "Search tables, fields, events...",
    context:
      "Placeholder in the search input of the Add Clinical Data popover.",
    category: "clinical",
    maxLength: 40,
    needsTranslation: true,
  },
  "add_data.results": {
    text: "results",
    context: "Suffix after the number of search results, e.g. '12 results'.",
    category: "clinical",
    maxLength: 10,
    needsTranslation: true,
  },
  "add_data.no_results": {
    text: "No matching items found",
    context:
      "Empty state when no search results match in the Add Clinical Data popover.",
    category: "clinical",
    maxLength: 30,
    needsTranslation: true,
  },
  "add_data.empty_hint": {
    text: "No clinical data yet. Use the sidebar to add new records.",
    context:
      "Hint shown in the middle of the data panel when a patient has no tables. Directs the user to the left-hand sidebar to begin adding data.",
    category: "clinical",
    maxLength: 60,
    needsTranslation: true,
  },
  "add_data.domain_search_placeholder": {
    text: "Search domains...",
    context:
      "Placeholder for the search input shown inside the sidebar 'Add domain' picker popover.",
    category: "clinical",
    maxLength: 30,
    needsTranslation: true,
  },
  "add_data.no_domain_results": {
    text: "No matching domains",
    context:
      "Empty state shown in the sidebar 'Add domain' picker when the search query matches no domain.",
    category: "clinical",
    maxLength: 30,
    needsTranslation: true,
  },
  "add_data.creating": {
    text: "Creating...",
    context: "Loading text while a clinical record is being created.",
    category: "clinical",
    maxLength: 15,
    needsTranslation: true,
  },
  "add_data.typing": {
    text: "Searching...",
    context: "Indicator shown while search input is being debounced.",
    category: "clinical",
    maxLength: 15,
    needsTranslation: true,
  },
  "add_data.create_table_hint": {
    text: "Create an empty table to start entering data",
    context:
      "Help text for table-level items in the Add Clinical Data popover.",
    category: "clinical",
    maxLength: 50,
    needsTranslation: true,
  },

  // ============================================================================
  // BILLING / ACCOUNT SETTINGS
  // ============================================================================
  "billing.account_settings": {
    text: "Account Settings",
    context: "Page title for account/billing settings.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.account_description": {
    text: "Manage your environments, subscription, usage, and team.",
    context: "Description text below account settings title.",
    category: "billing",
    maxLength: 60,
    needsTranslation: true,
  },
  "billing.tab_usage": {
    text: "Usage",
    context: "Tab label for usage statistics.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.tab_environments": {
    text: "Environments",
    context: "Tab label for environment management.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.tab_plans": {
    text: "Plans",
    context: "Tab label for subscription plans.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.tab_invoices": {
    text: "Invoices",
    context: "Tab label for invoice history.",
    category: "billing",
    maxLength: 12,
    needsTranslation: true,
  },
  "billing.tab_settings": {
    text: "Settings",
    context: "Tab label for account settings.",
    category: "billing",
    maxLength: 12,
    needsTranslation: true,
  },
  "billing.tab_contracts_legal": {
    text: "Contracts & Legal",
    context:
      "Tab label in the Billing/Account Settings page that opens the contracts and legal documents library.",
    category: "billing",
    maxLength: 22,
    needsTranslation: true,
  },
  "billing.legal.title": {
    text: "Contracts & Legal",
    context:
      "Title for the Contracts & Legal section in the billing/account settings page. Shows downloadable legal documents.",
    category: "billing",
    maxLength: 25,
    needsTranslation: true,
  },
  "billing.legal.subtitle": {
    text: "Review and download the legal documents used by Athena. Institutional signature workflows will appear here in a later phase.",
    context:
      "Subtitle under Contracts & Legal title explaining what the section is for.",
    category: "billing",
    maxLength: 160,
    needsTranslation: true,
  },
  "billing.legal.section_templates": {
    text: "Templates",
    context:
      "Section header in Contracts & Legal for downloadable document templates (T&C, consent, data agreement).",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.legal.section_commercial": {
    text: "Commercial contracts (Phase 2)",
    context:
      "Section header in Contracts & Legal for commercial contract templates that will be enabled in Phase 2.",
    category: "billing",
    maxLength: 40,
    needsTranslation: true,
  },
  "billing.legal.section_commercial_desc": {
    text: "These templates will support placeholder-filling and signed document upload or e-signature in a later phase.",
    context:
      "Description under the commercial contracts section explaining that signature workflows are coming later.",
    category: "billing",
    maxLength: 140,
    needsTranslation: true,
  },
  "billing.legal.download_pdf": {
    text: "Download PDF",
    context: "Button label to download a legal document as a PDF file.",
    category: "actions",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.legal.languages": {
    text: "Languages",
    context:
      "Label preceding language chips (ES/EN) in Contracts & Legal list items.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.legal.requires_signature": {
    text: "Requires signature",
    context:
      "Chip label shown on documents that require signature (patient consent, institution agreements).",
    category: "billing",
    maxLength: 24,
    needsTranslation: true,
  },
  "billing.legal.phase2": {
    text: "Phase 2",
    context:
      "Chip label indicating a feature/document is part of Phase 2 and not yet enabled.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.legal.phase2_locked": {
    text: "Coming soon",
    context:
      "Chip label for disabled documents in Contracts & Legal indicating they are not yet available.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.legal.load_error": {
    text: "Failed to load legal documents.",
    context:
      "Error message shown in Contracts & Legal when the API request fails.",
    category: "errors",
    maxLength: 60,
    needsTranslation: true,
  },
  "billing.legal.missing_source_title": {
    text: "Legal source documents are not available on the server.",
    context:
      "Warning shown in Contracts & Legal when the backend cannot find the legal DOCX source directory to generate PDFs.",
    category: "billing",
    maxLength: 90,
    needsTranslation: true,
  },
  "billing.legal.missing_source_help": {
    text: "Ask an administrator to configure LEGAL_DOCS_DIR or mount the legal documentation directory for the backend service.",
    context:
      "Help text explaining how to fix missing legal source docs configuration.",
    category: "billing",
    maxLength: 140,
    needsTranslation: true,
    translatorNotes:
      "Keep LEGAL_DOCS_DIR as a literal environment variable name.",
  },
  "billing.your_environments": {
    text: "Your Environments",
    context: "Section header for environment list.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.new_environment": {
    text: "New Environment",
    context: "Button to create a new environment.",
    category: "billing",
    maxLength: 18,
    needsTranslation: true,
  },
  "billing.no_environments": {
    text: "No environments yet",
    context: "Empty state title when no environments exist.",
    category: "billing",
    maxLength: 25,
    needsTranslation: true,
  },
  "billing.no_environments_desc": {
    text: "Create your first environment to start organizing patient data.",
    context: "Empty state description for environments.",
    category: "billing",
    maxLength: 70,
    needsTranslation: true,
  },
  "billing.create_environment": {
    text: "Create Environment",
    context: "Button to create an environment.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.create_new_environment": {
    text: "Create New Environment",
    context: "Dialog title for creating environment.",
    category: "modals",
    maxLength: 25,
    needsTranslation: true,
  },
  "billing.edit_environment": {
    text: "Edit Environment",
    context: "Dialog title for editing environment.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.environment_name": {
    text: "Environment Name",
    context: "Label for environment name input field.",
    category: "forms",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.environment_placeholder": {
    text: "e.g., Cancer Research Study 2024",
    context: "Placeholder for environment name input.",
    category: "forms",
    maxLength: 40,
    needsTranslation: true,
  },
  "billing.environment_helper": {
    text: "Give your environment a descriptive name",
    context: "Helper text for environment name field.",
    category: "forms",
    maxLength: 45,
    needsTranslation: true,
  },
  "billing.feature_flags": {
    text: "Feature Flags",
    context: "Section header for environment feature toggles.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.allow_ai_extraction": {
    text: "Allow AI Extraction",
    context: "Toggle label for AI extraction feature.",
    category: "billing",
    maxLength: 22,
    needsTranslation: true,
  },
  "billing.ai_extraction_desc": {
    text: "Enable AI-powered data extraction for this environment",
    context: "Description for AI extraction toggle.",
    category: "billing",
    maxLength: 60,
    needsTranslation: true,
  },
  "billing.allow_export": {
    text: "Allow Data Export",
    context: "Toggle label for data export feature.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.export_desc": {
    text: "Allow users to export patient data from this environment",
    context: "Description for data export toggle.",
    category: "billing",
    maxLength: 60,
    needsTranslation: true,
  },
  "billing.require_anonymization": {
    text: "Require Anonymization",
    context: "Toggle label for anonymization requirement.",
    category: "billing",
    maxLength: 25,
    needsTranslation: true,
  },
  "billing.anonymization_desc": {
    text: "Hide patient names for non-admin users",
    context: "Description for anonymization toggle.",
    category: "billing",
    maxLength: 45,
    needsTranslation: true,
  },
  "billing.save_changes": {
    text: "Save Changes",
    context: "Button to save edited environment settings.",
    category: "actions",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.patients_count": {
    text: "patients",
    context: "Label for patient count in environment card.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.export_chip": {
    text: "Export",
    context: "Chip label indicating export is enabled.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.anonymized_chip": {
    text: "Anonymized",
    context: "Chip label indicating anonymization is enabled.",
    category: "billing",
    maxLength: 12,
    needsTranslation: true,
  },
  "billing.full_access_chip": {
    text: "Full Access",
    context: "Chip label indicating full access permission.",
    category: "billing",
    maxLength: 12,
    needsTranslation: true,
  },
  "billing.enable_overage": {
    text: "Enable Overage Billing",
    context: "Toggle label for overage billing setting.",
    category: "billing",
    maxLength: 25,
    needsTranslation: true,
  },
  "billing.enable_ai": {
    text: "Enable AI",
    context: "Toggle label for the org-wide AI extraction setting in billing settings.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.enable_ai_desc": {
    text: "When enabled, AI extraction runs across every environment in your workspace. When disabled, documents are stored without automatic AI extraction.",
    context: "Caption describing the org-wide AI toggle in billing settings.",
    category: "billing",
    maxLength: 200,
    needsTranslation: true,
  },
  "billing.ai_not_in_plan": {
    text: "AI is not included in your current plan.",
    context: "Caption shown when the subscription tier does not permit AI extraction, disabling the toggle.",
    category: "billing",
    maxLength: 60,
    needsTranslation: true,
  },
  "billing.popular": {
    text: "Popular",
    context: "Badge label for popular plan.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.tokens": {
    text: "Tokens",
    context: "Tab label for token usage.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.documents": {
    text: "Documents",
    context: "Tab label for document usage.",
    category: "billing",
    maxLength: 12,
    needsTranslation: true,
  },
  "billing.voice": {
    text: "Voice",
    context: "Tab label for voice minutes usage.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // SHARE ENVIRONMENT MODAL
  // ============================================================================
  "share.environment_label": {
    text: "Environment",
    context: "Label for environment selector in share modal.",
    category: "modals",
    maxLength: 15,
    needsTranslation: true,
  },
  "share.email_label": {
    text: "Organization Email",
    context: "Label for email input in share modal.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },
  "share.permission_label": {
    text: "Permission Level",
    context: "Label for permission selector in share modal.",
    category: "modals",
    maxLength: 18,
    needsTranslation: true,
  },
  "share.anonymize_label": {
    text: "Anonymize Patient Data",
    context: "Label for anonymization toggle in share modal.",
    category: "modals",
    maxLength: 25,
    needsTranslation: true,
  },
  "share.expires_label": {
    text: "Expires On (Optional)",
    context: "Label for expiration date picker in share modal.",
    category: "modals",
    maxLength: 22,
    needsTranslation: true,
  },

  // ============================================================================
  // LOGIN / AUTH (additions)
  // ============================================================================
  "auth.username": {
    text: "Username",
    context: "Label for username input field on login page.",
    category: "auth",
    maxLength: 15,
    needsTranslation: true,
  },
  "auth.login_button": {
    text: "Log In",
    context: "Button to submit login form.",
    category: "auth",
    maxLength: 10,
    needsTranslation: true,
  },
  "auth.logging_in": {
    text: "Logging in...",
    context: "Button text while login is processing.",
    category: "auth",
    maxLength: 15,
    needsTranslation: true,
  },

  // ============================================================================
  // MODALS (GENERAL)
  // ============================================================================
  "modal.patient_name": {
    text: "Patient Name",
    context: "Label for patient name input in modal.",
    category: "modals",
    maxLength: 15,
    needsTranslation: true,
  },
  "modal.age": {
    text: "Age",
    context: "Label for age input in modal.",
    category: "modals",
    maxLength: 10,
    needsTranslation: true,
  },
  "modal.create_new_environment": {
    text: "Create New Environment",
    context: "Label for environment creation option in modal.",
    category: "modals",
    maxLength: 25,
    needsTranslation: true,
  },
  "modal.add_patient": {
    text: "Add Patient",
    context: "Title/button for adding a new patient in modal.",
    category: "modals",
    maxLength: 15,
    needsTranslation: true,
  },
  "modal.find_patient": {
    text: "Find Patient",
    context: "Title for finding/searching patients in modal.",
    category: "modals",
    maxLength: 15,
    needsTranslation: true,
  },
  "modal.select_environment": {
    text: "Select Environment",
    context: "Title for environment selection modal.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },

  // ============================================================================
  // COMMON (Additional)
  // ============================================================================
  "common.never": {
    text: "Never",
    context: "Indicates no expiration date set.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // BILLING (Additional)
  // ============================================================================
  "billing.no_usage_data": {
    text: "No usage data yet. Start using the platform to see your history.",
    context: "Empty state message in usage dashboard.",
    category: "billing",
    maxLength: 80,
    needsTranslation: true,
  },

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================
  "notifications.title": {
    text: "Notifications",
    context: "Title for the notification dropdown panel in the top navbar.",
    category: "notifications",
    maxLength: 20,
    needsTranslation: true,
  },
  "notifications.mark_all_read": {
    text: "Mark all read",
    context: "Button to mark all notifications as read.",
    category: "notifications",
    maxLength: 20,
    needsTranslation: true,
  },
  "notifications.empty": {
    text: "No notifications",
    context: "Empty state when there are no notifications.",
    category: "notifications",
    maxLength: 25,
    needsTranslation: true,
  },
  "notifications.task.document_upload": {
    text: "Document uploaded",
    context: "Notification title for a completed document upload.",
    category: "notifications",
    maxLength: 25,
    needsTranslation: true,
  },
  "notifications.task.llm_extraction": {
    text: "Extraction complete",
    context: "Notification title when LLM extraction finishes.",
    category: "notifications",
    maxLength: 25,
    needsTranslation: true,
  },
  "notifications.task.bulk_upload": {
    text: "Bulk upload",
    context: "Notification title for bulk upload progress/completion.",
    category: "notifications",
    maxLength: 20,
    needsTranslation: true,
  },
  "notifications.task.export": {
    text: "Data export ready",
    context: "Notification title when a data export completes.",
    category: "notifications",
    maxLength: 25,
    needsTranslation: true,
  },
  "notifications.status.in_progress": {
    text: "Processing...",
    context: "Status label for an active/in-progress task notification.",
    category: "notifications",
    maxLength: 15,
    needsTranslation: true,
  },
  "notifications.status.failed": {
    text: "Failed",
    context: "Status label for a failed task notification.",
    category: "notifications",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // COST ESTIMATOR
  // ============================================================================

  // ─── Bulk Upload v2: Tabs, ZIP, Hierarchy ─────────────────────────────

  // ─── Phase 2B: CSV / Excel Wizard ──────────────────────────────────────
  "csv.invalid_type": {
    text: "Please upload a .csv, .xlsx, or .xls file.",
    context:
      "Error when user selects an unsupported file type in CSV/Excel wizard.",
    category: "bulk_upload",
    maxLength: 60,
    needsTranslation: true,
  },
  "csv.too_large": {
    text: "File exceeds the 50 MB limit.",
    context: "Error when CSV/Excel file is too large.",
    category: "bulk_upload",
    maxLength: 40,
    needsTranslation: true,
  },
  "csv.parsing": {
    text: "Parsing file...",
    context: "Loading state while server parses the uploaded CSV/Excel file.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.select_file": {
    text: "Select CSV or Excel File",
    context: "Button label to open the file picker for CSV/Excel upload.",
    category: "bulk_upload",
    maxLength: 30,
    needsTranslation: true,
  },
  "csv.max_size": {
    text: "Maximum file size: 50 MB",
    context: "Caption below the file upload button indicating the size limit.",
    category: "bulk_upload",
    maxLength: 30,
    needsTranslation: true,
  },
  "csv.change_file": {
    text: "Change File",
    context:
      "Button label to select a different CSV/Excel file after one is already uploaded.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.header_row_title": {
    text: "Select header row",
    context:
      "Title above the raw preview table where users pick which row contains column headers.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.header_row_help": {
    text: "Click a row to use it as column headers. Rows above it will be skipped.",
    context:
      "Help text explaining how to select the header row in the raw preview table.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.header_row_current": {
    text: "Header row: {row}",
    context:
      "Caption showing the currently selected header row number. {row} is replaced with the 1-based row number.",
    category: "bulk_upload",
    placeholders: ["row"],
    needsTranslation: true,
  },
  "csv.row_skipped": {
    text: "row skipped",
    context: "Singular form — number of rows skipped above the header row.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.rows_skipped": {
    text: "rows skipped",
    context: "Plural form — number of rows skipped above the header row.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.mode_title": {
    text: "What would you like to do?",
    context: "Title for mode selection step in CSV/Excel wizard.",
    category: "bulk_upload",
    maxLength: 40,
    needsTranslation: true,
  },
  "csv.mode_create": {
    text: "CREATE new patients",
    context: "Radio label for CREATE mode in CSV wizard.",
    category: "bulk_upload",
    maxLength: 30,
    needsTranslation: true,
  },
  "csv.mode_create_desc": {
    text: "Each row (or group of rows) becomes a new patient in the platform.",
    context: "Description for CREATE mode.",
    category: "bulk_upload",
    maxLength: 80,
    needsTranslation: true,
  },
  "csv.mode_import": {
    text: "IMPORT data into existing patients",
    context: "Radio label for IMPORT mode in CSV wizard.",
    category: "bulk_upload",
    maxLength: 40,
    needsTranslation: true,
  },
  "csv.mode_import_desc": {
    text: "Match rows to patients that already exist and add new data.",
    context: "Description for IMPORT mode.",
    category: "bulk_upload",
    maxLength: 80,
    needsTranslation: true,
  },
  "csv.grouping_title": {
    text: "Row Structure",
    context: "Title for grouping detection step.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.grouping_individual": {
    text: "Each row = one patient",
    context: "Radio label for individual row mode.",
    category: "bulk_upload",
    maxLength: 30,
    needsTranslation: true,
  },
  "csv.grouping_grouped": {
    text: "Group rows by a column (multiple events per patient)",
    context: "Radio label for grouped row mode.",
    category: "bulk_upload",
    maxLength: 60,
    needsTranslation: true,
  },
  "csv.grouping_column": {
    text: "Group by column",
    context: "Dropdown label for selecting the grouping column.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.preview_title": {
    text: "Data Preview",
    context: "Title for the data preview table in the CSV wizard.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.schema_title": {
    text: "Column Definitions",
    context: "Title for the schema inference step.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.inferring": {
    text: "Analyzing...",
    context: "Loading state during LLM schema inference.",
    category: "bulk_upload",
    maxLength: 15,
    needsTranslation: true,
  },
  "csv.run_inference": {
    text: "Auto-Detect with AI",
    context: "Button label to trigger LLM schema inference.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.template_suggestion": {
    text: "We found saved templates matching your columns:",
    context: "Info alert when saved schema templates are found.",
    category: "bulk_upload",
    maxLength: 60,
    needsTranslation: true,
  },
  "csv.schema_help": {
    text: "These descriptions will guide the AI when processing each patient. Edit any incorrect descriptions.",
    context: "Help text for the column definitions editing step.",
    category: "bulk_upload",
    maxLength: 120,
    needsTranslation: true,
  },
  "csv.patient_name_col": {
    text: "Patient Name / ID Column",
    context:
      "Dropdown label for selecting which column contains the patient name.",
    category: "bulk_upload",
    maxLength: 30,
    needsTranslation: true,
  },
  "csv.create_preview_title": {
    text: "Patients to Create",
    context: "Title for patient preview in CREATE mode.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.create_grouped_desc": {
    text: "Rows will be grouped by the selected column. Each group becomes one patient.",
    context: "Description when rows are grouped in CREATE mode.",
    category: "bulk_upload",
    maxLength: 80,
    needsTranslation: true,
  },
  "csv.create_individual_desc": {
    text: "Each row will create one patient.",
    context: "Description when rows are individual in CREATE mode.",
    category: "bulk_upload",
    maxLength: 40,
    needsTranslation: true,
  },
  "csv.import_title": {
    text: "Match Patients",
    context: "Title for patient matching step in IMPORT mode.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.match_by": {
    text: "Match patients by",
    context: "Dropdown label for selecting matching tier.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.id_column": {
    text: "ID Column in File",
    context:
      "Dropdown label for selecting which column contains the matching ID.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.merge_mode_title": {
    text: "Merge Mode",
    context: "Title for merge mode selection in IMPORT mode.",
    category: "bulk_upload",
    maxLength: 15,
    needsTranslation: true,
  },
  "csv.all_columns_described": {
    text: "All {count} columns have descriptions.",
    context:
      "Tabular import: confirmation that every column has a description. {count} is preserved.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.columns_described_count": {
    text: "{filled}/{total} columns have descriptions. Columns without descriptions will use their header name.",
    context:
      "Tabular import: how many columns have descriptions. {filled} and {total} are preserved.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.drop_hint": {
    text: "Drop .csv / .xlsx / .xls to upload",
    context:
      "Tabular import: drop-zone hint listing the accepted spreadsheet file types.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.high_cost_warning": {
    text: "This import will cost more than €5. Please verify this is the correct file and that you have sufficient quota.",
    context:
      "Tabular import: warning shown when the estimated import cost exceeds five euros.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.matching_on_column": {
    text: "Matching on column",
    context:
      "Tabular import: label before the spreadsheet column used to match patients by external ID.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.rerun_inference": {
    text: "Re-run AI Detection",
    context:
      "Tabular import: button to run AI schema/column detection again.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.start_over": {
    text: "Upload Another File",
    context:
      "Tabular import: button to discard the current file and upload a different spreadsheet.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.use_suggested_header": {
    text: "Use row {row} as header (recommended)",
    context:
      "Tabular import: button to adopt the AI-suggested header row. {row} is preserved.",
    category: "bulk_upload",
    needsTranslation: true,
  },
  "csv.merge_append": {
    text: "APPEND: Add new rows only (safe)",
    context: "Radio label for APPEND merge mode.",
    category: "bulk_upload",
    maxLength: 40,
    needsTranslation: true,
  },
  "csv.merge_upsert": {
    text: "UPSERT: Add new + update matching rows",
    context: "Radio label for UPSERT merge mode.",
    category: "bulk_upload",
    maxLength: 45,
    needsTranslation: true,
  },
  "csv.merge_replace": {
    text: "REPLACE: Delete existing, insert new (destructive)",
    context: "Radio label for REPLACE merge mode.",
    category: "bulk_upload",
    maxLength: 55,
    needsTranslation: true,
  },
  "csv.replace_warning": {
    text: "REPLACE mode will delete all existing data in the target domain tables for matched patients. This cannot be undone.",
    context: "Warning alert for destructive REPLACE merge mode.",
    category: "bulk_upload",
    maxLength: 130,
    needsTranslation: true,
  },
  "csv.matching": {
    text: "Matching...",
    context: "Loading state during patient matching.",
    category: "bulk_upload",
    maxLength: 15,
    needsTranslation: true,
  },
  "csv.run_matching": {
    text: "Preview Matches",
    context: "Button label to trigger patient matching preview.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.confirm_title": {
    text: "Confirm & Start Processing",
    context: "Title for the final confirmation step.",
    category: "bulk_upload",
    maxLength: 30,
    needsTranslation: true,
  },
  "csv.summary_mode": {
    text: "Mode",
    context: "Label for mode in the summary panel.",
    category: "bulk_upload",
    maxLength: 10,
    needsTranslation: true,
  },
  "csv.summary_rows": {
    text: "Total rows",
    context: "Label for total rows in the summary panel.",
    category: "bulk_upload",
    maxLength: 15,
    needsTranslation: true,
  },
  "csv.summary_columns": {
    text: "Columns",
    context: "Label for column count in the summary panel.",
    category: "bulk_upload",
    maxLength: 10,
    needsTranslation: true,
  },
  "csv.summary_grouping": {
    text: "Grouping",
    context: "Label for grouping config in the summary panel.",
    category: "bulk_upload",
    maxLength: 15,
    needsTranslation: true,
  },
  "csv.summary_patient_col": {
    text: "Patient column",
    context: "Label for patient name column in the summary panel.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.summary_descriptions": {
    text: "Described columns",
    context: "Label for described column count in the summary panel.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.cost_title": {
    text: "Estimated Cost",
    context: "Title for the cost estimation panel.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.save_template": {
    text: "Save column definitions as a template for future uploads",
    context: "Checkbox label for saving schema template.",
    category: "bulk_upload",
    maxLength: 60,
    needsTranslation: true,
  },
  "csv.template_name": {
    text: "Template Name",
    context: "Input label for the saved template name.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.processing_title": {
    text: "Processing Spreadsheet Import",
    context: "Title shown during spreadsheet import processing.",
    category: "bulk_upload",
    maxLength: 35,
    needsTranslation: true,
  },
  "csv.back": {
    text: "Back",
    context: "Navigation button to go to previous wizard step.",
    category: "bulk_upload",
    maxLength: 10,
    needsTranslation: true,
  },
  "csv.next": {
    text: "Next",
    context: "Navigation button to go to next wizard step.",
    category: "bulk_upload",
    maxLength: 10,
    needsTranslation: true,
  },
  "csv.analyze": {
    text: "Analyze with AI",
    context:
      "Button label to trigger AI schema inference from the Next button.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.start_processing": {
    text: "Start Processing",
    context: "Button label to start the tabular upload processing.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.review_in_cards": {
    text: "Review & edit in cards",
    context:
      "Secondary button at the wizard's final step (Confirm & Start). Only rendered when the wizard is mounted inside the V2 cards UI modal. Closes the modal, materializes editable cards in V2 form.",
    category: "bulk_upload",
    maxLength: 30,
    needsTranslation: true,
  },
  "csv.failed_review_stage": {
    text: "Failed to stage review. Please retry.",
    context:
      "Error snackbar when the V2 'Review & edit in cards' POST (review_only=true) fails. User can retry from the same wizard step.",
    category: "bulk_upload",
    maxLength: 80,
    needsTranslation: true,
  },

  // ---------------------------------------------------------------------------
  // Tutorial / Onboarding
  // ---------------------------------------------------------------------------

  // ── Step 0 — Language Selection (centered, no spotlight) ─────────────
  "tutorial.language.title": {
    text: "Welcome to Athena Oncocraft",
    context: "Tutorial step 0: language selection (centered card).",
    category: "tutorial",
    maxLength: 30,
    needsTranslation: true,
  },
  "tutorial.language.description": {
    text: "Set your interface language and workspace defaults. You can change these later in Settings.",
    context:
      "Welcome setup modal (first login). No longer a tutorial step: the card also carries the AI and data-region controls, and nothing follows it, so the old 'before we begin' framing was false.",
    category: "tutorial",
    maxLength: 110,
    needsTranslation: true,
  },
  "tutorial.language.db_note": {
    text: "Note: The interface is displayed in your chosen language, but clinical database fields remain in English to ensure consistency across centres and users.",
    context: "Tutorial step 0: clarification about UI vs. DB language.",
    category: "tutorial",
    maxLength: 200,
    needsTranslation: true,
  },
  "tutorial.continue": {
    text: "Continue",
    context: "Tutorial continue button (language step).",
    category: "tutorial",
    maxLength: 15,
    needsTranslation: true,
  },
  "tutorial.settings.enable_ai": {
    text: "Enable AI",
    context: "First-login step: label for the org-wide AI extraction toggle.",
    category: "tutorial",
    maxLength: 20,
    needsTranslation: true,
  },
  "tutorial.settings.enable_ai_desc": {
    text: "Run AI extraction automatically across every environment in your workspace.",
    context: "First-login step: caption describing the org-wide AI toggle.",
    category: "tutorial",
    maxLength: 120,
    needsTranslation: true,
  },
  "tutorial.settings.ai_not_in_plan": {
    text: "AI is not included in your current plan.",
    context: "First-login step: caption shown when the tier does not permit AI.",
    category: "tutorial",
    maxLength: 60,
    needsTranslation: true,
  },
  "tutorial.settings.data_region": {
    text: "Data Processing Region",
    context: "First-login step: label for the data processing region selector.",
    category: "tutorial",
    maxLength: 30,
    needsTranslation: true,
  },
  "tutorial.settings.region_eu": {
    text: "Europe only",
    context: "First-login step: option for EU-only data processing region.",
    category: "tutorial",
    maxLength: 20,
    needsTranslation: true,
  },
  "tutorial.settings.region_global": {
    text: "Global (fastest)",
    context: "First-login step: option for global data processing region.",
    category: "tutorial",
    maxLength: 20,
    needsTranslation: true,
  },
  "tutorial.settings.region_eu_desc": {
    text: "Data is processed exclusively in Europe. Required for strict GDPR compliance.",
    context: "First-login step: caption describing the EU-only region option.",
    category: "tutorial",
    maxLength: 120,
    needsTranslation: true,
  },
  "tutorial.settings.region_global_desc": {
    text: "Data may be processed in any Azure region for best performance.",
    context: "First-login step: caption describing the global region option.",
    category: "tutorial",
    maxLength: 120,
    needsTranslation: true,
  },
  "tutorial.settings.region_needs_ai": {
    text: "Enable AI above to choose where data is processed.",
    context:
      "First-login step: shown under the region dropdown while it is disabled because AI is off. The region only selects the LLM deployment, so it has no effect until AI is enabled.",
    category: "tutorial",
    maxLength: 120,
    needsTranslation: true,
  },

  // ── Step 1 — Create Patient FAB (dashboard) ────────────────────────────
  "tutorial.create_patient.title": {
    text: "Create a New Patient",
    context:
      "Tutorial step — highlights the (+) FAB button that opens the patient form.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },
  "tutorial.create_patient.description": {
    text: "Press Next (or tap the button) and we'll open the form for you.",
    context:
      "Tutorial step — explains what the (+) FAB does. Form is auto-opened on Next or FAB click.",
    category: "tutorial",
    maxLength: 80,
    needsTranslation: true,
  },

  // ── Step 2 — Patient Form (name + environment, highlighted together) ──
  "tutorial.patient_form.title": {
    text: "Patient Registration",
    context:
      "Tutorial step — highlights the patient creation form (name input).",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },
  "tutorial.patient_form.description": {
    text: "We've filled in a test name. A patient needs at least a name or an external ID, and the name we filled is enough. Press Next to continue.",
    context:
      "Tutorial step — explains the form. A patient requires at least a name OR an external ID (not name alone); the tutorial pre-fills the name so the user can proceed.",
    category: "tutorial",
    maxLength: 160,
    needsTranslation: false,
  },
  "tutorial.patient_name.placeholder": {
    text: "Test Patient",
    context: "Pre-filled placeholder for the patient name during the tutorial.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },

  // ── Step 4 — Submit Patient (dashboard, requiresAction) ──────────────
  "tutorial.submit_patient.title": {
    text: "Confirm and Create",
    context: "Tutorial step: click the Create Patient button.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },
  "tutorial.submit_patient.description": {
    text: "Everything is set. Press the highlighted Create Patient button to register the record.",
    context:
      "Tutorial step — submit the create patient form. User must click the orange button.",
    category: "tutorial",
    maxLength: 120,
    needsTranslation: true,
  },
  "tutorial.submit_patient.action_hint": {
    text: "Press Create Patient",
    context: "Tutorial action hint for the submit button.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },

  // ── Step 4 — Sidebar overview (patient details) ────────────────────
  "tutorial.sidebar_overview.title": {
    text: "Navigate with the Sidebar",
    context: "Tutorial step — spotlights the left sidebar in patient details.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },
  "tutorial.sidebar_overview.description": {
    text: "Use the sidebar to navigate. Universal tables (Profile, Molecular, Hematological, etc.) apply to all patients regardless of their pathology. Cancer-specific sections appear below.",
    context: "Tutorial step — explains universal vs domain sections in the sidebar.",
    category: "tutorial",
    maxLength: 180,
    needsTranslation: true,
  },

  // ── Step 5 — Diagnosis & Patient Journey (sidebar domain section) ──
  "tutorial.sidebar_domain.title": {
    text: "Diagnosis & Patient Journey",
    context: "Tutorial step — spotlights the domain section of the sidebar.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },
  "tutorial.sidebar_domain.description": {
    text: "Each cancer section has two tables. Diagnosis & Baseline: a clinical snapshot of staging and histology. Patient Journey: a timeline of procedures, treatments, and follow-up. Other sections like Profile and Molecular are snapshot-only. More tables are waiting to be explored.",
    context: "Tutorial step — explains the difference between static (diagnosis) and event (patient journey) tables, and hints at more universal tables.",
    category: "tutorial",
    maxLength: 280,
    needsTranslation: true,
  },

  // ── Step 6 — Clinical tables area ─────────────────────────────────
  "tutorial.data_panel.title": {
    text: "Your Clinical Tables",
    context: "Tutorial step — highlights the main data area in patient details.",
    category: "tutorial",
    maxLength: 30,
    needsTranslation: true,
  },
  "tutorial.data_panel.description": {
    text: "Click a sidebar item to jump to that section. Use Show all fields to reveal and edit every available field, even empty ones.",
    context: "Tutorial step — explains how to browse and edit clinical data tables.",
    category: "tutorial",
    maxLength: 150,
    needsTranslation: true,
  },

  // ── Step 7 — Floating chat bar (collapsed) ─────────────────────────
  "tutorial.floating_chat.title": {
    text: "AI-Powered Data Entry",
    context: "Tutorial step — spotlights the collapsed floating chat bar at the bottom of patient details.",
    category: "tutorial",
    maxLength: 30,
    needsTranslation: true,
  },
  "tutorial.floating_chat.description": {
    text: "The chat panel lives at the bottom. Tap it to expand, then type notes, paste reports, or upload documents.",
    context: "Tutorial step — introduces the floating chat panel.",
    category: "tutorial",
    maxLength: 120,
    needsTranslation: true,
  },

  // ── Step 8 — Expand the chat (requiresAction) ────────────────────
  "tutorial.expand_chat.title": {
    text: "Open the Chat",
    context: "Tutorial step — user must click the expand arrow on the floating chat bar.",
    category: "tutorial",
    maxLength: 20,
    needsTranslation: true,
  },
  "tutorial.expand_chat.description": {
    text: "Tap the arrow to expand the panel.",
    context: "Tutorial step — prompts user to expand the floating chat.",
    category: "tutorial",
    maxLength: 50,
    needsTranslation: true,
  },
  "tutorial.expand_chat.action_hint": {
    text: "Tap the arrow",
    context: "Tutorial action hint for the chat expand button.",
    category: "tutorial",
    maxLength: 20,
    needsTranslation: true,
  },

  // ── Step 9 — Chat Input (patient details, with prefill) ───────────
  "tutorial.chat_input.title": {
    text: "Clinical Notes & Documents",
    context: "Tutorial step: chat input area, pre-filled with sample text.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },
  "tutorial.chat_input.description": {
    text: "We've added a sample note. Type free-text, paste reports, or drag documents. Press Next when ready.",
    context: "Tutorial step — explains the chat input with pre-filled sample text.",
    category: "tutorial",
    maxLength: 120,
    needsTranslation: true,
  },

  // ── Step 10 — Send Message (patient details, requiresAction) ─────────
  "tutorial.send_message.title": {
    text: "Submit the Note",
    context: "Tutorial step: send message button.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },
  "tutorial.send_message.description": {
    text: "Press Send. Athena will extract structured data automatically.",
    context: "Tutorial step — user must click the orange Send button.",
    category: "tutorial",
    maxLength: 80,
    needsTranslation: true,
  },
  "tutorial.send_message.action_hint": {
    text: "Press Send",
    context: "Tutorial action hint for the send button.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },

  // ── Step 13 — Notifications (navbar) ─────────────────────────────────
  "tutorial.notifications.title": {
    text: "Processing Notifications",
    context: "Tutorial step: notification bell.",
    category: "tutorial",
    maxLength: 50,
    needsTranslation: true,
  },
  "tutorial.notifications.description": {
    text: "Track progress here. Each extraction sends a notification with a direct link back to the patient.",
    context: "Tutorial step: explains the notification system.",
    category: "tutorial",
    maxLength: 120,
    needsTranslation: true,
  },

  // ── Step 15 — Search Patient (dashboard) ─────────────────────────────
  "tutorial.search_patient.title": {
    text: "Find Your Patients",
    context: "Tutorial step: search bar on the dashboard.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },
  "tutorial.search_patient.description": {
    text: "Start typing a name to find any patient instantly.",
    context:
      "Tutorial step: search area on the dashboard (search bar + results list).",
    category: "tutorial",
    maxLength: 60,
    needsTranslation: true,
  },
  "tutorial.search_patient.description_with_name": {
    text: 'We\'ve typed "{patientName}" for you. Results appear below as you type.',
    context: "Tutorial step: search area with pre-filled patient name.",
    category: "tutorial",
    maxLength: 80,
    needsTranslation: true,
  },
  "tutorial.search_patient.description_no_patient": {
    text: "Start typing a name to find any patient instantly.",
    context:
      "Tutorial step: search area (no patient was created during tutorial).",
    category: "tutorial",
    maxLength: 60,
    needsTranslation: true,
  },

  // ── Step 14 — Athena Logo / Home (navbar, auto-navigate on Next) ────
  "tutorial.athena_home.title": {
    text: "Back to Dashboard",
    context:
      "Tutorial step: click the Athena logo to go back to the dashboard.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },
  "tutorial.athena_home.description": {
    text: "The logo returns you to the dashboard at any time. Press Next and we'll take you there.",
    context:
      "Tutorial step — explains the home button. Auto-navigates on Next.",
    category: "tutorial",
    maxLength: 100,
    needsTranslation: true,
  },
  "tutorial.athena_home.action_hint": {
    text: "Click the Athena logo",
    context: "Tutorial action hint for the Athena logo.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },

  // ── Step 16 — Congratulations (centered, no spotlight) ──────────────
  "tutorial.congrats.title": {
    text: "You're all set!",
    context: "Tutorial completion card title.",
    category: "tutorial",
    maxLength: 30,
    needsTranslation: true,
  },
  "tutorial.congrats.description": {
    text: "Upload documents, type clinical notes, and Athena structures the data for you. Questions? support@athenatechai.com",
    context: "Tutorial completion card description with support email.",
    category: "tutorial",
    maxLength: 140,
    needsTranslation: true,
  },

  // ── NEW: Processing step (extraction-processing) ────────────────────
  "tutorial.processing.title": {
    text: "Extracting clinical data",
    context: "Title of the processing step card shown while the AI simulates data extraction during the tutorial.",
    category: "tutorial",
    maxLength: 40,
    needsTranslation: true,
  },
  "tutorial.processing.description": {
    text: "Athena is analyzing the clinical note and structuring the findings into clinical tables.",
    context: "Description shown on the processing step card during the tutorial extraction simulation.",
    category: "tutorial",
    maxLength: 120,
    needsTranslation: true,
  },
  "tutorial.processing.msg_analyzing": {
    text: "Analyzing clinical document...",
    context: "First rotating message shown during the tutorial extraction processing animation.",
    category: "tutorial",
    maxLength: 50,
    needsTranslation: true,
  },
  "tutorial.processing.msg_detecting": {
    text: "Detecting cancer type...",
    context: "Second rotating message shown during the tutorial extraction processing animation.",
    category: "tutorial",
    maxLength: 50,
    needsTranslation: true,
  },
  "tutorial.processing.msg_structuring": {
    text: "Structuring clinical data...",
    context: "Third rotating message shown during the tutorial extraction processing animation.",
    category: "tutorial",
    maxLength: 50,
    needsTranslation: true,
  },
  "tutorial.processing.msg_building": {
    text: "Building clinical tables...",
    context: "Fourth rotating message shown during the tutorial extraction processing animation.",
    category: "tutorial",
    maxLength: 50,
    needsTranslation: true,
  },
  "tutorial.processing.please_wait": {
    text: "Please wait...",
    context: "Shown below the progress bar while the tutorial extraction is still in progress.",
    category: "tutorial",
    maxLength: 30,
    needsTranslation: true,
  },

  // ── Step 12 — Data Extracted (spotlight sidebar after processing) ────
  "tutorial.data_explained.title": {
    text: "Data Extracted",
    context: "Title of the tutorial step after AI processing completes, spotlighting the sidebar.",
    category: "tutorial",
    maxLength: 20,
    needsTranslation: true,
  },
  "tutorial.data_explained.description": {
    text: "Look at the sidebar. Patient Journey now lists each extracted event. Tap any item to jump to it. Use Show all fields to add more detail manually.",
    context: "Short description directing the user to the sidebar after AI extraction.",
    category: "tutorial",
    maxLength: 180,
    needsTranslation: true,
  },

  // ── Generic tutorial actions ─────────────────────────────────────────
  "tutorial.click_to_continue": {
    text: "Click the highlighted element",
    context:
      "Generic action hint for tutorial steps that require user interaction.",
    category: "tutorial",
    maxLength: 50,
    needsTranslation: true,
  },
  "tutorial.skip": {
    text: "Skip",
    context: "Tutorial skip button.",
    category: "tutorial",
    maxLength: 10,
    needsTranslation: true,
  },
  "tutorial.next": {
    text: "Next",
    context: "Tutorial next button.",
    category: "tutorial",
    maxLength: 10,
    needsTranslation: true,
  },
  "tutorial.back": {
    text: "Back",
    context: "Tutorial back button.",
    category: "tutorial",
    maxLength: 10,
    needsTranslation: true,
  },
  "tutorial.finish": {
    text: "Finish",
    context: "Tutorial finish button (last step).",
    category: "tutorial",
    maxLength: 10,
    needsTranslation: true,
  },
  "tutorial.of": {
    text: "of",
    context: "Tutorial step counter separator (e.g., '2 of 5').",
    category: "tutorial",
    maxLength: 5,
    needsTranslation: true,
  },
  "tutorial.menu_item": {
    text: "Quick Tutorial",
    context: "Menu item in TopNavBar user menu to restart the tutorial.",
    category: "tutorial",
    maxLength: 20,
    needsTranslation: true,
  },

  // ---------------------------------------------------------------------------
  // Dashboard Empty States
  // ---------------------------------------------------------------------------

  "dashboard.empty_state.title": {
    text: "No more manual entry.",
    context: "Dashboard empty state headline when no patients exist.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },
  "dashboard.empty_state.subtitle": {
    text: "Create your first patient to start using the platform. Upload clinical documents and let Athena extract structured data automatically.",
    context: "Dashboard empty state subtitle.",
    category: "dashboard",
    maxLength: 150,
    needsTranslation: true,
  },
  "dashboard.empty_state.start_tutorial": {
    text: "Start Tutorial",
    context: "Button to start the onboarding tutorial from the empty state.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard.empty_analytics.title": {
    text: "Analytics will appear here",
    context: "Analytics empty state title when there are 0 patients.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },
  "dashboard.empty_analytics.description": {
    text: "Create patients and upload clinical documents to see cohort demographics, staging distribution, mutation prevalence, treatment analytics, and more.",
    context: "Analytics empty state description.",
    category: "dashboard",
    maxLength: 200,
    needsTranslation: true,
  },
  "dashboard.empty_documents.title": {
    text: "Upload documents to unlock analytics",
    context:
      "Analytics empty state title when patients exist but no documents have been uploaded yet.",
    category: "dashboard",
    maxLength: 50,
    needsTranslation: true,
  },
  "dashboard.empty_documents.description": {
    text: "Open a patient record and upload clinical documents. Athena will automatically extract structured data and populate the charts below.",
    context:
      "Analytics empty state description when patients exist but no documents uploaded.",
    category: "dashboard",
    maxLength: 200,
    needsTranslation: true,
  },

  // ===========================================================================
  // LAYOUT — Document Modal
  // ===========================================================================
  "document_modal.title": {
    text: "Document Preview",
    context: "Dialog title for the document viewer modal.",
    category: "modals",
    maxLength: 25,
    needsTranslation: true,
  },
  "document_modal.prev": {
    text: "Prev",
    context: "Navigation button to go to the previous document in the viewer.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "document_modal.next": {
    text: "Next",
    context: "Navigation button to go to the next document in the viewer.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "document_modal.zoom_in": {
    text: "Zoom in",
    context: "Tooltip for the zoom-in button in the image viewer.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "document_modal.zoom_out": {
    text: "Zoom out",
    context: "Tooltip for the zoom-out button in the image viewer.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "document_modal.reset_view": {
    text: "Reset view",
    context: "Tooltip for the reset view button in the image viewer.",
    category: "actions",
    maxLength: 12,
    needsTranslation: true,
  },
  "document_modal.preview_not_available": {
    text: "Preview not available for .{ext}",
    context:
      "Message when the document format cannot be previewed inline. {ext} is the file extension.",
    category: "modals",
    maxLength: 50,
    placeholders: ["ext"],
    needsTranslation: true,
  },
  "document_modal.loading": {
    text: "Loading...",
    context: "Loading state while fetching a text file for preview.",
    category: "common",
    maxLength: 15,
    needsTranslation: true,
  },
  "document_modal.failed_to_load": {
    text: "Failed to load text file",
    context: "Error state when a text file cannot be fetched for preview.",
    category: "errors",
    maxLength: 30,
    needsTranslation: true,
  },

  // ===========================================================================
  // LAYOUT — Notification Bell
  // ===========================================================================
  "notification_bell.just_now": {
    text: "just now",
    context:
      "Relative time label for events that occurred less than one minute ago.",
    category: "notifications",
    maxLength: 10,
    needsTranslation: true,
  },
  "notification_bell.mins_ago": {
    text: "{mins}m ago",
    context:
      "Relative time label showing minutes since event. {mins} is the number of minutes.",
    category: "notifications",
    maxLength: 10,
    placeholders: ["mins"],
    needsTranslation: true,
  },
  "notification_bell.hours_ago": {
    text: "{hours}h ago",
    context:
      "Relative time label showing hours since event. {hours} is the number of hours.",
    category: "notifications",
    maxLength: 10,
    placeholders: ["hours"],
    needsTranslation: true,
  },
  "notification_bell.days_ago": {
    text: "{days}d ago",
    context:
      "Relative time label showing days since event. {days} is the number of days.",
    category: "notifications",
    maxLength: 10,
    placeholders: ["days"],
    needsTranslation: true,
  },
  "notification_bell.invitation_accepted": {
    text: "Invitation accepted! You now have access to the environment.",
    context: "Success snackbar after accepting an environment invitation.",
    category: "notifications",
    maxLength: 80,
    needsTranslation: true,
  },
  "notification_bell.failed_accept": {
    text: "Failed to accept invitation",
    context: "Fallback error message when accepting an invitation fails.",
    category: "errors",
    maxLength: 40,
    needsTranslation: true,
  },
  "notification_bell.invitation_declined": {
    text: "Invitation declined.",
    context: "Info snackbar after declining an environment invitation.",
    category: "notifications",
    maxLength: 30,
    needsTranslation: true,
  },
  "notification_bell.failed_decline": {
    text: "Failed to decline invitation",
    context: "Fallback error message when declining an invitation fails.",
    category: "errors",
    maxLength: 40,
    needsTranslation: true,
  },
  "notification_bell.accept": {
    text: "Accept",
    context: "Button to accept an environment invitation.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "notification_bell.decline": {
    text: "Decline",
    context: "Button to decline an environment invitation.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },
  "notification_bell.accepted": {
    text: "Accepted",
    context: "Status label for an accepted environment invitation.",
    category: "notifications",
    maxLength: 12,
    needsTranslation: true,
  },
  "notification_bell.declined": {
    text: "Declined",
    context: "Status label for a declined environment invitation.",
    category: "notifications",
    maxLength: 12,
    needsTranslation: true,
  },
  "notification_bell.patient_id": {
    text: "(ID: {id})",
    context:
      "Patient ID shown next to the patient name in a notification row. {id} is the patient ID number.",
    category: "notifications",
    maxLength: 15,
    placeholders: ["id"],
    needsTranslation: true,
  },

  // ===========================================================================
  // LAYOUT — Camera Modal
  // ===========================================================================
  "camera_modal.title": {
    text: "Camera",
    context: "Dialog title for the camera capture modal.",
    category: "modals",
    maxLength: 10,
    needsTranslation: true,
  },
  "camera_modal.capture": {
    text: "Capture",
    context: "Button to capture a photo from the camera.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },

  // ===========================================================================
  // LAYOUT — Notes / Anamnesis Modal
  // ===========================================================================
  "notes_modal.title": {
    text: "Notes / Anamnesis",
    context: "Dialog title for the clinical notes editor.",
    category: "modals",
    maxLength: 25,
    translatorNotes:
      "Anamnesis is a medical term for patient history. Keep both terms.",
    needsTranslation: true,
  },
  "notes_modal.recording_stop": {
    text: "Recording. Click to stop.",
    context: "Tooltip while voice recording is active in the notes editor.",
    category: "chat",
    maxLength: 30,
    needsTranslation: true,
  },
  "notes_modal.record_transcribe": {
    text: "Record voice note and transcribe",
    context: "Tooltip for starting a voice recording in the notes editor.",
    category: "chat",
    maxLength: 40,
    needsTranslation: true,
  },
  "notes_modal.save": {
    text: "Save Notes",
    context: "Button to save the clinical notes content.",
    category: "actions",
    maxLength: 15,
    needsTranslation: true,
  },

  // ===========================================================================
  // LAYOUT — GlassMorphism Table
  // ===========================================================================
  "glass_table.variable": {
    text: "Variable",
    context: "Column header in transposed table view showing the field name.",
    category: "tables",
    maxLength: 15,
    needsTranslation: true,
  },
  "glass_table.value": {
    text: "Value",
    context:
      "Column header in transposed table view showing the field value (single row).",
    category: "tables",
    maxLength: 10,
    needsTranslation: true,
  },
  "glass_table.value_numbered": {
    text: "Value ({index})",
    context:
      "Column header in transposed table view when multiple value columns exist. {index} is the 1-based column number.",
    category: "tables",
    maxLength: 15,
    placeholders: ["index"],
    needsTranslation: true,
  },
  "glass_table.empty_fields_hint": {
    text: "* Hover to see fields that are currently empty",
    context:
      "Footnote under the clinical table explaining that empty fields are visible on hover.",
    category: "tables",
    maxLength: 60,
    needsTranslation: true,
  },

  // ===========================================================================
  // LAYOUT — Document Attach Modal
  // ===========================================================================
  "attach_modal.title": {
    text: "Attach Document",
    context: "Dialog title for the document upload modal.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },
  "attach_modal.drag_drop": {
    text: "Drag & drop files here, or",
    context: "Instruction text in the file drop zone.",
    category: "forms",
    maxLength: 30,
    needsTranslation: true,
  },
  "attach_modal.choose_files": {
    text: "Choose File(s)",
    context: "Button to open the file picker in the attach modal.",
    category: "actions",
    maxLength: 15,
    needsTranslation: true,
  },

  // ===========================================================================
  // PATIENT DETAILS
  // ===========================================================================
  "patient_details.loading": {
    text: "Loading patient data...",
    context: "Shown while patient details are being fetched from the server.",
    category: "patients",
    maxLength: 30,
    needsTranslation: true,
  },
  "patient_details.report_title": {
    text: "Clinical History Report",
    context: "Dialog title for the generated clinical history report.",
    category: "patients",
    maxLength: 30,
    needsTranslation: true,
  },
  "patient_details.generating_report": {
    text: "Generating report...",
    context: "Loading state while the AI generates a clinical history report.",
    category: "patients",
    maxLength: 25,
    needsTranslation: true,
  },
  "patient_details.no_report": {
    text: "No report available.",
    context: "Empty state when no clinical history report exists.",
    category: "patients",
    maxLength: 25,
    needsTranslation: true,
  },
  "patient_details.copy_clipboard": {
    text: "Copy to Clipboard",
    context: "Button to copy the generated clinical report text to clipboard.",
    category: "actions",
    maxLength: 20,
    needsTranslation: true,
  },
  "patient_details.report_cached": {
    text: "Report loaded from cache (no data changes detected)",
    context:
      "Info notification when a previously generated report is served from cache.",
    category: "notifications",
    maxLength: 60,
    needsTranslation: true,
  },
  "patient_details.report_generated": {
    text: "Report generated successfully",
    context:
      "Success notification when a new clinical history report is generated.",
    category: "notifications",
    maxLength: 40,
    needsTranslation: true,
  },
  "patient_details.report_failed": {
    text: "Report generation failed",
    context: "Error notification when report generation fails.",
    category: "errors",
    maxLength: 30,
    needsTranslation: true,
  },
  "patient_details.report_copied": {
    text: "Report copied to clipboard",
    context: "Success notification after copying the report to clipboard.",
    category: "notifications",
    maxLength: 30,
    needsTranslation: true,
  },
  "patient_details.unknown_patient": {
    text: "Unknown Patient",
    context: "Fallback name when patient name is not available.",
    category: "patients",
    maxLength: 20,
    needsTranslation: true,
  },

  // ===========================================================================
  // PATIENT DETAILS — Layout
  // ===========================================================================
  "patient_layout.expand_chat": {
    text: "Expand chat",
    context:
      "Tooltip for the button to expand the chat panel in patient details.",
    category: "patients",
    maxLength: 15,
    needsTranslation: true,
  },
  "patient_layout.collapse_chat": {
    text: "Collapse chat",
    context: "Tooltip for the button to collapse the chat panel.",
    category: "patients",
    maxLength: 15,
    needsTranslation: true,
  },
  "patient_layout.expand_other_first": {
    text: "Expand other panel first",
    context:
      "Tooltip shown when a panel cannot be collapsed because the other is already collapsed.",
    category: "patients",
    maxLength: 30,
    needsTranslation: true,
  },
  "patient_layout.chat": {
    text: "Chat",
    context: "Label for the collapsed chat panel sidebar.",
    category: "chat",
    maxLength: 10,
    needsTranslation: true,
  },
  "patient_layout.id_label": {
    text: "ID: {id}",
    context:
      "Patient ID label in the mobile header. {id} is the patient ID number.",
    category: "patients",
    maxLength: 15,
    placeholders: ["id"],
    needsTranslation: true,
  },

  // ===========================================================================
  // PATIENT DETAILS — Data Panel (section headers)
  // ===========================================================================
  "data_panel.diagnosis": {
    text: "Baseline",
    context:
      "Subsection header for cancer-specific static tables (diagnosis snapshot + baseline characteristics) in patient details. Previously labeled 'Diagnosis & Baseline'.",
    category: "clinical",
    maxLength: 25,
    needsTranslation: true,
  },
  "data_panel.events": {
    text: "History",
    context:
      "Subsection header for cancer-specific event tables (procedures, treatments, follow-up) in patient details. Full form: 'History (Procedures, Treatments & Follow-up)'. Previously labeled 'Patient Journey' / 'Events'.",
    category: "clinical",
    maxLength: 25,
    needsTranslation: true,
  },
  "data_panel.events_tooltip": {
    text: "History (Procedures, Treatments & Follow-up)",
    context:
      "Full descriptive tooltip for the Patient Journey section. Appears on hover to explain what the section contains.",
    category: "clinical",
    maxLength: 60,
    needsTranslation: true,
  },
  "data_panel.events_subtitle": {
    text: "Procedures, Treatments & Follow-up",
    context:
      "Short clarifying subtitle shown inline next to 'Patient Journey' in the DataPanel header.",
    category: "clinical",
    maxLength: 40,
    needsTranslation: true,
  },
  "data_panel.extracting_in_progress": {
    text: "Extracting data: tables will update automatically...",
    context: "Banner shown in data panel while AI extraction is in progress.",
    category: "clinical",
    maxLength: 80,
    needsTranslation: true,
  },

  // ===========================================================================
  // PATIENT DETAILS — Share Patient Modal
  // ===========================================================================
  "share_patient.title": {
    text: "Share Patient",
    context: "Dialog title for the share patient modal.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },
  "share_patient.description_named": {
    text: 'Share "{name}" with another user. They will receive a notification.',
    context:
      "Description in share patient modal when patient name is known. {name} is the patient name.",
    category: "modals",
    maxLength: 80,
    placeholders: ["name"],
    needsTranslation: true,
  },
  "share_patient.description_generic": {
    text: "Share this patient with another user. They will receive a notification.",
    context: "Description in share patient modal when patient name is unknown.",
    category: "modals",
    maxLength: 80,
    needsTranslation: true,
  },
  "share_patient.search_label": {
    text: "Search user by email or name",
    context: "Label for the user search autocomplete field in the share modal.",
    category: "forms",
    maxLength: 35,
    needsTranslation: true,
  },
  "share_patient.search_placeholder": {
    text: "email@example.com",
    context: "Placeholder for the user search field in the share modal.",
    category: "forms",
    maxLength: 25,
    needsTranslation: true,
  },
  "share_patient.search_hint": {
    text: "Type to search users...",
    context:
      "No-options text when query is too short in the share user search.",
    category: "forms",
    maxLength: 25,
    needsTranslation: true,
  },
  "share_patient.no_users_found": {
    text: "No users found",
    context: "No-options text when user search returns no matches.",
    category: "forms",
    maxLength: 20,
    needsTranslation: true,
  },
  "share_patient.sharing": {
    text: "Sharing...",
    context: "Button text while the share request is in flight.",
    category: "actions",
    maxLength: 15,
    needsTranslation: true,
  },
  "share_patient.success": {
    text: "Patient shared with {email}",
    context:
      "Success notification after sharing a patient. {email} is the recipient's email.",
    category: "notifications",
    maxLength: 50,
    placeholders: ["email"],
    needsTranslation: true,
  },
  "share_patient.failed": {
    text: "Failed to share patient",
    context: "Error notification when sharing a patient fails.",
    category: "errors",
    maxLength: 30,
    needsTranslation: true,
  },

  // ===========================================================================
  // PATIENT DETAILS — Action Panel
  // ===========================================================================
  "action_panel.link_copied": {
    text: "Patient link copied to clipboard",
    context: "Success notification after copying the patient URL.",
    category: "notifications",
    maxLength: 40,
    needsTranslation: true,
  },
  "action_panel.patient_deleted": {
    text: "Patient deleted successfully",
    context: "Success notification after deleting a patient.",
    category: "notifications",
    maxLength: 35,
    needsTranslation: true,
  },
  "action_panel.delete_failed": {
    text: "Failed to delete patient",
    context: "Error notification when patient deletion fails.",
    category: "errors",
    maxLength: 30,
    needsTranslation: true,
  },
  "action_panel.export_completed": {
    text: "Export completed",
    context: "Success notification after data export finishes.",
    category: "notifications",
    maxLength: 20,
    needsTranslation: true,
  },
  "action_panel.export_failed": {
    text: "Export failed",
    context: "Error notification when data export fails.",
    category: "errors",
    maxLength: 15,
    needsTranslation: true,
  },
  "action_panel.generate_report": {
    text: "Generate Report",
    context:
      "Tooltip for the generate clinical report button in the action panel.",
    category: "actions",
    maxLength: 20,
    needsTranslation: true,
  },
  "action_panel.export_data": {
    text: "Export Data",
    context: "Tooltip for the export data button in the action panel.",
    category: "actions",
    maxLength: 15,
    needsTranslation: true,
  },
  "action_panel.delete_title": {
    text: "Delete Patient",
    context: "Title for the delete patient confirmation dialog.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },
  "action_panel.delete_confirm": {
    text: "Are you sure you want to delete",
    context:
      "First part of the delete confirmation message, followed by the patient name.",
    category: "modals",
    maxLength: 40,
    needsTranslation: true,
  },
  "action_panel.delete_warning": {
    text: "This action cannot be undone. All associated documents, tables, and data will be permanently removed.",
    context: "Warning text in the delete patient confirmation dialog.",
    category: "modals",
    maxLength: 120,
    needsTranslation: true,
  },
  "action_panel.deleting": {
    text: "Deleting...",
    context: "Button text while a patient deletion is in progress.",
    category: "actions",
    maxLength: 15,
    needsTranslation: true,
  },

  // ===========================================================================
  // CHAT — Messages & History
  // ===========================================================================
  "chat.doctor_label": {
    text: "Dr",
    context: "Chip label identifying the doctor/clinician in the chat.",
    category: "chat",
    maxLength: 5,
    needsTranslation: true,
  },
  "chat.patient_label": {
    text: "Pt",
    context: "Chip label identifying the patient (AI response) in the chat.",
    category: "chat",
    maxLength: 5,
    translatorNotes: "Short abbreviation for 'Patient'. Keep abbreviated.",
    needsTranslation: true,
  },
  "chat.document_fallback": {
    text: "Document",
    context:
      "Fallback label for a document attachment when the file name is unknown.",
    category: "chat",
    maxLength: 12,
    needsTranslation: true,
  },
  "chat.photo_alt": {
    text: "Photo",
    context: "Alt text for an uploaded photo in the chat.",
    category: "chat",
    maxLength: 10,
    needsTranslation: true,
  },
  "chat.voice_note": {
    text: "Voice Note",
    context: "Label for a voice recording message in the chat.",
    category: "chat",
    maxLength: 15,
    needsTranslation: true,
  },
  "chat.no_messages": {
    text: "No messages yet",
    context: "Empty state text when the patient chat has no messages.",
    category: "chat",
    maxLength: 20,
    needsTranslation: true,
  },
  "chat.start_typing_hint": {
    text: "Start typing, upload files, or record a voice note",
    context:
      "Helper text below the empty chat state encouraging user interaction.",
    category: "chat",
    maxLength: 60,
    needsTranslation: true,
  },
  "chat.recording": {
    text: "Recording...",
    context: "Status text shown during active voice recording in the chat.",
    category: "chat",
    maxLength: 15,
    needsTranslation: true,
  },
  "chat.untitled_document": {
    text: "Untitled Document",
    context: "Fallback name for a document without a file name.",
    category: "chat",
    maxLength: 20,
    needsTranslation: true,
  },

  // ===========================================================================
  // CHAT — Report Modal
  // ===========================================================================
  "report_modal.title": {
    text: "Evidence Report",
    context: "Dialog title for the evidence report viewer.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },
  "report_modal.document_title": {
    text: "Document Title",
    context: "Section header for the document title in the report.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },
  "report_modal.chat_summary": {
    text: "Chat Summary",
    context: "Section header for the chat summary in the report.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },
  "report_modal.validation_results": {
    text: "Validation Results:",
    context: "Section header for validation results in the report.",
    category: "modals",
    maxLength: 25,
    needsTranslation: true,
  },
  "report_modal.close": {
    text: "Close Report",
    context: "Button to close the evidence report modal.",
    category: "actions",
    maxLength: 15,
    needsTranslation: true,
  },

  // ===========================================================================
  // CHAT — Document Preview Modal
  // ===========================================================================
  "doc_preview.checking": {
    text: "Checking document...",
    context: "Loading state while verifying the document is accessible.",
    category: "chat",
    maxLength: 25,
    needsTranslation: true,
  },
  "doc_preview.processing": {
    text: "The document is being processed. Please try again later.",
    context: "Message when the document is not yet ready for preview.",
    category: "chat",
    maxLength: 60,
    needsTranslation: true,
  },
  "doc_preview.not_available": {
    text: "Preview not available for {ext}",
    context:
      "Message when the document format cannot be previewed. {ext} is the file extension.",
    category: "chat",
    maxLength: 50,
    placeholders: ["ext"],
    needsTranslation: true,
  },
  "doc_preview.title": {
    text: "Document Preview",
    context: "Dialog title for the document preview modal in the chat.",
    category: "modals",
    maxLength: 20,
    needsTranslation: true,
  },

  // ===========================================================================
  // DASHBOARD — AddPatientForm (bulk upload messages)
  // ===========================================================================
  "notification_bell.patients_failed": {
    text: "patient(s) failed",
    context:
      "Notification bell detail text for bulk extraction failures. Shows count of failed patients next to action buttons.",
    category: "notifications",
    maxLength: 30,
    needsTranslation: true,
  },
  "notification_bell.retry_failed": {
    text: "Retry",
    context:
      "Button label in the notification dropdown to retry failed patients from a bulk upload.",
    category: "notifications",
    maxLength: 10,
    needsTranslation: true,
  },
  "notification_bell.download_errors": {
    text: "Download Errors",
    context:
      "Button label in the notification dropdown to download a JSON failure report for a bulk upload.",
    category: "notifications",
    maxLength: 20,
    needsTranslation: true,
  },
  "notification_bell.bulk_retry_started": {
    text: "Retrying failed patients...",
    context:
      "Info snackbar message shown when user clicks Retry in a bulk upload failure notification.",
    category: "notifications",
    maxLength: 40,
    needsTranslation: true,
  },
  "notification_bell.bulk_retry_failed": {
    text: "Failed to retry",
    context: "Error snackbar message shown when the bulk retry request fails.",
    category: "notifications",
    maxLength: 25,
    needsTranslation: true,
  },
  "notification_bell.no_report": {
    text: "No failure report available",
    context:
      "Warning snackbar when attempting to download a failure report but none exists.",
    category: "notifications",
    maxLength: 35,
    needsTranslation: true,
  },
  "notification_bell.download_failed": {
    text: "Failed to download report",
    context: "Error snackbar when the failure report download request fails.",
    category: "notifications",
    maxLength: 35,
    needsTranslation: true,
  },

  // ===========================================================================
  // DASHBOARD — Tabular Wizard
  // ===========================================================================
  "csv.step_upload": {
    text: "Upload",
    context: "Step label for the file upload step in the CSV/Excel wizard.",
    category: "bulk_upload",
    maxLength: 10,
    needsTranslation: true,
  },
  "csv.step_mode": {
    text: "Mode",
    context: "Step label for the mode selection step in the CSV/Excel wizard.",
    category: "bulk_upload",
    maxLength: 10,
    needsTranslation: true,
  },
  "csv.step_grouping": {
    text: "Grouping",
    context: "Step label for the row grouping step in the CSV/Excel wizard.",
    category: "bulk_upload",
    maxLength: 12,
    needsTranslation: true,
  },
  "csv.step_column_definitions": {
    text: "Column Definitions",
    context:
      "Step label for the schema definition step in the CSV/Excel wizard.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.step_patient_resolution": {
    text: "Patient Resolution",
    context:
      "Step label for the patient matching step in the CSV/Excel wizard.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.step_confirm": {
    text: "Confirm & Start",
    context:
      "Step label for the final confirmation step in the CSV/Excel wizard.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.parse_failed": {
    text: "Failed to parse file",
    context: "Fallback error when CSV/Excel file parsing fails on the server.",
    category: "errors",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.schema_inference_failed": {
    text: "Schema inference failed",
    context: "Fallback error when AI schema inference fails.",
    category: "errors",
    maxLength: 30,
    needsTranslation: true,
  },
  "csv.applied_template": {
    text: 'Applied template "{name}"',
    context:
      "Success snackbar when a saved schema template is applied. {name} is the template name.",
    category: "notifications",
    maxLength: 40,
    placeholders: ["name"],
    needsTranslation: true,
  },
  "csv.match_failed": {
    text: "Patient matching failed",
    context: "Fallback error when patient matching fails in the CSV wizard.",
    category: "errors",
    maxLength: 30,
    needsTranslation: true,
  },
  "csv.processed_success": {
    text: "Successfully processed {count} patients from spreadsheet!",
    context:
      "Success snackbar after spreadsheet import completes. {count} is the patient count.",
    category: "notifications",
    maxLength: 60,
    placeholders: ["count"],
    needsTranslation: true,
  },
  "csv.processed_partial": {
    text: "Partially complete: {processed}/{total} patients.",
    context: "Warning snackbar when spreadsheet import partially fails.",
    category: "notifications",
    maxLength: 50,
    placeholders: ["processed", "total"],
    needsTranslation: true,
  },
  "csv.processing_failed_msg": {
    text: "Processing failed: {error}",
    context:
      "Error snackbar when spreadsheet import fails. {error} is the error message.",
    category: "errors",
    maxLength: 80,
    placeholders: ["error"],
    needsTranslation: true,
  },
  "csv.failed_start_processing": {
    text: "Failed to start processing",
    context:
      "Fallback error when the spreadsheet processing fails to initiate.",
    category: "errors",
    maxLength: 30,
    needsTranslation: true,
  },
  "csv.status_processing": {
    text: "Processing... {progress}%",
    context:
      "Status text during spreadsheet processing. {progress} is the completion percentage.",
    category: "bulk_upload",
    maxLength: 25,
    placeholders: ["progress"],
    needsTranslation: true,
  },
  "csv.status_completed": {
    text: "Completed!",
    context: "Status text when spreadsheet processing is finished.",
    category: "bulk_upload",
    maxLength: 15,
    needsTranslation: true,
  },
  "csv.status_partial": {
    text: "Partially completed",
    context: "Status text when spreadsheet processing partially completed.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.status_failed": {
    text: "Failed",
    context: "Status text when spreadsheet processing failed.",
    category: "bulk_upload",
    maxLength: 10,
    needsTranslation: true,
  },
  "csv.status_queued": {
    text: "Queued, waiting for active slot...",
    context: "Status text when spreadsheet processing is queued.",
    category: "bulk_upload",
    maxLength: 40,
    needsTranslation: true,
  },
  "csv.no_description": {
    text: "(no description)",
    context: "Placeholder shown when a column has no description defined.",
    category: "bulk_upload",
    maxLength: 20,
    needsTranslation: true,
  },
  "csv.internal_id": {
    text: "Internal Platform ID",
    context: "Dropdown option for matching by Athena's internal patient ID.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.external_id": {
    text: "External ID (MRN)",
    context: "Dropdown option for matching by external Medical Record Number.",
    category: "bulk_upload",
    maxLength: 25,
    translatorNotes: "MRN = Medical Record Number. Keep abbreviated form.",
    needsTranslation: true,
  },
  "csv.patient_name_option": {
    text: "Patient Name",
    context: "Dropdown option for matching patients by their name.",
    category: "bulk_upload",
    maxLength: 15,
    needsTranslation: true,
  },
  "csv.summary_create": {
    text: "CREATE new patients",
    context: "Summary label showing the selected mode is CREATE.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.summary_import": {
    text: "IMPORT into existing",
    context: "Summary label showing the selected mode is IMPORT.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.grouping_by_col": {
    text: 'By "{column}"',
    context:
      "Summary label showing grouping by a specific column. {column} is the column name.",
    category: "bulk_upload",
    maxLength: 30,
    placeholders: ["column"],
    needsTranslation: true,
  },
  "csv.each_row_patient": {
    text: "Each row = 1 patient",
    context: "Summary label showing each row represents one patient.",
    category: "bulk_upload",
    maxLength: 25,
    needsTranslation: true,
  },
  "csv.template_placeholder": {
    text: "e.g., Breast cancer cohort export",
    context: "Placeholder for the saved template name input.",
    category: "bulk_upload",
    maxLength: 40,
    needsTranslation: true,
  },
  "csv.format_issue_title": {
    text: "Spreadsheet format issue detected",
    context:
      "Title for the warning alert when the uploaded Excel file has structural issues (merged cells or multi-row headers).",
    category: "bulk_upload",
    maxLength: 45,
    needsTranslation: true,
  },
  "csv.format_issue_merged": {
    text: "This file contains merged cells. Please unmerge all cells in your spreadsheet before uploading.",
    context:
      "Warning message shown when the uploaded Excel file contains merged cell regions.",
    category: "bulk_upload",
    maxLength: 120,
    needsTranslation: true,
  },
  "csv.format_issue_multirow": {
    text: "This file appears to have multiple header rows. Please restructure so column headers are on a single row.",
    context:
      "Warning message shown when the uploaded Excel file has a multi-row header (first row is mostly empty, real headers on row 2+).",
    category: "bulk_upload",
    maxLength: 120,
    needsTranslation: true,
  },
  "csv.format_good_label": {
    text: "Correct format: single header row, no merged cells:",
    context:
      "Label above the ASCII art showing the correct spreadsheet format.",
    category: "bulk_upload",
    maxLength: 60,
    needsTranslation: true,
  },
  "csv.format_bad_merged_label": {
    text: "Incorrect: merged category cells above headers:",
    context:
      "Label above the ASCII art showing an incorrect format with merged cells.",
    category: "bulk_upload",
    maxLength: 60,
    needsTranslation: true,
  },
  "csv.format_bad_multi_label": {
    text: "Incorrect: extra row above the real headers:",
    context:
      "Label above the ASCII art showing an incorrect format with an extra category row.",
    category: "bulk_upload",
    maxLength: 60,
    needsTranslation: true,
  },
  "csv.format_fix_hint": {
    text: "Tip: Open the file in Excel, unmerge any merged cells, ensure headers are on a single row, then re-upload.",
    context:
      "Helpful tip at the bottom of the format guidance alert telling the user how to fix the issue.",
    category: "bulk_upload",
    maxLength: 130,
    needsTranslation: true,
  },

  // ===========================================================================
  // UPLOAD — Clinical Image Disclaimer
  // ===========================================================================
  "upload.clinical_image_disclaimer_title": {
    text: "Clinical Image Detected",
    context:
      "Title of the legal disclaimer alert shown when a clinical image or WSI file is selected for upload.",
    category: "bulk_upload",
    maxLength: 40,
    needsTranslation: true,
  },
  "upload.clinical_image_disclaimer": {
    text: "This file appears to be a clinical image (photograph, slide scan, or whole slide image). Athena will securely store this file for you to review in the future, but will not perform any automated diagnosis, labelling, or clinical interpretation. Athena is not a certified medical device and must not be used for diagnostic purposes.",
    context:
      "Legal disclaimer body text when a clinical/WSI image is uploaded.",
    category: "legal",
    maxLength: 400,
    needsTranslation: true,
  },
  "upload.clinical_image_disclaimer_note": {
    text: "Regular images containing text (e.g. scanned documents) will be processed normally through our text extraction pipeline.",
    context:
      "Smaller note below the clinical image disclaimer explaining that text-based images are still processed.",
    category: "bulk_upload",
    maxLength: 150,
    needsTranslation: true,
  },

  // ===========================================================================
  // DASHBOARD — Chat (NL Queries)
  // ===========================================================================
  "dashboard_chat.query_history": {
    text: "Query history",
    context: "Tooltip for the button that opens query history.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard_chat.recent_queries": {
    text: "Recent Queries",
    context: "Header text for the query history panel.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard_chat.no_queries": {
    text: "No queries yet.",
    context: "Empty state when no NL queries have been made.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "dashboard_chat.results_limited": {
    text: "Results are limited to {max} rows for performance.",
    context:
      "Notice when query results are truncated. {max} is the maximum row count.",
    category: "dashboard",
    maxLength: 60,
    placeholders: ["max"],
    needsTranslation: true,
  },
  "dashboard_chat.export_hint": {
    text: "Use the Export button below to download the full dataset.",
    context:
      "Additional hint shown when results are truncated and export is available.",
    category: "dashboard",
    maxLength: 60,
    needsTranslation: true,
  },
  "dashboard_chat.download_csv": {
    text: "Download result (CSV)",
    context:
      "Always-visible button that downloads whatever is currently shown in the NL query result panel (columns + rows) as a CSV file. Client-side serialization, no backend round-trip. Works for aggregates (distributions, trends, counts), not just patient lists.",
    category: "dashboard",
    maxLength: 30,
    needsTranslation: true,
  },
  "dashboard_chat.disambiguation.title": {
    text: "This question can be read a few ways",
    context:
      "Heading above the disambiguation panel, shown when the NL pipeline could not confidently pick a single interpretation of the question and offers the user 2-4 readings to choose from.",
    category: "dashboard",
    maxLength: 60,
    needsTranslation: true,
  },
  "dashboard_chat.disambiguation.subtitle": {
    text: "Pick the reading you meant, or refine your question below.",
    context:
      "Subtitle under the disambiguation heading instructing the user to choose one of the interpretation options or type a clarification.",
    category: "dashboard",
    maxLength: 90,
    needsTranslation: true,
  },
  "dashboard_chat.disambiguation.suggested": {
    text: "Suggested",
    context:
      "Small badge on the interpretation option the system ranked as the most complete / most likely default.",
    category: "dashboard",
    maxLength: 16,
    needsTranslation: true,
  },
  "dashboard_chat.disambiguation.use_this": {
    text: "Use this interpretation",
    context:
      "Button inside an interpretation option that selects that reading; the query re-runs and returns that interpretation's result.",
    category: "dashboard",
    maxLength: 30,
    needsTranslation: true,
  },
  "dashboard_chat.disambiguation.rows": {
    text: "{count} rows",
    context:
      "Row-count caption shown for each interpretation option so the user can compare how much each reading returns. {count} is the number of result rows.",
    category: "dashboard",
    maxLength: 20,
    placeholders: ["count"],
    needsTranslation: true,
  },
  "dashboard_chat.disambiguation.refine": {
    text: "Refine",
    context:
      "Button next to the clarification text field; submits the user's clarification and re-runs the query guided by it.",
    category: "dashboard",
    maxLength: 16,
    needsTranslation: true,
  },
  "dashboard_chat.disambiguation.refine_placeholder": {
    text: "None of these? Add a clarification...",
    context:
      "Placeholder text in the clarification field below the interpretation options, where the user can type a refinement instead of picking an option.",
    category: "dashboard",
    maxLength: 50,
    needsTranslation: true,
  },
  "dashboard_chat.disambiguation.completeness_high": {
    text: "Complete",
    context:
      "Chip on an interpretation option indicating the query captures all the data the question implies (completeness = high).",
    category: "dashboard",
    maxLength: 16,
    needsTranslation: true,
  },
  "dashboard_chat.disambiguation.completeness_medium": {
    text: "Partial",
    context:
      "Chip on an interpretation option indicating the query captures most but possibly not all relevant data (completeness = medium).",
    category: "dashboard",
    maxLength: 16,
    needsTranslation: true,
  },
  "dashboard_chat.disambiguation.completeness_low": {
    text: "Narrow",
    context:
      "Chip on an interpretation option indicating the query may miss or under-count relevant data (completeness = low).",
    category: "dashboard",
    maxLength: 16,
    needsTranslation: true,
  },
  "dashboard_chat.step.understanding": {
    text: "Understanding your question",
    context:
      "Progress 'thought' shown while the NL query pipeline classifies the user's intent. Curated step label streamed during the wait so the screen is not blank.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },
  "dashboard_chat.step.selecting": {
    text: "Selecting the relevant data",
    context:
      "Progress 'thought' shown while the pipeline picks which clinical tables are relevant to the question.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },
  "dashboard_chat.step.drafting": {
    text: "Drafting queries",
    context:
      "Progress 'thought' shown while the pipeline drafts candidate database queries for the question.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },
  "dashboard_chat.step.checking": {
    text: "Cross-checking results",
    context:
      "Progress 'thought' shown while the pipeline runs the candidate queries and cross-checks their results for agreement.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },
  "dashboard_chat.step.ranking": {
    text: "Ranking interpretations",
    context:
      "Progress 'thought' shown when the question was ambiguous and the pipeline is ranking the possible interpretations.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },
  "dashboard_chat.step.formatting": {
    text: "Preparing your answer",
    context:
      "Progress 'thought' shown while the pipeline formats the final answer and its chart for display.",
    category: "dashboard",
    maxLength: 40,
    needsTranslation: true,
  },
  "dashboard_chat.export_patients_full": {
    text: "Export {count} patient{plural} (full data)",
    context:
      "Button that triggers the full-cohort export dialog scoped to the patient IDs in the current result. {count} is the number of patients, {plural} is 's' or empty. Only shown when the result rows contain patient IDs.",
    category: "dashboard",
    maxLength: 50,
    placeholders: ["count", "plural"],
    needsTranslation: true,
  },
  "dashboard_chat.export_patients": {
    text: "Export {count} patient(s) (full data)",
    context:
      "Button to export matched patients from a NL query. {count} is the patient count.",
    category: "dashboard",
    maxLength: 40,
    placeholders: ["count"],
    needsTranslation: true,
  },
  "dashboard_chat.patients_sidebar": {
    text: "Patients ({count})",
    context:
      "Sidebar header showing the number of matched patients. {count} is the count.",
    category: "dashboard",
    maxLength: 20,
    placeholders: ["count"],
    needsTranslation: true,
  },
  "dashboard_chat.showing_rows": {
    text: "Showing {displayed} of {total} rows",
    context:
      "Pagination indicator for large result sets. {displayed} and {total} are row counts.",
    category: "dashboard",
    maxLength: 35,
    placeholders: ["displayed", "total"],
    needsTranslation: true,
  },

  // ===========================================================================
  // DASHBOARD — Misc
  // ===========================================================================
  "dashboard.unnamed_patient": {
    text: "Unnamed Patient",
    context: "Fallback name for patients without a name in the dashboard list.",
    category: "patients",
    maxLength: 20,
    needsTranslation: true,
  },

  // ===========================================================================
  // COMPANY CREATION
  // ===========================================================================
  "company.choose_environments": {
    text: "Choose one or more holding companies (environments).",
    context:
      "Caption text in the company creation form for environment selection.",
    category: "forms",
    maxLength: 60,
    needsTranslation: true,
  },

  // ===========================================================================
  // AUTH — Login & Social Login
  // ===========================================================================
  "auth.sign_in": {
    text: "Sign In",
    context: "Heading on the login page.",
    category: "auth",
    maxLength: 10,
    needsTranslation: true,
  },
  "auth.sign_in_description": {
    text: "Secure authentication via your trusted identity provider",
    context: "Subtitle on the login page below the Sign In heading.",
    category: "auth",
    maxLength: 65,
    needsTranslation: true,
  },
  "auth.enterprise_info": {
    text: "You can use your company or institution email (e.g. Microsoft 365) to sign in. If access is blocked, contact your IT department to approve Athena Tech.",
    context: "Informational note on the login page for enterprise SSO users.",
    category: "auth",
    maxLength: 180,
    needsTranslation: true,
  },
  "auth.sign_in_linkedin": {
    text: "Sign in with LinkedIn",
    context: "Button text for LinkedIn OAuth sign-in.",
    category: "auth",
    maxLength: 25,
    needsTranslation: true,
  },
  "auth.sign_in_microsoft": {
    text: "Sign in with Microsoft",
    context: "Button text for Microsoft OAuth sign-in.",
    category: "auth",
    maxLength: 25,
    needsTranslation: true,
  },
  "auth.completing_linkedin": {
    text: "Completing LinkedIn login…",
    context: "Loading text shown during LinkedIn OAuth callback processing.",
    category: "auth",
    maxLength: 30,
    needsTranslation: true,
  },
  "auth.completing_microsoft": {
    text: "Completing Microsoft login…",
    context: "Loading text shown during Microsoft OAuth callback processing.",
    category: "auth",
    maxLength: 30,
    needsTranslation: true,
  },

  // ===========================================================================
  // EDITABLE — Add Event Button
  // ===========================================================================
  "add_event.title": {
    text: "Add Event",
    context:
      "Button label to add a new clinical event to the patient timeline.",
    category: "clinical",
    maxLength: 12,
    needsTranslation: true,
  },
  "add_event.select_type": {
    text: "Select event type to create",
    context: "Popover title for the event type selection.",
    category: "clinical",
    maxLength: 30,
    needsTranslation: true,
  },

  // ===========================================================================
  // NAVIGATION — TopNavBar
  // ===========================================================================
  "nav.select_language": {
    text: "SELECT LANGUAGE",
    context:
      "Header label in the language selector dropdown in the top navigation bar.",
    category: "navigation",
    maxLength: 20,
    needsTranslation: true,
  },

  // ===========================================================================
  // LANDING — Contact Page
  // ===========================================================================
  "contact.get_in_touch": {
    text: "GET IN TOUCH",
    context: "Overline text above the Contact Us heading on the contact page.",
    category: "common",
    maxLength: 15,
    needsTranslation: true,
  },
  "contact.title": {
    text: "Contact Us",
    context: "Main heading on the contact page.",
    category: "common",
    maxLength: 15,
    needsTranslation: true,
  },
  "contact.description": {
    text: "We are here to help and answer any questions you might have. Whether you are interested in learning more about Athena, exploring partnership opportunities, or simply want to share your thoughts, we would love to hear from you.",
    context: "Introductory paragraph on the contact page.",
    category: "common",
    maxLength: 300,
    needsTranslation: true,
  },
  "contact.send_message_title": {
    text: "Send us a message",
    context: "Section title above the contact form.",
    category: "common",
    maxLength: 25,
    needsTranslation: true,
  },
  "contact.form_subtitle": {
    text: "Fill in the form below and we will get back to you shortly.",
    context: "Subtitle below the form section title.",
    category: "common",
    maxLength: 60,
    needsTranslation: true,
  },
  "contact.thank_you": {
    text: "Thank you for your message",
    context: "Heading shown after a contact form submission succeeds.",
    category: "common",
    maxLength: 30,
    needsTranslation: true,
  },
  "contact.inquiry_received": {
    text: "We have received your inquiry and will respond within 1-2 business days.",
    context: "Follow-up message after a successful contact form submission.",
    category: "common",
    maxLength: 80,
    needsTranslation: true,
  },
  "contact.send_another": {
    text: "Send another message",
    context: "Button to reset the form and send another message.",
    category: "actions",
    maxLength: 25,
    needsTranslation: true,
  },
  "contact.name": {
    text: "Name",
    context: "Form label for the name field on the contact page.",
    category: "forms",
    maxLength: 10,
    needsTranslation: true,
  },
  "contact.email": {
    text: "Email",
    context: "Form label for the email field on the contact page.",
    category: "forms",
    maxLength: 10,
    needsTranslation: true,
  },
  "contact.message": {
    text: "Message",
    context: "Form label for the message textarea on the contact page.",
    category: "forms",
    maxLength: 10,
    needsTranslation: true,
  },
  "contact.sending": {
    text: "Sending...",
    context: "Button text while the contact form is being submitted.",
    category: "actions",
    maxLength: 12,
    needsTranslation: true,
  },
  "contact.send_message": {
    text: "Send Message",
    context: "Submit button on the contact form.",
    category: "actions",
    maxLength: 15,
    needsTranslation: true,
  },
  "contact.our_office": {
    text: "Our office",
    context: "Section heading for the office location and contact details.",
    category: "common",
    maxLength: 15,
    needsTranslation: true,
  },
  "contact.address_label": {
    text: "Address",
    context: "Label for the office address in the contact section.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "contact.telephone_label": {
    text: "Telephone",
    context: "Label for the office telephone number.",
    category: "common",
    maxLength: 12,
    needsTranslation: true,
  },
  "contact.email_label": {
    text: "Email",
    context: "Label for the office email address.",
    category: "common",
    maxLength: 10,
    needsTranslation: true,
  },
  "contact.linkedin_label": {
    text: "LinkedIn",
    context: "Label for the LinkedIn profile link.",
    category: "common",
    maxLength: 12,
    needsTranslation: true,
  },
  "contact.network_error": {
    text: "Network error. Please check your connection and try again.",
    context:
      "Error message when the contact form fails due to a network issue.",
    category: "errors",
    maxLength: 60,
    needsTranslation: true,
  },

  // ===========================================================================
  // LAYOUT — Tier Simulation Banner
  // ===========================================================================
  "tier_sim.simulating": {
    text: "SIMULATING:",
    context: "Label prefix in the tier simulation banner.",
    category: "settings",
    maxLength: 15,
    needsTranslation: true,
  },
  "tier_sim.tier_suffix": {
    text: "tier",
    context:
      "Suffix after the tier name in the simulation banner (e.g. 'Professional tier').",
    category: "settings",
    maxLength: 10,
    needsTranslation: true,
  },
  "tier_sim.stop": {
    text: "Stop",
    context: "Button to stop the tier simulation.",
    category: "actions",
    maxLength: 10,
    needsTranslation: true,
  },

  // ===========================================================================
  // Legal Compliance
  // ===========================================================================
  "legal.modal.title": {
    text: "Legal Agreement Required",
    context:
      "Title of the non-dismissible modal shown when the user has pending legal documents to accept.",
    category: "legal",
    maxLength: 40,
    needsTranslation: true,
  },
  "legal.modal.subtitle": {
    text: "Please review and accept the following before continuing.",
    context: "Subtitle shown below the title in the legal acceptance modal.",
    category: "legal",
    maxLength: 80,
    needsTranslation: true,
  },
  "legal.modal.scroll_hint": {
    text: "Scroll to the bottom to accept",
    context:
      "Small hint shown when the user has not yet scrolled to the bottom of the legal document.",
    category: "legal",
    maxLength: 40,
    needsTranslation: true,
  },
  "legal.modal.accept_button": {
    text: "I Accept",
    context:
      "Button to confirm acceptance of the legal document. Enabled only after scrolling to the bottom.",
    category: "legal",
    maxLength: 20,
    needsTranslation: true,
  },
  "legal.modal.accepting": {
    text: "Accepting...",
    context:
      "Loading state on the accept button while the request is in flight.",
    category: "legal",
    maxLength: 20,
    needsTranslation: true,
  },
  "legal.modal.remaining": {
    text: "{current} of {total}",
    context:
      "Progress indicator showing which document the user is currently reviewing (e.g. '1 of 2').",
    category: "legal",
    maxLength: 15,
    placeholders: ["current", "total"],
    needsTranslation: true,
  },
  "legal.modal.effective_date": {
    text: "Effective: {date}",
    context: "Shows the effective date of the legal document being reviewed.",
    category: "legal",
    maxLength: 30,
    placeholders: ["date"],
    needsTranslation: true,
  },
  "legal.modal.version": {
    text: "Version {version}",
    context: "Shows the version of the legal document being reviewed.",
    category: "legal",
    maxLength: 20,
    placeholders: ["version"],
    needsTranslation: true,
  },
  // ============================================================================
  // ENVIRONMENTS PAGE
  // ============================================================================
  "environments.page_title": {
    text: "Environments",
    context: "Page title for the dedicated environments management page.",
    category: "environments",
    maxLength: 20,
    needsTranslation: true,
  },
  "environments.page_description": {
    text: "Manage your environments, invite team members, and control access to patient data.",
    context: "Subtitle description shown under the Environments page title.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.create_environment": {
    text: "Create Environment",
    context: "Button to create a new environment on the Environments page.",
    category: "environments",
    maxLength: 25,
    needsTranslation: true,
  },
  "environments.create_new": {
    text: "Create New Environment",
    context: "Dialog title for creating a new environment.",
    category: "environments",
    maxLength: 30,
    needsTranslation: true,
  },
  "environments.edit": {
    text: "Edit Environment",
    context: "Dialog title for editing an existing environment.",
    category: "environments",
    maxLength: 25,
    needsTranslation: true,
  },
  "environments.env_name_label": {
    text: "Environment Name",
    context: "Label for the environment name input field.",
    category: "environments",
    maxLength: 25,
    needsTranslation: true,
  },
  "environments.env_name_placeholder": {
    text: "e.g., Cancer Research Study 2024",
    context: "Placeholder text in the environment name input field.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.env_name_helper": {
    text: "Give your environment a descriptive name to help your team identify it.",
    context: "Helper text below the environment name input.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.save_changes": {
    text: "Save Changes",
    context: "Button to save environment edits.",
    category: "environments",
    maxLength: 20,
    needsTranslation: true,
  },
  "environments.feature_flags": {
    text: "Feature Flags",
    context:
      "Section title in the edit environment dialog for toggling features.",
    category: "environments",
    maxLength: 20,
    needsTranslation: true,
  },
  "environments.allow_ai": {
    text: "Allow AI Extraction",
    context:
      "Toggle label for enabling AI-powered data extraction in the environment.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.allow_ai_desc": {
    text: "Enable AI-powered data extraction for documents uploaded in this environment.",
    context: "Description for the AI extraction toggle.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.allow_export": {
    text: "Allow Data Export",
    context: "Toggle label for enabling data export in the environment.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.allow_export_desc": {
    text: "Allow users to export patient data from this environment.",
    context: "Description for the data export toggle.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.require_anon": {
    text: "Require Anonymization",
    context: "Toggle label for requiring patient name anonymization.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.require_anon_desc": {
    text: "Hide patient names for non-admin users in this environment.",
    context: "Description for the anonymization toggle.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.invite": {
    text: "Invite",
    context: "Button label to invite a user to an environment.",
    category: "environments",
    maxLength: 12,
    needsTranslation: true,
  },
  "environments.invite_to": {
    text: "Invite to {name}",
    context: "Dialog title when inviting a user to a specific environment.",
    category: "environments",
    placeholders: ["name"],
    needsTranslation: true,
  },
  "environments.invite_member": {
    text: "Invite Member",
    context: "Button label to open the invite dialog from the members list.",
    category: "environments",
    maxLength: 20,
    needsTranslation: true,
  },
  "environments.send_invitation": {
    text: "Send Invitation",
    context: "Button to confirm and send a user invitation.",
    category: "environments",
    maxLength: 20,
    needsTranslation: true,
  },
  "environments.invite_sent": {
    text: "Invitation sent successfully. The user will receive a notification.",
    context: "Success message after sending an environment invitation.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.invite_error": {
    text: "Failed to send invitation",
    context: "Error message when an invitation fails.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.members": {
    text: "Members",
    context: "Button label to view the members of an environment.",
    category: "environments",
    maxLength: 12,
    needsTranslation: true,
  },
  "environments.members_of": {
    text: "Members of {name}",
    context: "Dialog title showing members of a specific environment.",
    category: "environments",
    placeholders: ["name"],
    needsTranslation: true,
  },
  "environments.members_plural": {
    text: "members",
    context: "Plural form of 'member' in the members count chip.",
    category: "environments",
    maxLength: 12,
    needsTranslation: true,
  },
  "environments.member_singular": {
    text: "member",
    context: "Singular form of 'member' in the members count chip.",
    category: "environments",
    maxLength: 12,
    needsTranslation: true,
  },
  "environments.no_members": {
    text: "No members yet. Invite someone to get started.",
    context: "Empty state message when an environment has no members.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.remove_member": {
    text: "Remove member",
    context: "Tooltip for the remove member button.",
    category: "environments",
    maxLength: 20,
    needsTranslation: true,
  },
  "environments.role_updated": {
    text: "Member role updated",
    context: "Success message after changing a member's role.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.role_update_error": {
    text: "Failed to update member",
    context: "Error message when role update fails.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.member_removed": {
    text: "Member removed",
    context: "Success message after removing a member from an environment.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.member_remove_error": {
    text: "Failed to remove member",
    context: "Error message when member removal fails.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.col_can_export": {
    text: "Export",
    context:
      "Column header / label for the per-member toggle that controls whether a member can export and download data from the environment.",
    category: "environments",
    maxLength: 12,
    needsTranslation: true,
  },
  "environments.export_updated": {
    text: "Export permission updated",
    context:
      "Success toast after toggling a member's export/download permission.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.export_update_error": {
    text: "Failed to update export permission",
    context:
      "Error toast when toggling a member's export/download permission fails.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.export_toggle_hint": {
    text: "Allow this member to export and download data from this environment.",
    context:
      "Tooltip on the per-member export toggle (for non-viewer roles).",
    category: "environments",
    needsTranslation: true,
  },
  "environments.export_viewer_hint": {
    text: "Viewers cannot export regardless of this setting.",
    context:
      "Tooltip shown on the disabled export toggle for members with the Viewer role, who can never export.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.name_required": {
    text: "Environment name is required",
    context:
      "Validation message when trying to create an environment without a name.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.created_success": {
    text: "Environment created successfully",
    context: "Success message after creating a new environment.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.created_error": {
    text: "Failed to create environment",
    context: "Error message when environment creation fails.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.updated_success": {
    text: "Environment updated",
    context: "Success message after updating an environment.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.updated_error": {
    text: "Failed to update environment",
    context: "Error message when environment update fails.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.no_environments": {
    text: "No environments yet",
    context: "Empty state title when there are no environments.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.no_environments_desc": {
    text: "Create your first environment to start organizing patient data and inviting your team.",
    context: "Empty state description with call to action.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.unnamed": {
    text: "Unnamed",
    context: "Fallback name for environments without a name.",
    category: "environments",
    maxLength: 15,
    needsTranslation: true,
  },
  "environments.chip_ai": {
    text: "AI Enabled",
    context: "Chip label indicating an environment has AI extraction enabled.",
    category: "environments",
    maxLength: 15,
    needsTranslation: true,
  },
  "environments.chip_export": {
    text: "Export",
    context: "Chip label indicating an environment has data export enabled.",
    category: "environments",
    maxLength: 10,
    needsTranslation: true,
  },
  "environments.chip_anonymized": {
    text: "Anonymized",
    context:
      "Chip label indicating an environment requires patient anonymization.",
    category: "environments",
    maxLength: 15,
    needsTranslation: true,
  },
  "environments.patients": {
    text: "patients",
    context: "Label for the patient count on an environment card.",
    category: "environments",
    maxLength: 15,
    needsTranslation: true,
  },
  "environments.billed_to": {
    text: "Billed to",
    context: "Label showing which organization is billed for this environment.",
    category: "environments",
    maxLength: 15,
    needsTranslation: true,
  },
  "environments.search_email_label": {
    text: "Search by email",
    context: "Label for the user search input in the invite dialog.",
    category: "environments",
    maxLength: 25,
    needsTranslation: true,
  },
  "environments.search_email_placeholder": {
    text: "Start typing an email address...",
    context: "Placeholder for the user search input.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.search_email_helper": {
    text: "Type at least 3 characters to search. You can also type a full email for users not yet on the platform.",
    context: "Helper text for the user search input.",
    category: "environments",
    needsTranslation: true,
  },
  "environments.role_label": {
    text: "Role",
    context: "Label for the role selector when inviting a user.",
    category: "environments",
    maxLength: 10,
    needsTranslation: true,
  },
  "environments.status_pending": {
    text: "Pending",
    context: "Status chip for a member whose invitation is pending.",
    category: "environments",
    maxLength: 12,
    needsTranslation: true,
  },
  "environments.status_active": {
    text: "Active",
    context: "Status chip for an active member.",
    category: "environments",
    maxLength: 12,
    needsTranslation: true,
  },
  "environments.invited_on": {
    text: "Invited",
    context: "Label before the invitation date in the members list.",
    category: "environments",
    maxLength: 12,
    needsTranslation: true,
  },
  "environments.stat_environments": {
    text: "ENVIRONMENTS",
    context: "Stat card label for the total number of environments.",
    category: "environments",
    maxLength: 15,
    needsTranslation: true,
  },
  "environments.stat_env_limit": {
    text: "of {max} on {plan} plan",
    context: "Subtitle in stat card showing the environment limit.",
    category: "environments",
    placeholders: ["max", "plan"],
    needsTranslation: true,
  },
  "environments.stat_env_unlimited": {
    text: "Unlimited on {plan} plan",
    context: "Subtitle in stat card when environments are unlimited.",
    category: "environments",
    placeholders: ["plan"],
    needsTranslation: true,
  },
  "environments.stat_team_seats": {
    text: "TEAM SEATS",
    context: "Stat card label for the number of team seats available.",
    category: "environments",
    maxLength: 15,
    needsTranslation: true,
  },
  "environments.stat_seats_tier": {
    text: "{plan} plan",
    context: "Subtitle in stat card showing which plan provides the seats.",
    category: "environments",
    placeholders: ["plan"],
    needsTranslation: true,
  },
  "environments.unlimited": {
    text: "Unlimited",
    context: "Shown when team seats or environments are unlimited.",
    category: "environments",
    maxLength: 15,
    needsTranslation: true,
  },
  "environments.stat_total_patients": {
    text: "TOTAL PATIENTS",
    context: "Stat card label for total patients across all environments.",
    category: "environments",
    maxLength: 20,
    needsTranslation: true,
  },
  "environments.stat_across_envs": {
    text: "Across all environments",
    context: "Subtitle in the total patients stat card.",
    category: "environments",
    needsTranslation: true,
  },
  // ============================================================================
  // TIER GATE - Upgrade prompts & disabled feature messages
  // ============================================================================
  "tier_gate.view_plans": {
    text: "View Plans",
    context: "Button label that navigates to the plans page for upgrading.",
    category: "tier_gate",
    maxLength: 15,
    needsTranslation: true,
  },
  "tier_gate.upgrade_required": {
    text: "Upgrade Required",
    context:
      "Badge label shown on locked features that require a plan upgrade.",
    category: "tier_gate",
    maxLength: 25,
    needsTranslation: true,
  },
  "tier_gate.environments_menu": {
    text: "Upgrade to AI Professional to manage team environments and invite collaborators.",
    context:
      "Tooltip shown on the disabled Environments menu item for users on Free or Professional plans.",
    category: "tier_gate",
    needsTranslation: true,
  },
  "tier_gate.invite_upgrade_prompt": {
    text: "Upgrade to AI Professional or higher to invite team members and collaborate on patient data within your environments.",
    context:
      "Alert banner shown on the Environments page for users who cannot invite team members on their current plan.",
    category: "tier_gate",
    needsTranslation: true,
  },
  "tier_gate.invite_requires_upgrade": {
    text: "Inviting team members requires the AI Professional plan or higher.",
    context: "Tooltip on the disabled Invite button for lower-tier users.",
    category: "tier_gate",
    needsTranslation: true,
  },
  "tier_gate.env_limit_reached": {
    text: "You have reached the maximum number of environments for your current plan. Upgrade to create more.",
    context: "Tooltip on the disabled Create Environment button when at limit.",
    category: "tier_gate",
    needsTranslation: true,
  },
  "tier_gate.ai_extraction_locked": {
    text: "AI-powered data extraction is available on the AI Professional plan and above.",
    context: "Tooltip for disabled AI extraction features on lower tiers.",
    category: "tier_gate",
    needsTranslation: true,
  },
  "tier_gate.voice_locked": {
    text: "Voice transcription is available on the AI Professional plan and above.",
    context: "Tooltip for disabled voice recording features on lower tiers.",
    category: "tier_gate",
    needsTranslation: true,
  },
  "tier_gate.export_locked": {
    text: "Data export is available on the Network plan and above.",
    context: "Tooltip for disabled export features on lower tiers.",
    category: "tier_gate",
    needsTranslation: true,
  },
  "tier_gate.bulk_upload_locked": {
    text: "Bulk upload is available on the AI Professional plan and above.",
    context: "Tooltip for disabled bulk upload features on lower tiers.",
    category: "tier_gate",
    needsTranslation: true,
  },
  "tier_gate.cross_org_locked": {
    text: "Cross-organization sharing is available on the Network plan and above.",
    context: "Tooltip for disabled cross-org sharing features on lower tiers.",
    category: "tier_gate",
    needsTranslation: true,
  },
  "tier_gate.feature_unavailable": {
    text: "This feature is not available on your current plan.",
    context: "Generic message for features not included in the user's tier.",
    category: "tier_gate",
    needsTranslation: true,
  },
  "tier_gate.upgrade_to_unlock": {
    text: "Upgrade your plan to unlock this feature.",
    context: "Generic call to action for tier-gated features.",
    category: "tier_gate",
    maxLength: 50,
    needsTranslation: true,
  },
  // ============================================================================
  // NAV - Environments menu item
  // ============================================================================
  "nav.environments": {
    text: "Environments",
    context:
      "Menu item in the user dropdown to navigate to the Environments page.",
    category: "navigation",
    maxLength: 20,
    needsTranslation: true,
  },
  // ============================================================================
  // Contracts Wizard
  // ============================================================================
  "contracts_wizard.title": {
    text: "Legal Setup",
    context: "Title of the contracts wizard modal.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.intro.why_title": {
    text: "Why this matters",
    context: "Section title in the wizard intro step.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.intro.why_body": {
    text: "Legal compliance protects both your institution and your patients. We recommend you share the documents generated in the next steps with your ethics committee (hospitals) or compliance officer (CRO / pharmaceutical companies).",
    context: "Body text explaining why legal setup matters.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.intro.commitment_title": {
    text: "Our commitment",
    context: "Section title in the wizard intro step.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.intro.commitment_processor": {
    text: "Athena Tech acts exclusively as a data processor under your instructions. We do not claim ownership of your data.",
    context: "Data processor commitment statement.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.intro.commitment_baa": {
    text: "All AI processing runs on dedicated Microsoft Azure instances in Europe under a Business Associate Agreement (BAA). Your data stays in the EU and is processed here.",
    context: "BAA and HIPAA commitment statement.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.intro.next_steps_title": {
    text: "What you'll do next",
    context: "Section title listing the wizard steps.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.intro.next_step_1": {
    text: "Institutional Agreement - Configure and sign the agreement that governs our relationship.",
    context: "Description of step 1 in the wizard.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.intro.next_step_2": {
    text: "Patient Consent Template - Build a consent template for your patients.",
    context: "Description of step 2 in the wizard.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.modules_title": {
    text: "Select Agreement Modules",
    context: "Title for the module selection section.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.modules_warning": {
    text: "Before proceeding, verify that only the modules relevant to your practice are selected. Your agreement should reflect the scope approved by your ethics committee.",
    context:
      "Advisory about selecting only relevant modules to match ethics committee scope.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.generate": {
    text: "Generate Agreement",
    context: "Button to generate the agreement PDF.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.review_title": {
    text: "Review Your Agreement",
    context: "Title for the PDF review phase.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.download": {
    text: "Download PDF",
    context: "Button to download the agreement PDF.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.continue_to_sign": {
    text: "Continue to Sign",
    context: "Button to proceed from review to signing.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.sign_digitally": {
    text: "Sign Digitally",
    context: "Tab label for digital signature.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.upload_signed": {
    text: "Upload Signed Copy",
    context: "Tab label for uploading a signed PDF.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.upload_hint": {
    text: "If your institution requires a wet signature or external digital signature, upload the signed PDF here.",
    context: "Instructions for the upload tab.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.signature_detected": {
    text: "Digital signature detected",
    context: "Confirmation that the uploaded PDF has a digital signature.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.signature_not_detected": {
    text: "We could not detect a digital signature. If this document was signed with a wet signature and scanned, please confirm below.",
    context: "Warning when no digital signature is found in uploaded PDF.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.confirm_wet_signature": {
    text: "I confirm this document has been properly signed",
    context: "Checkbox to confirm a wet-signed document.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.confirm_read": {
    text: "I have read and agree to the terms of this agreement",
    context: "Checkbox to confirm having read the agreement.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.confirm_authorized": {
    text: "I am authorized to sign on behalf of this institution",
    context: "Checkbox to confirm signing authority.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.agreement.sign_agreement": {
    text: "Sign Agreement",
    context: "Button to digitally sign the agreement.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.consent.title": {
    text: "Patient Consent Template",
    context: "Title for the patient consent wizard step.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.consent.responsibility": {
    text: "The responsibility to collect patient consent belongs to your institution. We provide this template to simplify your workflow. You are free to use your own consent document.",
    context: "Explanation of institutional consent responsibility.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.consent.language": {
    text: "Template Language",
    context: "Label for the language selector.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.consent.modules_title": {
    text: "Consent Sections",
    context: "Title for the consent module checkboxes.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.consent.preset_standard": {
    text: "Standard",
    context: "Label for the standard consent preset.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.consent.preset_extended": {
    text: "Extended",
    context: "Label for the extended consent preset.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.consent.generate": {
    text: "Generate Template",
    context: "Button to generate the consent PDF.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.consent.download": {
    text: "Download Template",
    context: "Button to download the consent PDF.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.consent.acknowledgment": {
    text: "I understand that the responsibility to collect and manage patient consent lies with my institution",
    context: "Acknowledgment checkbox for consent responsibility.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.consent.complete": {
    text: "Complete Setup",
    context: "Button to complete the consent step.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.complete.title": {
    text: "You're all set!",
    context: "Title for the completion step.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.complete.body": {
    text: "Your legal setup is complete. Your signed agreement and consent template are available anytime from this menu.",
    context: "Body text for the completion step.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.complete.update_hint": {
    text: "If your institution's needs change, you can return here to update your agreement.",
    context: "Hint about being able to update later.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.complete.close": {
    text: "Close",
    context: "Button to close the wizard.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.chip.complete": {
    text: "Legal setup complete",
    context: "Tooltip for the navbar chip when setup is complete.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.chip.pending": {
    text: "Legal setup pending",
    context: "Tooltip for the navbar chip when setup is in progress.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.chip.not_started": {
    text: "Legal setup required",
    context: "Tooltip for the navbar chip when no setup has started.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.step.intro": {
    text: "Introduction",
    context: "Stepper label for the introduction step.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.step.agreement": {
    text: "Agreement",
    context: "Stepper label for the agreement step.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.step.consent": {
    text: "Patient Consent",
    context: "Stepper label for the consent step.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  "contracts_wizard.step.complete": {
    text: "Complete",
    context: "Stepper label for the completion step.",
    category: "contracts_wizard",
    needsTranslation: true,
  },
  // ============================================================================
  // Admin Legal Review
  // ============================================================================
  "admin_legal.title": {
    text: "Legal Compliance Review",
    context: "Title for the superadmin legal review panel.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.total_orgs": {
    text: "Total Organizations",
    context: "Summary card label.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.signed": {
    text: "Agreements Signed",
    context: "Summary card label.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.pending": {
    text: "Agreements Pending",
    context: "Summary card label.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.consent_ack": {
    text: "Consent Acknowledged",
    context: "Summary card label.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.org_name": {
    text: "Organization",
    context: "Table column header.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.tier": {
    text: "Tier",
    context: "Table column header.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.agreement_status": {
    text: "Agreement",
    context: "Table column header.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.signed_by": {
    text: "Signed By",
    context: "Table column header.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.signed_date": {
    text: "Date",
    context: "Table column header.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.modules": {
    text: "Modules",
    context: "Table column header.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.consent": {
    text: "Consent",
    context: "Table column header.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.actions": {
    text: "Actions",
    context: "Table column header.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.download_pdf": {
    text: "Download Signed PDF",
    context: "Button to download a signed agreement PDF.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.not_started": {
    text: "Not Started",
    context: "Status chip for organizations with no agreement.",
    category: "admin_legal",
    needsTranslation: true,
  },
  "admin_legal.search_placeholder": {
    text: "Search organizations...",
    context: "Placeholder for the search input.",
    category: "admin_legal",
    needsTranslation: true,
  },

  // --- ManageEnvironments role options ---
  "manage_env.role_owner": {
    text: "Owner",
    context: "Label for the Owner role in environment management",
    category: "manage_env",
    needsTranslation: true,
  },
  "manage_env.role_admin": {
    text: "Admin",
    context: "Label for the Admin role in environment management",
    category: "manage_env",
    needsTranslation: true,
  },
  "manage_env.role_editor": {
    text: "Editor",
    context: "Label for the Editor role in environment management",
    category: "manage_env",
    needsTranslation: true,
  },
  "manage_env.role_contributor": {
    text: "Contributor",
    context: "Label for the Contributor role in environment management",
    category: "manage_env",
    needsTranslation: true,
  },
  "manage_env.role_viewer": {
    text: "Viewer",
    context: "Label for the Viewer role in environment management",
    category: "manage_env",
    needsTranslation: true,
  },
  "manage_env.role_owner_desc": {
    text: "Full control, can delete environment",
    context: "Description for the Owner role in environment management",
    category: "manage_env",
    needsTranslation: true,
  },
  "manage_env.role_admin_desc": {
    text: "Full control except delete",
    context: "Description for the Admin role in environment management",
    category: "manage_env",
    needsTranslation: true,
  },
  "manage_env.role_editor_desc": {
    text: "Can view, add, and edit",
    context: "Description for the Editor role in environment management",
    category: "manage_env",
    needsTranslation: true,
  },
  "manage_env.role_contributor_desc": {
    text: "Can view and add only",
    context: "Description for the Contributor role in environment management",
    category: "manage_env",
    needsTranslation: true,
  },
  "manage_env.role_viewer_desc": {
    text: "Read-only access",
    context: "Description for the Viewer role in environment management",
    category: "manage_env",
    needsTranslation: true,
  },
  "manage_env.user_fallback": {
    text: "User",
    context: "Fallback display name when a member has no name or username",
    category: "manage_env",
    needsTranslation: true,
  },

  // --- CreateEnvironmentModal role labels ---
  "env_modal.role_admin": {
    text: "Admin",
    context: "Label for the Admin role in environment creation modal",
    category: "env_modal",
    needsTranslation: true,
  },
  "env_modal.role_editor": {
    text: "Editor",
    context: "Label for the Editor role in environment creation modal",
    category: "env_modal",
    needsTranslation: true,
  },
  "env_modal.role_contributor": {
    text: "Contributor",
    context: "Label for the Contributor role in environment creation modal",
    category: "env_modal",
    needsTranslation: true,
  },
  "env_modal.role_viewer": {
    text: "Viewer",
    context: "Label for the Viewer role in environment creation modal",
    category: "env_modal",
    needsTranslation: true,
  },

  // --- AdminLegalReviewPanel error ---
  "admin_legal.load_error": {
    text: "Failed to load compliance overview",
    context: "Error message when compliance overview API call fails",
    category: "admin_legal",
    needsTranslation: true,
  },

  // ── Error Tickets ──────────────────────────────────────────────────
  "error_tickets.severity_label": {
    text: "Severity",
    context: "Label for the severity filter dropdown.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.status_label": {
    text: "Status",
    context: "Label for the status filter and resolution status dropdowns.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.filter_all": {
    text: "All",
    context: "Option to show all items (no filter applied).",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.severity_critical": {
    text: "critical",
    context: "Severity filter option.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.severity_high": {
    text: "high",
    context: "Severity filter option.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.severity_medium": {
    text: "medium",
    context: "Severity filter option.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.severity_low": {
    text: "low",
    context: "Severity filter option.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.status_open": {
    text: "open",
    context: "Status filter option.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.status_investigating": {
    text: "investigating",
    context: "Status filter option.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.status_resolved": {
    text: "resolved",
    context: "Status filter option.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.status_wont_fix": {
    text: "wont fix",
    context: "Status filter option for tickets that will not be fixed.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.clear_resolved": {
    text: "Clear Resolved",
    context: "Button to delete all resolved error tickets.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.col_time": {
    text: "Time",
    context: "Table column header for ticket creation time.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.col_severity": {
    text: "Severity",
    context: "Table column header.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.col_status": {
    text: "Status",
    context: "Table column header.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.col_title": {
    text: "Title",
    context: "Table column header.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.col_source": {
    text: "Source",
    context: "Table column header.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.col_count": {
    text: "#",
    context: "Table column header for occurrence count.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.total_tickets": {
    text: "total tickets",
    context: "Suffix after a number showing total ticket count.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.load_error": {
    text: "Failed to load ticket",
    context: "Snackbar error when ticket detail fetch fails.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.update_success": {
    text: "Ticket updated",
    context: "Snackbar success after updating a ticket.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.update_error": {
    text: "Failed to update ticket",
    context: "Snackbar error when ticket update fails.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.delete_success": {
    text: "Ticket deleted",
    context: "Snackbar success after deleting a ticket.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.delete_error": {
    text: "Failed to delete ticket",
    context: "Snackbar error when ticket deletion fails.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.clear_success": {
    text: "resolved tickets cleared",
    context:
      "Snackbar success after clearing resolved tickets (prefixed by count).",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.clear_error": {
    text: "Failed to clear tickets",
    context: "Snackbar error when clearing resolved tickets fails.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.detail_severity": {
    text: "Severity",
    context: "Label in the ticket detail dialog meta section.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.detail_status": {
    text: "Status",
    context: "Label in the ticket detail dialog meta section.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.detail_source": {
    text: "Source",
    context: "Label in the ticket detail dialog meta section.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.detail_occurrences": {
    text: "Occurrences",
    context: "Label in the ticket detail dialog meta section.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.affected_user": {
    text: "Affected user:",
    context: "Label before the affected user email in ticket detail.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.request_path": {
    text: "Path:",
    context: "Label before the request path in ticket detail.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.description_traceback": {
    text: "Description / Traceback",
    context: "Section heading for the error description or traceback.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.resolution": {
    text: "Resolution",
    context: "Section heading for the ticket resolution form.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.resolution_notes": {
    text: "Resolution Notes",
    context: "Label for the resolution notes text field.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.resolved_by": {
    text: "Resolved by",
    context: "Prefix before the resolver name in ticket detail.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.resolved_on": {
    text: "on",
    context: "Connector word between resolver name and resolution date.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.delete_button": {
    text: "Delete",
    context: "Button to delete an error ticket.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.close_button": {
    text: "Close",
    context: "Button to close the ticket detail dialog.",
    category: "error_tickets",
    needsTranslation: true,
  },
  "error_tickets.update_button": {
    text: "Update",
    context: "Button to save ticket resolution changes.",
    category: "error_tickets",
    needsTranslation: true,
  },

  // ── Monitoring Dashboard ───────────────────────────────────────────
  "monitoring.utilization": {
    text: "utilization",
    context: "Suffix after percentage in slot gauge (e.g. '75% utilization').",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.refresh_now": {
    text: "Refresh now",
    context: "Tooltip on the manual refresh button.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.channel_interactive": {
    text: "Interactive",
    context: "Label for the interactive LLM channel.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.channel_bulk": {
    text: "Bulk",
    context: "Label for the bulk LLM channel.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.channel_voice": {
    text: "Voice",
    context: "Label for the voice LLM channel.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.axis_total": {
    text: "Total",
    context: "Y-axis label for total tokens in the token rate chart.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.axis_prompt_completion": {
    text: "Prompt / Completion",
    context: "Y-axis label for prompt and completion tokens.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.series_total_tokens": {
    text: "Total Tokens",
    context: "Chart series name for total token count.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.series_prompt": {
    text: "Prompt",
    context: "Chart series name for prompt tokens.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.series_completion": {
    text: "Completion",
    context: "Chart series name for completion tokens.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.tokens_per_min": {
    text: "tokens/min",
    context: "Unit label for token rate (e.g. '1,200 tokens/min').",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.last_hour": {
    text: "last hour",
    context:
      "Suffix for the total-tokens-last-hour chip (e.g. '45,000 last hour').",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.no_token_data": {
    text: "No token rate data available",
    context: "Empty state when token rate data cannot be loaded.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.axis_tokens_cumulative": {
    text: "Tokens (cumulative)",
    context: "Y-axis label for cumulative token usage chart.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.no_usage_data": {
    text: "No usage data available",
    context: "Empty state when usage history data cannot be loaded.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.prompt_tokens": {
    text: "Prompt Tokens",
    context: "Cost summary card label for prompt tokens.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.completion_tokens": {
    text: "Completion Tokens",
    context: "Cost summary card label for completion tokens.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.total_mtd": {
    text: "Total (MTD)",
    context: "Cost summary card label for month-to-date total.",
    category: "monitoring",
    needsTranslation: true,
  },
  "monitoring.no_cost_data": {
    text: "Cost data unavailable",
    context: "Empty state when cost summary data cannot be loaded.",
    category: "monitoring",
    needsTranslation: true,
  },

  // ============================================================================
  // TIER PRICING EDITOR (Admin)
  // ============================================================================
  "tier_editor.error_load": {
    text: "Failed to load tiers.",
    context: "Error message when tier data fails to load from the API.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.error_save": {
    text: "Failed to save tier.",
    context: "Error message when saving tier changes fails.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.error_sync": {
    text: "Stripe sync failed.",
    context: "Error message when Stripe synchronization fails.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.updated_successfully": {
    text: "updated successfully.",
    context:
      "Success suffix shown after a tier name, e.g. 'Starter updated successfully.'",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.retry": {
    text: "Retry",
    context: "Button to retry loading tiers after an error.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.sync_tooltip": {
    text: "Sync all paid tiers to Stripe — creates/updates Products and Prices",
    context: "Tooltip explaining the Sync All to Stripe button.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.syncing": {
    text: "Syncing…",
    context: "Button label while Stripe sync is in progress.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.sync_all": {
    text: "Sync All to Stripe",
    context: "Button to sync all paid tiers to Stripe.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.sync_results_title": {
    text: "Stripe Sync Results",
    context: "Section title for the Stripe sync results panel.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.dismiss": {
    text: "Dismiss",
    context: "Button to dismiss the sync results panel.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.free_price": {
    text: "Free",
    context: "Price display for tiers with zero cost.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.per_month": {
    text: "/mo",
    context: "Abbreviated per-month suffix shown next to tier prices.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.per_month_long": {
    text: "/ month",
    context: "Per-month suffix in the price helper text.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.stripe_synced": {
    text: "Stripe Synced",
    context: "Chip label indicating a tier is synced with Stripe.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.no_stripe": {
    text: "No Stripe",
    context: "Chip label for the free tier which has no Stripe integration.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.custom": {
    text: "Custom",
    context: "Chip label for enterprise tier with custom pricing.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.not_synced": {
    text: "Not Synced",
    context: "Chip label indicating a tier is not yet synced with Stripe.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.features_enabled": {
    text: "features enabled",
    context:
      "Suffix in the feature count display, e.g. '10 / 14 features enabled'.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.edit_prefix": {
    text: "Edit",
    context:
      "Prefix for the edit tier dialog title, followed by the tier name.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.edit_subtitle": {
    text: "Modify pricing, quotas, and features for this tier",
    context: "Subtitle in the edit tier dialog.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.section_pricing": {
    text: "Pricing",
    context: "Section header in the edit tier dialog.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.label_price_monthly": {
    text: "Monthly Price (cents)",
    context: "Label for the monthly price input field.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.label_display_name": {
    text: "Display Name",
    context: "Label for the tier display name input field.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.label_trial_days": {
    text: "Trial Days",
    context: "Label for the trial days input field.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.day_free_trial": {
    text: "-day free trial",
    context: "Suffix in the trial days helper text, e.g. '14-day free trial'.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.no_trial": {
    text: "No trial",
    context: "Helper text when trial days is zero.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.label_display_order": {
    text: "Display Order",
    context: "Label for the display order input field.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.label_description": {
    text: "Description",
    context: "Label for the tier description input field.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.stripe_integration": {
    text: "Stripe Integration",
    context: "Info box title showing Stripe product/price details.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.stripe_product": {
    text: "Product:",
    context: "Label for the Stripe product ID display.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.stripe_price": {
    text: "Price:",
    context: "Label for the Stripe price ID display.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.stripe_price_warning": {
    text: "Changing the price will archive the old Stripe Price and create a new one automatically.",
    context: "Warning text about Stripe price changes.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.section_quotas": {
    text: "Quotas & Limits",
    context: "Section header for the quotas editing area.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.quota_none": {
    text: "None",
    context:
      "Helper text when a quota value is zero for tokens/documents/voice.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.quota_unlimited": {
    text: "Unlimited",
    context:
      "Helper text when a quota value is zero for patients/users/environments.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.section_features": {
    text: "Feature Flags",
    context: "Section header for the feature flags editing area.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.saving": {
    text: "Saving…",
    context: "Button label while a save operation is in progress.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.save_changes": {
    text: "Save Changes",
    context: "Button to save tier changes.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.max_patients": {
    text: "Max Patients",
    context: "Quota field label for the maximum number of patients.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.max_documents_per_month": {
    text: "Documents / Month",
    context: "Quota field label for the maximum documents per month.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.max_tokens_per_month": {
    text: "Tokens / Month",
    context: "Quota field label for the maximum AI tokens per month.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.max_voice_minutes_per_month": {
    text: "Voice Min / Month",
    context:
      "Quota field label for the maximum voice transcription minutes per month.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.max_users": {
    text: "Max Users",
    context: "Quota field label for the maximum number of users.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.max_environments": {
    text: "Max Environments",
    context: "Quota field label for the maximum number of environments.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.max_bulk_upload_patients": {
    text: "Bulk Upload Limit",
    context: "Quota field label for the bulk upload patient limit.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.can_edit_records": {
    text: "Edit Records",
    context: "Feature flag label for record editing capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.can_use_ai_extraction": {
    text: "AI Extraction",
    context: "Feature flag label for AI data extraction capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.can_use_voice": {
    text: "Voice Transcription",
    context: "Feature flag label for voice transcription capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.can_export_csv": {
    text: "Export CSV",
    context: "Feature flag label for CSV export capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.can_export_api": {
    text: "Export API",
    context: "Feature flag label for API export capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.can_use_api": {
    text: "API Access",
    context: "Feature flag label for API access capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.can_cross_org_share": {
    text: "Cross-Org Sharing",
    context: "Feature flag label for cross-organization sharing capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.can_bulk_upload": {
    text: "Bulk Upload",
    context: "Feature flag label for bulk upload capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.has_analytics_view": {
    text: "Analytics View",
    context: "Feature flag label for analytics view capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.has_analytics_filter": {
    text: "Analytics Filter (NL)",
    context: "Feature flag label for natural language analytics filter.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.has_sso": {
    text: "SSO",
    context: "Feature flag label for single sign-on capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.has_audit_logs": {
    text: "Audit Logs",
    context: "Feature flag label for audit logs capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.has_priority_support": {
    text: "Priority Support",
    context: "Feature flag label for priority support capability.",
    category: "tier_editor",
    needsTranslation: true,
  },
  "tier_editor.requires_institutional_agreement": {
    text: "Requires Agreement",
    context: "Feature flag label for requiring an institutional agreement.",
    category: "tier_editor",
    needsTranslation: true,
  },

  // ============================================================================
  // BILLING SETTINGS (additional keys)
  // ============================================================================
  "billing.checkout_success": {
    text: "Subscription activated successfully!",
    context: "Snackbar message after successful Stripe checkout.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.checkout_cancelled": {
    text: "Checkout was cancelled.",
    context: "Snackbar message when user cancels Stripe checkout.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.member_role_updated": {
    text: "Member role updated",
    context: "Snackbar message after updating a member's role.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.error_update_member": {
    text: "Failed to update member",
    context: "Error message when member role update fails.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.member_removed": {
    text: "Member removed",
    context: "Snackbar message after removing a member.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.error_remove_member": {
    text: "Failed to remove member",
    context: "Error message when member removal fails.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.error_start_checkout": {
    text: "Failed to start checkout",
    context: "Error message when Stripe checkout initiation fails.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.error_open_portal": {
    text: "Failed to open billing portal",
    context: "Error message when Stripe billing portal fails to open.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.overage_enabled_msg": {
    text: "Overage billing enabled. You will be charged for usage beyond your quota.",
    context: "Snackbar message when overage billing is turned on.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.overage_disabled_msg": {
    text: "Overage billing disabled. Usage will be blocked when quota is reached.",
    context: "Snackbar message when overage billing is turned off.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.error_update_settings": {
    text: "Failed to update settings",
    context: "Error message when organization settings update fails.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.org_name_updated": {
    text: "Organization name updated",
    context: "Snackbar message after successfully updating the org name.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.error_update_name": {
    text: "Failed to update name",
    context: "Error message when organization name update fails.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.env_name_required": {
    text: "Environment name is required",
    context: "Validation warning when creating an environment without a name.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.env_created": {
    text: "Environment created successfully",
    context: "Snackbar message after creating a new environment.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.error_create_env": {
    text: "Failed to create environment",
    context: "Error message when environment creation fails.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.env_updated": {
    text: "Environment updated",
    context: "Snackbar message after updating an environment.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.error_update_env": {
    text: "Failed to update environment",
    context: "Error message when environment update fails.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.invitation_sent": {
    text: "Invitation sent successfully. The user will receive a notification.",
    context: "Snackbar message after sending an environment invitation.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.error_send_invitation": {
    text: "Failed to send invitation",
    context: "Error message when invitation fails to send.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.unnamed": {
    text: "Unnamed",
    context: "Fallback name for environments without a name.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.ai_enabled_chip": {
    text: "AI Enabled",
    context: "Chip label for environments with AI extraction enabled.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.billed_to": {
    text: "Billed to:",
    context: "Label prefix before the organization name on environment cards.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.invite": {
    text: "Invite",
    context: "Button to open the invite user dialog for an environment.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.members": {
    text: "Members",
    context: "Button to open the members list for an environment.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.invite_to": {
    text: "Invite to",
    context: "Dialog title prefix, followed by environment name.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.search_by_email": {
    text: "Search by email",
    context: "Label for the email search field in the invite dialog.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.search_email_placeholder": {
    text: "Start typing an email address...",
    context: "Placeholder text for the email search input.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.search_email_helper": {
    text: "Type at least 3 characters to search. You can also type a full email for users not yet on the platform.",
    context: "Helper text for the invite email search field.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.role_label": {
    text: "Role",
    context: "Label for the role selection dropdown.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.send_invitation": {
    text: "Send Invitation",
    context: "Button to submit the invitation in the invite dialog.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.members_of": {
    text: "Members of",
    context:
      "Dialog title prefix for the members modal, followed by environment name.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.no_members": {
    text: "No members yet. Invite someone to get started.",
    context: "Empty state message in the members dialog.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.status_pending": {
    text: "Pending",
    context: "Chip label for pending member invitations.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.status_active": {
    text: "Active",
    context: "Chip label for active members.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.remove_member": {
    text: "Remove member",
    context: "Tooltip for the remove member button.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.invite_member": {
    text: "Invite Member",
    context: "Button in the members dialog to open the invite flow.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.beta_simulator_title": {
    text: "Beta Tier Simulator",
    context: "Title for the beta tier simulation panel (beta users only).",
    category: "billing",
    needsTranslation: true,
  },
  "billing.beta_simulator_desc": {
    text: "Experience the product as a specific tier would see it. Only affects the frontend UI.",
    context: "Description for the beta tier simulation panel.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.stop_simulation": {
    text: "Stop Simulation",
    context: "Button to stop the tier simulation.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.opening": {
    text: "Opening...",
    context: "Button label while the Stripe portal is loading.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.manage_subscription": {
    text: "Manage Subscription",
    context: "Button to open the Stripe customer portal.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.cancelling": {
    text: "Cancelling...",
    context: "Button label while subscription cancellation is in progress.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.cancel_subscription": {
    text: "Cancel Subscription",
    context: "Button to initiate subscription cancellation.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.subscription_manage_hint": {
    text: "Update payment method, change plan, or cancel your subscription",
    context: "Caption text below the subscription management buttons.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.cancel_subscription_title": {
    text: "Cancel Subscription",
    context: "Dialog title for the subscription cancellation confirmation.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.cancel_what_happens": {
    text: "Here's what will happen:",
    context: "Intro text in the cancellation dialog explaining consequences.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.cancel_data_preserved": {
    text: "All your environments, patients, and data will be preserved.",
    context: "Reassurance text in the cancellation dialog about data safety.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.cancel_resubscribe": {
    text: "You can resubscribe at any time to regain access to premium features.",
    context: "Text in the cancellation dialog about resubscribing.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.keep_subscription": {
    text: "Keep My Subscription",
    context:
      "Button to dismiss the cancellation dialog and keep the subscription.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.yes_cancel": {
    text: "Yes, Cancel",
    context: "Button to confirm subscription cancellation.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.no_invoices": {
    text: "No invoices yet. Invoices will appear here once you have a paid subscription.",
    context: "Empty state message in the invoices tab.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.invoice_date": {
    text: "Date",
    context: "Invoice table column header for the invoice date.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.invoice_period": {
    text: "Period",
    context: "Invoice table column header for the billing period.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.invoice_amount": {
    text: "Amount",
    context: "Invoice table column header for the amount paid.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.invoice_status": {
    text: "Status",
    context: "Invoice table column header for the payment status.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.invoice_actions": {
    text: "Actions",
    context: "Invoice table column header for download/view actions.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.organization_heading": {
    text: "Organization",
    context: "Heading for the organization info card in settings.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.org_name_label": {
    text: "Organization Name",
    context: "Label for the organization name field in settings.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.subscription_status_label": {
    text: "Subscription Status",
    context: "Label for the subscription status display in settings.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.member_since": {
    text: "Member Since",
    context: "Label for the organization creation date in settings.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.billing_settings_heading": {
    text: "Billing Settings",
    context: "Heading for the billing settings card.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.overage_desc": {
    text: "When enabled, usage beyond your quota will be charged to your payment method. When disabled, features will be blocked when quota is reached.",
    context: "Description text for the overage billing toggle.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.payment_method_label": {
    text: "Payment Method",
    context: "Label for the payment method section in billing settings.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.manage_payment": {
    text: "Manage Payment Methods",
    context: "Button to open Stripe portal for payment method management.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.cancel_subscription_label": {
    text: "Cancel Subscription",
    context: "Label for the cancel subscription section in billing settings.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.cancel_desc": {
    text: "You can cancel your subscription at any time. Your access will continue until the end of your billing period.",
    context: "Description text for the cancel subscription section.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.tab_admin_pricing": {
    text: "Pricing",
    context: "Tab label for the admin pricing panel (superadmin only).",
    category: "billing",
    needsTranslation: true,
  },
  "billing.admin_panel_hint": {
    text: "For monitoring, analytics, error tickets, and system config, visit the full Admin Panel.",
    context: "Hint text in the admin pricing tab directing to the admin panel.",
    category: "billing",
    needsTranslation: true,
  },
  "billing.open_admin_panel": {
    text: "Open Admin Panel",
    context: "Button to navigate to the full admin panel.",
    category: "billing",
    needsTranslation: true,
  },

  // ============================================================================
  // HERO / LANDING
  // ============================================================================
  "hero.transform_clinical_data": {
    text: "Transform Clinical Data",
    context:
      "Main heading on the hero section of the landing page, first part before gradient text.",
    category: "landing",
    needsTranslation: true,
  },
  "hero.into_intelligence": {
    text: "into Intelligence",
    context: "Gradient-styled continuation of the hero heading.",
    category: "landing",
    needsTranslation: true,
  },
  "hero.description": {
    text: "The AI-powered oncology platform that extracts, structures, and analyzes patient data across 44 cancer types. From molecular genetics to treatment outcomes, fully automated.",
    context:
      "Hero section subtitle text describing the platform's capabilities.",
    category: "landing",
    needsTranslation: true,
  },
  "hero.log_in": {
    text: "Log In",
    context:
      "Primary CTA button on the hero section that opens the login panel.",
    category: "landing",
    maxLength: 10,
    needsTranslation: true,
  },
  "hero.learn_more": {
    text: "Learn More",
    context:
      "Secondary CTA button on the hero section that scrolls to features.",
    category: "landing",
    maxLength: 15,
    needsTranslation: true,
  },
  "hero.sign_in": {
    text: "Sign In",
    context: "Heading on the sign-in panel within the hero card.",
    category: "landing",
    maxLength: 10,
    needsTranslation: true,
  },
  "hero.enterprise_info": {
    text: "Sign in or create your account using your institutional email (e.g. Microsoft 365, Google Workspace). If access is blocked, contact your IT department to approve Athena Tech.",
    context:
      "Info note under SSO buttons explaining that both sign-in and account creation are supported via institutional OAuth.",
    category: "landing",
    needsTranslation: true,
  },
  "hero.back": {
    text: "Back",
    context:
      "Button to go back from the login panel to the welcome panel in the hero card.",
    category: "landing",
    maxLength: 10,
    needsTranslation: true,
  },
  "hero.clinical_intelligence_platform": {
    text: "Clinical Intelligence Platform",
    context:
      "Subtitle shown under the Athena OncoCraft logo in the hero right panel.",
    category: "landing",
    needsTranslation: true,
  },
  "hero.stat_cancer_types": {
    text: "Cancer Types",
    context:
      "Label for the stat counter showing number of supported cancer types.",
    category: "landing",
    maxLength: 20,
    needsTranslation: true,
  },
  "hero.stat_patient_records": {
    text: "Patient Records",
    context:
      "Label for the stat counter showing number of patient records processed.",
    category: "landing",
    maxLength: 20,
    needsTranslation: true,
  },
  "hero.stat_hospitals": {
    text: "Hospitals & Institutions",
    context:
      "Label for the stat counter showing number of hospital/institution users.",
    category: "landing",
    maxLength: 30,
    needsTranslation: true,
  },
  "hero.stat_countries": {
    text: "Countries",
    context:
      "Label for the stat counter showing number of countries using the platform.",
    category: "landing",
    maxLength: 15,
    needsTranslation: true,
  },
  "hero.nl_query_label": {
    text: "Natural Language Query",
    context:
      "Label shown above NL query examples in the rotating sentences on the hero right panel.",
    category: "landing",
    needsTranslation: true,
  },

  // ============================================================================
  // AUTH SNACKBAR MESSAGES
  // ============================================================================
  "auth.login_successful": {
    text: "Login successful!",
    context:
      "Snackbar success message shown after a successful login via OAuth.",
    category: "auth",
    needsTranslation: true,
  },
  "auth.social_login_failed": {
    text: "Social login failed. Please try again.",
    context: "Snackbar error message shown when an OAuth login attempt fails.",
    category: "auth",
    needsTranslation: true,
  },
  "auth.email_already_registered": {
    text: "This email is already registered with another account. Please sign in with the original provider.",
    context:
      "Snackbar error when a user tries to log in with a provider that conflicts with an existing account.",
    category: "auth",
    needsTranslation: true,
  },

  // ============================================================================
  // NETWORK STATUS
  // ============================================================================
  "network.connection_restored": {
    text: "Connection restored",
    context:
      "Snackbar message shown when the user's internet connection is restored after being offline.",
    category: "network",
    needsTranslation: true,
  },
  "network.offline": {
    text: "You are offline. Some features may not work.",
    context:
      "Snackbar warning message shown when the user loses internet connection.",
    category: "network",
    needsTranslation: true,
  },

  // ============================================================================
  // ELIGIBLE TRIALS PANEL
  // ============================================================================
  "trials.eligible_title": {
    text: "Eligible Clinical Trials",
    context:
      "Panel title for the eligible clinical trials section in patient details.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.param_condition": {
    text: "Condition",
    context:
      "Editable clinical-trials search query strip: label for the medical condition / disease field.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.param_term": {
    text: "Term",
    context:
      "Editable clinical-trials search query strip: label for the free-text general search term field.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.param_status": {
    text: "Status",
    context:
      "Editable clinical-trials search query strip: label for the trial recruitment status field.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.search_query": {
    text: "Query",
    context:
      "Heading of the editable query strip that shows the parameters sent to ClinicalTrials.gov.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.search_query_tooltip": {
    text: "The query sent to ClinicalTrials.gov. Edit and re-run to widen or refine the search.",
    context:
      "Tooltip explaining the editable clinical-trials query strip.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.rerun": {
    text: "Re-run",
    context:
      "Button that re-runs the clinical-trials search after the user edits the query parameters.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.worldwide": {
    text: "Worldwide",
    context:
      "Default location label when no specific location filter is set for trial search.",
    category: "clinical",
    maxLength: 15,
    needsTranslation: true,
  },
  "trials.location_placeholder": {
    text: "City, Country (or leave blank for worldwide)",
    context: "Placeholder text for the clinical trials location filter input.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.change_location_tooltip": {
    text: "Click to change location filter",
    context: "Tooltip on the location chip in the eligible trials panel.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.enrolled": {
    text: "Enrolled",
    context: "Label for enrolled patient count in a clinical trial card.",
    category: "clinical",
    maxLength: 15,
    needsTranslation: true,
  },
  "trials.phase_early_1": {
    text: "Early Phase 1",
    context: "Clinical trial phase label.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.phase_1": {
    text: "Phase I",
    context: "Clinical trial phase label.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.phase_1_2": {
    text: "Phase I/II",
    context: "Clinical trial phase label.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.phase_2": {
    text: "Phase II",
    context: "Clinical trial phase label.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.phase_2_3": {
    text: "Phase II/III",
    context: "Clinical trial phase label.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.phase_3": {
    text: "Phase III",
    context: "Clinical trial phase label.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.phase_4": {
    text: "Phase IV",
    context: "Clinical trial phase label.",
    category: "clinical",
    needsTranslation: true,
  },
  "trials.phase_na": {
    text: "N/A",
    context: "Clinical trial phase label when phase is not applicable.",
    category: "clinical",
    needsTranslation: true,
  },

  // ============================================================================
  // PRICING SECTION (LANDING)
  // ============================================================================
  "pricing.section_title": {
    text: "Pricing",
    context: "Section heading for the pricing cards on the landing page.",
    category: "landing",
    needsTranslation: true,
  },
  "pricing.free_tier_note": {
    text: "Free tier available for invited viewers and contributors. No credit card required.",
    context: "Footnote text below the pricing cards on the landing page.",
    category: "landing",
    needsTranslation: true,
  },
  "pricing.most_popular": {
    text: "Most Popular",
    context: "Badge shown on the recommended pricing tier card.",
    category: "landing",
    maxLength: 15,
    needsTranslation: true,
  },
  "pricing.begin_journey": {
    text: "Begin the Journey",
    context:
      "CTA button text on the landing navbar leading to the pricing section.",
    category: "landing",
    maxLength: 25,
    needsTranslation: true,
  },

  // ============================================================================
  // DOCUMENT VIEWERS
  // ============================================================================
  "doc_viewer.pdf_title": {
    text: "PDF Viewer",
    context:
      "Title shown in document preview modals when displaying a PDF file.",
    category: "documents",
    needsTranslation: true,
  },
  "doc_viewer.office_title": {
    text: "Office Document Preview",
    context:
      "Title shown in document preview modals when displaying an Office document.",
    category: "documents",
    needsTranslation: true,
  },
  "doc_viewer.generic_title": {
    text: "Document Viewer",
    context: "Generic title shown in document viewer components.",
    category: "documents",
    needsTranslation: true,
  },

  // ============================================================================
  // ADMIN PANELS
  // ============================================================================
  "admin.custom_days_label": {
    text: "Custom days",
    context:
      "Label for the custom day count input in the admin access tiers panel.",
    category: "admin",
    needsTranslation: true,
  },
  "admin.custom_token_limit": {
    text: "Custom token limit (optional)",
    context:
      "Label for the custom token limit input when granting beta access.",
    category: "admin",
    needsTranslation: true,
  },
  "admin.grant_beta_tooltip": {
    text: "Grant time-limited beta access",
    context: "Tooltip on the button to grant temporary beta access to a user.",
    category: "admin",
    needsTranslation: true,
  },
  "admin.xxmm_phase_reading": {
    text: "Reading source",
    context:
      "XXMM sync live-progress label shown while the sync reads all data from the upstream XXMM database.",
    category: "admin",
    needsTranslation: true,
  },
  "admin.xxmm_phase_preparing": {
    text: "Preparing",
    context:
      "XXMM sync live-progress label shown while the sync transforms the loaded patient data in memory before writing.",
    category: "admin",
    needsTranslation: true,
  },
  "admin.xxmm_phase_writing_patients": {
    text: "Writing patients",
    context:
      "XXMM sync live-progress label shown while the sync bulk-writes patient and clinical records into OncoCraft.",
    category: "admin",
    needsTranslation: true,
  },
  "admin.xxmm_phase_writing_clinical": {
    text: "Writing clinical tables",
    context:
      "XXMM sync live-progress label shown while the sync writes the clinical (universal) tables into OncoCraft.",
    category: "admin",
    needsTranslation: true,
  },
  "admin.xxmm_phase_images": {
    text: "Downloading images",
    context:
      "XXMM sync live-progress label shown during the long follow-up phase that downloads clinical images one by one. Followed by a count like '3,120 / 7,718'.",
    category: "admin",
    needsTranslation: true,
  },
  "admin.config_saved": {
    text: "Configuration saved",
    context: "Snackbar success message after saving system configuration.",
    category: "admin",
    needsTranslation: true,
  },
  "admin.config_save_failed": {
    text: "Failed to save configuration",
    context: "Snackbar error message when system configuration save fails.",
    category: "admin",
    needsTranslation: true,
  },
  "admin.refresh_tooltip": {
    text: "Refresh",
    context: "Tooltip on the refresh icon button in admin panels.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.modified_chip": {
    text: "modified",
    context:
      "Chip label shown next to configuration values that have been modified.",
    category: "admin",
    maxLength: 15,
    needsTranslation: true,
  },
  "admin.period_label": {
    text: "Period",
    context: "Label for the time period selector in the landing analytics tab.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // AGENT CHAT
  // ============================================================================
  "agent_chat.batch_intake_title": {
    text: "Batch Intake Agent",
    context: "Title for the batch intake agent chat panel and home screen.",
    category: "agent_chat",
    needsTranslation: true,
  },

  // ============================================================================
  // EXPORT DIALOG
  // ============================================================================
  "export.format_excel": {
    text: "Excel (.xlsx)",
    context: "Label for the Excel export format option in the export dialog.",
    category: "export",
    needsTranslation: true,
  },
  "export.format_json": {
    text: "JSON",
    context: "Label for the JSON export format option in the export dialog.",
    category: "export",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // BETA BADGE
  // ============================================================================
  "beta.badge_label": {
    text: "BETA",
    context: "Badge label shown on features that are in beta testing.",
    category: "general",
    maxLength: 6,
    needsTranslation: true,
  },

  // ============================================================================
  // PATIENT DETAILS LAYOUT
  // ============================================================================
  "patient.expand_chat_tooltip": {
    text: "Expand chat",
    context:
      "Tooltip on the button to expand the chat panel in patient details.",
    category: "patients",
    needsTranslation: true,
  },

  // ============================================================================
  // BETA GATE CARD
  // ============================================================================
  "betaGate.onTheList": {
    text: "You're on the list.",
    context: "Confirmation title shown after user is registered for beta access.",
    category: "general",
    needsTranslation: true,
  },
  "betaGate.weWillReachOut": {
    text: "We'll reach out when Athena OncoCraft launches.",
    context: "Subtitle shown after beta registration confirming the user will be contacted.",
    category: "general",
    needsTranslation: true,
  },
  "betaGate.contactUs": {
    text: "Contact us at support@athenatechai.com",
    context: "Link text for users to contact support after registering for beta.",
    category: "general",
    needsTranslation: true,
  },
  "betaGate.headline": {
    text: "No more manual entry.",
    context: "Main headline on the beta gate card promoting the platform.",
    category: "general",
    needsTranslation: true,
  },
  "betaGate.body": {
    text: "Every day, clinicians handle pathology reports, lab results, voice notes, spreadsheets. We are building something that reads them all and turns them into structured, queryable data. Automatically. Across 44 cancer types.",
    context: "Description paragraph on the beta gate card explaining the platform.",
    category: "general",
    needsTranslation: true,
  },
  "betaGate.weWillReachOutReady": {
    text: "We'll reach out when it's ready.",
    context: "Italic note on the beta gate card reassuring the user they will be contacted.",
    category: "general",
    needsTranslation: true,
  },
  "betaGate.betaOpensIn": {
    text: "Beta opens in",
    context: "Label before the countdown number showing days until beta launch.",
    category: "general",
    needsTranslation: true,
  },
  "betaGate.days": {
    text: "days",
    context: "Unit label after the countdown number (e.g. '34 days').",
    category: "general",
    maxLength: 10,
    needsTranslation: true,
  },

  // ---------------------------------------------------------------------------
  // Welcome / Dashboard Empty State — How It Works steps
  // Shown in WelcomeHeroCard when no patients exist yet.
  // ---------------------------------------------------------------------------
  "dashboard.empty_state.how_it_works": {
    text: "HOW IT WORKS",
    context: "Small uppercase section label above the 4-step flow (Upload → Extract → Validate → Analyze) in the dashboard empty/welcome state.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "welcome.step_upload_title": {
    text: "Upload",
    context: "Step 1 title in the 4-step 'How It Works' flow on the dashboard welcome card. Refers to uploading clinical documents.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "welcome.step_upload_desc": {
    text: "Clinical documents, pathology reports, scans, or voice notes.",
    context: "Step 1 description in the dashboard welcome card 'How It Works' flow. Explains what types of files can be uploaded.",
    category: "dashboard",
    maxLength: 100,
    needsTranslation: true,
  },
  "welcome.step_extract_title": {
    text: "Extract",
    context: "Step 2 title in the 4-step 'How It Works' flow on the dashboard welcome card. Refers to AI extraction of structured data.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "welcome.step_extract_desc": {
    text: "AI structures data across 44 cancer types automatically.",
    context: "Step 2 description in the dashboard welcome card 'How It Works' flow. Explains that AI automatically structures data across cancer types.",
    category: "dashboard",
    maxLength: 100,
    needsTranslation: true,
  },
  "welcome.step_validate_title": {
    text: "Validate",
    context: "Step 3 title in the 4-step 'How It Works' flow on the dashboard welcome card. Refers to reviewing and approving AI-extracted data.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "welcome.step_validate_desc": {
    text: "Review and approve extracted tables with inline editing.",
    context: "Step 3 description in the dashboard welcome card 'How It Works' flow. Explains that users review AI-extracted data with inline editing.",
    category: "dashboard",
    maxLength: 100,
    needsTranslation: true,
  },
  "welcome.step_analyze_title": {
    text: "Analyze",
    context: "Step 4 title in the 4-step 'How It Works' flow on the dashboard welcome card. Refers to cohort analytics and dashboards.",
    category: "dashboard",
    maxLength: 20,
    needsTranslation: true,
  },
  "welcome.step_analyze_desc": {
    text: "Dashboards, natural language queries, and cohort analysis.",
    context: "Step 4 description in the dashboard welcome card 'How It Works' flow. Explains the analytics features available after data is structured.",
    category: "dashboard",
    maxLength: 100,
    needsTranslation: true,
  },

  // ---------------------------------------------------------------------------
  // Landing Page — How It Works section
  // Shown on the public landing page for unauthenticated visitors.
  // ---------------------------------------------------------------------------
  "landing.how_it_works_title": {
    text: "How It Works",
    context: "Section heading on the public landing page for the 4-step process flow (Upload → Extract → Validate → Analyze).",
    category: "landing",
    maxLength: 30,
    needsTranslation: true,
  },
  "landing.how_it_works_subtitle": {
    text: "From unstructured clinical text to actionable intelligence in four steps.",
    context: "Subtitle under the 'How It Works' section heading on the public landing page.",
    category: "landing",
    maxLength: 120,
    needsTranslation: true,
  },
  "landing.why_athena_title": {
    text: "Why Athena",
    context:
      "Section heading above the three Why Athena cards.",
    category: "landing",
    maxLength: 40,
    needsTranslation: true,
  },
  "landing.why_athena_subtitle": {
    text: "Built on open clinical standards, governed by the institutions that own the data.",
    context:
      "Why Athena section subtitle, one line under the heading.",
    category: "landing",
    maxLength: 110,
    needsTranslation: true,
  },
  "landing.why_standards_title": {
    text: "Standards-native, not another silo",
    context:
      "Why Athena card 1 title: interoperability.",
    category: "landing",
    maxLength: 60,
    needsTranslation: true,
  },
  "landing.why_standards_desc": {
    text: "Records are structured onto FHIR resources and coded with SNOMED CT as they are captured. Your data leaves as cleanly as it arrives, so it stays usable in registries, data spaces and your own systems.",
    context:
      "Why Athena card 1 body.",
    category: "landing",
    maxLength: 280,
    needsTranslation: true,
  },
  "landing.why_standards_proof": {
    text: "16 FHIR-aligned tables, 636 fields",
    context:
      "Why Athena card 1 proof line. Verified numbers, do not round.",
    category: "landing",
    maxLength: 60,
    needsTranslation: true,
  },
  "landing.why_governance_title": {
    text: "Your data stays governed by you",
    context:
      "Why Athena card 2 title: data governance.",
    category: "landing",
    maxLength: 60,
    needsTranslation: true,
  },
  "landing.why_governance_desc": {
    text: "Each institution keeps its own environment. Consortia share a view of the cohort while every centre controls who may read its records and who may export them. You set the terms with CROs and industry partners.",
    context:
      "Why Athena card 2 body.",
    category: "landing",
    maxLength: 280,
    needsTranslation: true,
  },
  "landing.why_governance_proof": {
    text: "Per-centre read and export control",
    context:
      "Why Athena card 2 proof chip.",
    category: "landing",
    maxLength: 60,
    needsTranslation: true,
  },
  "landing.why_cohort_title": {
    text: "From free text to a queryable cohort",
    context:
      "Why Athena card 3 title.",
    category: "landing",
    maxLength: 60,
    needsTranslation: true,
  },
  "landing.why_cohort_desc": {
    text: "Reports, notes and scans become structured fields you can question in plain language, then turn into cohorts. Trial matching shows its eligibility reasoning, so a clinician can judge it rather than trust it.",
    context:
      "Why Athena card 3 body. Makes NO outcome claim: public page, not a certified medical device.",
    category: "landing",
    maxLength: 280,
    needsTranslation: true,
  },
  "landing.why_cohort_proof": {
    text: "44 cancer types, matched to ClinicalTrials.gov",
    context:
      "Why Athena card 3 proof chip. 44 = the cancer.* registry minus the `other` bucket. NOT 45, NOT 50+.",
    category: "landing",
    maxLength: 70,
    needsTranslation: true,
  },
  "landing.step_upload_title": {
    text: "Upload",
    context: "Step 1 title in the landing page 'How It Works' section. Refers to uploading clinical documents.",
    category: "landing",
    maxLength: 20,
    needsTranslation: true,
  },
  "landing.step_upload_desc": {
    text: "Clinical documents, pathology reports, scans, voice notes, or bulk ZIP archives.",
    context: "Step 1 description in the landing page 'How It Works' section. Lists the types of files the platform accepts.",
    category: "landing",
    maxLength: 120,
    needsTranslation: true,
  },
  "landing.step_extract_title": {
    text: "Extract",
    context: "Step 2 title in the landing page 'How It Works' section. Refers to AI-powered data extraction.",
    category: "landing",
    maxLength: 20,
    needsTranslation: true,
  },
  "landing.step_extract_desc": {
    text: "AI structures data across 44 cancer types: molecular, hematological, and cancer-specific fields.",
    context: "Step 2 description in the landing page 'How It Works' section. Explains the breadth of AI extraction capabilities.",
    category: "landing",
    maxLength: 120,
    needsTranslation: true,
  },
  "landing.step_validate_title": {
    text: "Validate",
    context: "Step 3 title in the landing page 'How It Works' section. Refers to reviewing and approving AI-extracted data.",
    category: "landing",
    maxLength: 20,
    needsTranslation: true,
  },
  "landing.step_validate_desc": {
    text: "Review, refine, and approve extracted tables with inline editing and audit trails.",
    context: "Step 3 description in the landing page 'How It Works' section. Explains the validation workflow.",
    category: "landing",
    maxLength: 120,
    needsTranslation: true,
  },
  "landing.step_analyze_title": {
    text: "Analyze",
    context: "Step 4 title in the landing page 'How It Works' section. Refers to cohort analytics and dashboards.",
    category: "landing",
    maxLength: 20,
    needsTranslation: true,
  },
  "landing.step_analyze_desc": {
    text: "Dynamic dashboards, natural language queries, cohort analysis, and filtered data export.",
    context: "Step 4 description in the landing page 'How It Works' section. Describes the analytics and export features.",
    category: "landing",
    maxLength: 120,
    needsTranslation: true,
  },

  // ============================================================================
  // PATIENT ACTIONS (ActionPanel, Dashboard)
  // ============================================================================
  "patient.delete_title": {
    text: "Delete Patient?",
    context: "Title of the confirmation dialog when deleting a patient.",
    category: "patient",
    maxLength: 30,
    needsTranslation: true,
  },
  "patient.delete_confirm": {
    text: "Are you sure you want to delete {{name}}? This will also delete all related tables and data. This action cannot be undone.",
    context: "Confirmation dialog body when deleting a patient. {{name}} is the patient name.",
    category: "patient",
    maxLength: 200,
    needsTranslation: true,
  },
  "patient.delete_failed": {
    text: "Failed to delete patient",
    context: "Error notification when patient deletion fails.",
    category: "patient",
    maxLength: 40,
    needsTranslation: true,
  },
  "patient.deleted_success": {
    text: "Patient deleted successfully",
    context: "Success notification after deleting a patient.",
    category: "patient",
    maxLength: 40,
    needsTranslation: true,
  },
  "patient.create_button": {
    text: "Create Patient",
    context: "Button label to create a new patient on the dashboard.",
    category: "patient",
    maxLength: 25,
    needsTranslation: true,
  },
  "patient.unknown": {
    text: "Unknown Patient",
    context: "Fallback name displayed when a patient has no name.",
    category: "patient",
    maxLength: 25,
    needsTranslation: true,
  },

  // ============================================================================
  // EDITABLE TABLE (EditableTable.tsx)
  // ============================================================================
  "editable.lock_tooltip": {
    text: "Locked — AI will not modify this record",
    context: "Tooltip shown on the lock icon when a record is locked.",
    category: "editable",
    maxLength: 60,
    needsTranslation: true,
  },
  "editable.reference_none": {
    text: "None",
    context: "Option in a reference/FK picker (e.g. a Procedure's linked Diagnosis) that unassigns the link, leaving it empty.",
    category: "editable",
    maxLength: 20,
    needsTranslation: true,
  },
  "editable.reference_unspecified": {
    text: "(unspecified)",
    context: "Fallback label for a reference/FK picker option whose display field is empty (e.g. a Diagnosis with no cancer type set).",
    category: "editable",
    maxLength: 24,
    needsTranslation: true,
  },
  "editable.source_user": {
    text: "Created by user",
    context: "Tooltip for the source icon when a record was manually created.",
    category: "editable",
    maxLength: 30,
    needsTranslation: true,
  },
  "editable.source_edited": {
    text: "Edited by user (AI extracted, then modified)",
    context: "Tooltip for the source icon when an AI-extracted record was later edited by a human.",
    category: "editable",
    maxLength: 60,
    needsTranslation: true,
  },
  "editable.source_ai": {
    text: "AI extracted",
    context: "Tooltip for the source icon when a record was extracted by AI.",
    category: "editable",
    maxLength: 20,
    needsTranslation: true,
  },
  "editable.record_locked": {
    text: "Record locked",
    context: "Success notification when a record is locked.",
    category: "editable",
    maxLength: 20,
    needsTranslation: true,
  },
  "editable.field_locked": {
    text: "Field locked",
    context: "Success notification when a single field is locked against AI overwrite (per-field lock).",
    category: "editable",
    maxLength: 20,
    needsTranslation: true,
  },
  "editable.field_unlocked": {
    text: "Field unlocked",
    context: "Success notification when a single field is unlocked (AI may update it again).",
    category: "editable",
    maxLength: 20,
    needsTranslation: true,
  },
  "editable.toggle_field_lock_failed": {
    text: "Failed to toggle field lock",
    context: "Error notification when toggling a single field's lock fails.",
    category: "editable",
    maxLength: 40,
    needsTranslation: true,
  },
  "editable.lock_field": {
    text: "Lock field (protect from AI changes)",
    context: "Tooltip on the per-field lock button in the field editor when the field is currently unlocked.",
    category: "editable",
    maxLength: 50,
    needsTranslation: true,
  },
  "editable.unlock_field": {
    text: "Unlock field (let AI update it again)",
    context: "Tooltip on the per-field lock button in the field editor when the field is currently locked.",
    category: "editable",
    maxLength: 50,
    needsTranslation: true,
  },
  "editable.record_unlocked": {
    text: "Record unlocked",
    context: "Success notification when a record is unlocked.",
    category: "editable",
    maxLength: 20,
    needsTranslation: true,
  },
  "editable.toggle_lock_failed": {
    text: "Failed to toggle lock",
    context: "Error notification when locking/unlocking a record fails.",
    category: "editable",
    maxLength: 30,
    needsTranslation: true,
  },
  "editable.save_failed": {
    text: "Failed to save {{field}}",
    context: "Error notification when saving a field value fails. {{field}} is the field name.",
    category: "editable",
    maxLength: 40,
    needsTranslation: true,
  },
  "editable.field_added": {
    text: "Added {{field}}",
    context: "Success notification when a new field is added. {{field}} is the field name.",
    category: "editable",
    maxLength: 30,
    needsTranslation: true,
  },
  "editable.add_field_failed": {
    text: "Failed to add field",
    context: "Error notification when adding a field fails.",
    category: "editable",
    maxLength: 30,
    needsTranslation: true,
  },
  "editable.patient_id_required": {
    text: "Patient ID is required to create events",
    context: "Error notification when trying to create an event without a patient ID.",
    category: "editable",
    maxLength: 50,
    needsTranslation: true,
  },
  "editable.event_created": {
    text: "Created {{eventType}} event",
    context: "Success notification when a new event is created. {{eventType}} is the event type.",
    category: "editable",
    maxLength: 40,
    needsTranslation: true,
  },
  "editable.create_event_failed": {
    text: "Failed to create event",
    context: "Error notification when event creation fails.",
    category: "editable",
    maxLength: 30,
    needsTranslation: true,
  },
  "editable.no_data": {
    text: "No data available",
    context: "Empty state message shown in an editable table when there is no data.",
    category: "editable",
    maxLength: 25,
    needsTranslation: true,
  },
  "editable.column_field": {
    text: "Field",
    context: "Column header for the field name column in editable tables.",
    category: "editable",
    maxLength: 10,
    needsTranslation: true,
  },
  "editable.column_value": {
    text: "Value",
    context: "Column header for the value column in editable tables.",
    category: "editable",
    maxLength: 10,
    needsTranslation: true,
  },
  "editable.add_field": {
    text: "Add field",
    context: "Tooltip for the button that adds a new field to a table.",
    category: "editable",
    maxLength: 15,
    needsTranslation: true,
  },
  "editable.add_field_to_event": {
    text: "Add field to this event",
    context: "Tooltip for the button that adds a new field to a specific event.",
    category: "editable",
    maxLength: 30,
    needsTranslation: true,
  },
  "editable.unlock_record": {
    text: "Unlock record",
    context: "Tooltip for the button that unlocks a locked record.",
    category: "editable",
    maxLength: 20,
    needsTranslation: true,
  },
  "editable.lock_record": {
    text: "Lock record (prevent AI changes)",
    context: "Tooltip for the button that locks a record to prevent AI modifications.",
    category: "editable",
    maxLength: 40,
    needsTranslation: true,
  },

  // ============================================================================
  // CHAT INTERFACE (ChatInterface.tsx)
  // ============================================================================
  "chat.transcribing": {
    text: "Transcribing audio...",
    context: "Processing message shown while audio is being transcribed.",
    category: "chat",
    maxLength: 30,
    needsTranslation: true,
  },
  "chat.transcription_failed_no_text": {
    text: "Transcription failed — no text received from backend",
    context: "Error notification when audio transcription returns no text.",
    category: "chat",
    maxLength: 60,
    needsTranslation: true,
  },
  "chat.transcription_failed": {
    text: "Transcription failed: {{error}}",
    context: "Error notification when audio transcription fails. {{error}} is the error message.",
    category: "chat",
    maxLength: 60,
    needsTranslation: true,
  },
  "chat.recovering_recording": {
    text: "Recovering recording...",
    context: "Processing message shown while recovering a previously interrupted recording.",
    category: "chat",
    maxLength: 30,
    needsTranslation: true,
  },
  "chat.recording_recovered": {
    text: "Recording recovered successfully!",
    context: "Success notification after recovering a previously interrupted recording.",
    category: "chat",
    maxLength: 40,
    needsTranslation: true,
  },
  "chat.recover_confirm": {
    text: "You have an unfinished recording from {{date}}.\n\nWould you like to recover and transcribe it?",
    context: "Confirmation dialog asking if the user wants to recover a previous recording. {{date}} is the date/time.",
    category: "chat",
    maxLength: 120,
    needsTranslation: true,
  },
  "chat.saving_message": {
    text: "Saving message...",
    context: "Processing message shown while a chat message is being saved.",
    category: "chat",
    maxLength: 30,
    needsTranslation: true,
  },
  "chat.message_saved": {
    text: "Message saved and processing started",
    context: "Success notification after a chat message is saved and extraction begins.",
    category: "chat",
    maxLength: 50,
    needsTranslation: true,
  },
  "chat.save_failed": {
    text: "Failed to save message",
    context: "Error notification when saving a chat message fails.",
    category: "chat",
    maxLength: 30,
    needsTranslation: true,
  },
  "chat.upload_progress": {
    text: "Uploading {{current}} of {{total}}: {{name}}",
    context: "Processing message showing file upload progress. {{current}}, {{total}}, {{name}} are placeholders.",
    category: "chat",
    maxLength: 60,
    needsTranslation: true,
  },
  "chat.upload_failed": {
    text: "Failed to upload {{name}}",
    context: "Error notification when a file upload fails. {{name}} is the file name.",
    category: "chat",
    maxLength: 40,
    needsTranslation: true,
  },
  "chat.upload_success": {
    text: "{{count}} file(s) uploaded successfully",
    context: "Success notification after files are uploaded. {{count}} is the number of files.",
    category: "chat",
    maxLength: 50,
    needsTranslation: true,
  },
  "chat.upload_partial": {
    text: "{{success}} uploaded, {{failed}} failed",
    context: "Warning notification when some uploads succeeded and some failed.",
    category: "chat",
    maxLength: 40,
    needsTranslation: true,
  },
  "chat.uploading_photo": {
    text: "Uploading photo...",
    context: "Processing message shown while a photo is being uploaded.",
    category: "chat",
    maxLength: 25,
    needsTranslation: true,
  },
  "chat.photo_upload_failed": {
    text: "Failed to upload photo",
    context: "Error notification when photo upload fails.",
    category: "chat",
    maxLength: 30,
    needsTranslation: true,
  },
  "chat.processing": {
    text: "Processing...",
    context: "Generic processing status shown in the chat interface.",
    category: "chat",
    maxLength: 20,
    needsTranslation: true,
  },
  "chat.extracting": {
    text: "Extracting clinical data from documents...",
    context: "Status message shown while AI is extracting data from uploaded documents.",
    category: "chat",
    maxLength: 50,
    needsTranslation: true,
  },
  "chat.voice_hint": {
    text: "Voice note transcribed — Edit and press Enter to send",
    context: "Hint shown after a voice note is transcribed, prompting the user to edit and send.",
    category: "chat",
    maxLength: 60,
    needsTranslation: true,
  },
  "chat.cancel": {
    text: "Cancel",
    context: "Cancel button in the chat interface (e.g., cancel a pending voice transcription).",
    category: "chat",
    maxLength: 10,
    needsTranslation: true,
  },
  "chat.user_fallback": {
    text: "User",
    context: "Fallback display name when the current user has no name set.",
    category: "chat",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // DATA PANEL (DataPanel.tsx)
  // ============================================================================
  "data_panel.hematological": {
    text: "Hematological Profile",
    context: "Section header for the hematological profile table in the patient data panel.",
    category: "data_panel",
    maxLength: 30,
    needsTranslation: true,
  },
  "data_panel.trial_enrollment": {
    text: "Clinical Trial Enrollment",
    context: "Section header for the clinical trial enrollment table in the patient data panel.",
    category: "data_panel",
    maxLength: 30,
    needsTranslation: true,
  },
  "data_panel.adverse_events": {
    text: "Adverse Events",
    context: "Section header for the adverse events / toxicity table in the patient data panel.",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "data_panel.medications": {
    text: "Medications",
    context: "Section header for the concomitant / home medications (drug B) table in the patient data panel. Covers prescription home meds, OTC drugs, supplements and herbals, not the anticancer therapy.",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "data_panel.family_history": {
    text: "Family History",
    context: "Section header for the family history table in the patient data panel. Covers conditions in the patient's blood relatives (hereditary cancer risk), not the patient's own diagnoses.",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "data_panel.diagnoses": {
    text: "Diagnoses",
    context: "Section header for the diagnoses table in the patient data panel. Lists the patient's cancer diagnoses (cancer type, histology, coding).",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "data_panel.procedures": {
    text: "Procedures",
    context: "Section header for the procedures table in the patient data panel. Lists clinical procedures performed (surgery, biopsy, imaging-guided procedures, coded interventions).",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "data_panel.tumor_assessment": {
    text: "Staging",
    context: "Section header (and sidebar nav label) for the tumor assessment / staging table in the patient data panel. Lists TNM categories, stage group, staging system, ECOG, Breslow thickness, ulceration and assessment date for each staging event.",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "data_panel.disease_status": {
    text: "Disease Status",
    context: "Section header (and sidebar nav label) for the disease status table in the patient data panel. Tracks the clinical disease status over time (e.g. no evidence of disease, stable disease, progression) with response assessment and ECOG performance status per status date.",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "data_panel.imaging": {
    text: "Imaging",
    context: "Section header (and sidebar nav label) for the imaging table in the patient data panel. Lists radiology / imaging studies (e.g. CT, MRI, PET, ultrasound) with modality, study date, body site and findings per study.",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "labresult.qualitative_result": {
    text: "Qualitative Result",
    context: "Tooltip shown on a Lab Result table cell in the 'Value' column when the row has no numeric value and the cell is instead showing the qualitative result (e.g. 'detected', 'positive (Allred 8/8)'). Clarifies that the displayed text is the qualitative result field, not a numeric measurement. Fallback label; the backend field's verbose_name is used when available.",
    category: "data_panel",
    maxLength: 24,
    needsTranslation: true,
  },
  "imaging.image_column": {
    text: "Image",
    context: "Header of the leftmost thumbnail column in the Imaging study table. Each row can show a small preview of the source disease image the study was read from.",
    category: "data_panel",
    maxLength: 12,
    needsTranslation: true,
  },
  "imaging.view_image": {
    text: "View full image",
    context: "Tooltip on an imaging-study thumbnail. Clicking opens the full-size image in an in-app lightbox viewer (zoom, pan and prev/next navigation).",
    category: "data_panel",
    maxLength: 24,
    needsTranslation: true,
  },
  "imaging.no_image": {
    text: "No image",
    context: "Tooltip on the placeholder shown in the Imaging table thumbnail column when an imaging-study row has no linked source image.",
    category: "data_panel",
    maxLength: 16,
    needsTranslation: true,
  },
  "imaging.image_alt": {
    text: "Imaging study",
    context: "Alt text for the imaging-study image thumbnail / preview (accessibility, screen readers).",
    category: "data_panel",
    maxLength: 24,
    needsTranslation: true,
  },
  "imaging.lightbox_title": {
    text: "Image viewer",
    context: "Accessible label for the full-screen image lightbox overlay that opens when a clinical image thumbnail is clicked. Shows one image at a time with zoom, pan and prev/next navigation.",
    category: "data_panel",
    maxLength: 24,
    needsTranslation: true,
  },
  "imaging.lightbox_close": {
    text: "Close",
    context: "Tooltip / aria-label for the X button that closes the full-screen clinical-image lightbox viewer.",
    category: "data_panel",
    maxLength: 16,
    needsTranslation: true,
  },
  "imaging.lightbox_prev": {
    text: "Previous",
    context: "Tooltip / aria-label for the left-arrow button that shows the previous image in the clinical-image lightbox carousel.",
    category: "data_panel",
    maxLength: 16,
    needsTranslation: true,
  },
  "imaging.lightbox_next": {
    text: "Next",
    context: "Tooltip / aria-label for the right-arrow button that shows the next image in the clinical-image lightbox carousel.",
    category: "data_panel",
    maxLength: 16,
    needsTranslation: true,
  },
  "imaging.lightbox_zoom_in": {
    text: "Zoom in",
    context: "Tooltip / aria-label for the zoom-in control in the clinical-image lightbox viewer.",
    category: "data_panel",
    maxLength: 16,
    needsTranslation: true,
  },
  "imaging.lightbox_zoom_out": {
    text: "Zoom out",
    context: "Tooltip / aria-label for the zoom-out control in the clinical-image lightbox viewer.",
    category: "data_panel",
    maxLength: 16,
    needsTranslation: true,
  },
  "imaging.lightbox_reset": {
    text: "Reset view",
    context: "Tooltip / aria-label for the control that resets the zoom and pan of the current image back to fit-to-screen in the clinical-image lightbox viewer.",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "diagnosis.linked_images": {
    text: "Lesion images",
    context: "Section header shown at the top of a Diagnosis record's detail modal, above a small gallery of clinical photographs (thumbnails) of that specific lesion/tumour. Clicking a thumbnail opens the full-size image.",
    category: "data_panel",
    maxLength: 24,
    needsTranslation: true,
  },
  "data_panel.systemic_therapy": {
    text: "Systemic Therapy",
    context: "Section header (and sidebar nav label) for the systemic therapy table in the patient data panel. Lists systemic oncology treatments (e.g. chemotherapy, immunotherapy, targeted therapy) with therapy type, drug name, start date and response assessment per line of treatment.",
    category: "data_panel",
    maxLength: 24,
    needsTranslation: true,
  },
  "data_panel.lab_results": {
    text: "Lab Results",
    context: "Section header (and sidebar nav label) for the laboratory results table in the patient data panel. Lists individual lab test results (e.g. LDH, blood counts, tumour markers) with analyte name, measured value, unit and result date, one row per measurement.",
    category: "data_panel",
    maxLength: 24,
    needsTranslation: true,
  },
  "data_panel.add_record_short": {
    text: "Add",
    context: "Label for the '+ Add' chip on the Medications and Adverse Events section headers that creates a new record. Mirrors the domain History '+ Event' button.",
    category: "data_panel",
    maxLength: 12,
    needsTranslation: true,
  },
  "data_panel.medication_fallback": {
    text: "Medication {{n}}",
    context: "Fallback label for a medication record in the sidebar when it has no medication name yet. {{n}} is the 1-based record index.",
    category: "data_panel",
    maxLength: 25,
    needsTranslation: true,
  },
  "data_panel.adverse_event_fallback": {
    text: "Adverse Event {{n}}",
    context: "Fallback label for an adverse event record in the sidebar when it has no CTCAE term yet. {{n}} is the 1-based record index.",
    category: "data_panel",
    maxLength: 25,
    needsTranslation: true,
  },
  "data_panel.diagnosis_fallback": {
    text: "Diagnosis {{n}}",
    context: "Fallback label for a diagnosis record in the sidebar when it has no cancer type / histology yet. {{n}} is the 1-based record index.",
    category: "data_panel",
    maxLength: 25,
    needsTranslation: true,
  },
  "data_panel.procedure_fallback": {
    text: "Procedure {{n}}",
    context: "Fallback label for a procedure record in the sidebar when it has no procedure name yet. {{n}} is the 1-based record index.",
    category: "data_panel",
    maxLength: 25,
    needsTranslation: true,
  },
  "data_panel.tumor_assessment_fallback": {
    text: "Staging {{n}}",
    context: "Fallback label for a tumor assessment / staging record in the sidebar when it has no stage group yet. {{n}} is the 1-based record index.",
    category: "data_panel",
    maxLength: 25,
    needsTranslation: true,
  },
  "data_panel.disease_status_fallback": {
    text: "Disease Status {{n}}",
    context: "Fallback label for a disease status record in the sidebar when it has no status date yet. {{n}} is the 1-based record index.",
    category: "data_panel",
    maxLength: 25,
    needsTranslation: true,
  },
  "data_panel.imaging_fallback": {
    text: "Imaging {{n}}",
    context: "Fallback label for an imaging study record in the sidebar when it has no modality yet. {{n}} is the 1-based record index.",
    category: "data_panel",
    maxLength: 25,
    needsTranslation: true,
  },
  "data_panel.systemic_therapy_fallback": {
    text: "Systemic Therapy {{n}}",
    context: "Fallback label for a systemic therapy record in the sidebar when it has no drug name or therapy type yet. {{n}} is the 1-based record index.",
    category: "data_panel",
    maxLength: 30,
    needsTranslation: true,
  },
  "data_panel.lab_results_fallback": {
    text: "Lab Result {{n}}",
    context: "Fallback label for a laboratory result record in the sidebar when it has no analyte name yet. {{n}} is the 1-based record index.",
    category: "data_panel",
    maxLength: 30,
    needsTranslation: true,
  },
  "data_panel.family_history_fallback": {
    text: "Family History {{n}}",
    context: "Fallback label for a family history record in the sidebar when it has no relationship or condition yet. {{n}} is the 1-based record index.",
    category: "data_panel",
    maxLength: 30,
    needsTranslation: true,
  },
  "data_panel.find_trials": {
    text: "Find Clinical Trials",
    context: "Tooltip for the button that navigates to the clinical trial matching panel.",
    category: "data_panel",
    maxLength: 30,
    needsTranslation: true,
  },
  "data_panel.find_trials_via": {
    text: "via ClinicalTrials.gov (NIH)",
    context: "Secondary text explaining the source of clinical trial data.",
    category: "data_panel",
    maxLength: 40,
    needsTranslation: true,
  },
  "data_panel.empty_section_title": {
    text: "No data available",
    context: "Bold heading shown inside the empty-state card when a clinical section has no records.",
    category: "data_panel",
    maxLength: 30,
    needsTranslation: true,
  },
  "data_panel.empty_section_hint": {
    text: "Create a record to start adding clinical data to this section.",
    context: "Hint text below the empty-state heading, explaining the user can create a new record.",
    category: "data_panel",
    maxLength: 80,
    needsTranslation: true,
  },
  "data_panel.create_record": {
    text: "Create record",
    context: "Chip label for the button that creates a new table record in an empty section.",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "data_panel.create_diagnosis_record": {
    text: "Diagnosis record",
    context: "Chip label for the button that creates a new diagnosis (static) table record.",
    category: "data_panel",
    maxLength: 25,
    needsTranslation: true,
  },
  "data_panel.add_event": {
    text: "Add event",
    context: "Chip label for the button that creates a new event table record.",
    category: "data_panel",
    maxLength: 15,
    needsTranslation: true,
  },

  // ============================================================================
  // TABLE SIDEBAR (TableSidebar.tsx)
  // ============================================================================
  "table_sidebar.patient_data": {
    text: "Patient Data",
    context: "Small header label above the table navigation in patient details.",
    category: "data_panel",
    maxLength: 20,
    needsTranslation: true,
  },
  "table_sidebar.universal": {
    text: "Universal",
    context: "Section label for universal (cross-domain) clinical tables in the sidebar.",
    category: "data_panel",
    maxLength: 15,
    needsTranslation: true,
  },
  "table_sidebar.static_data": {
    text: "Baseline",
    context: "Sidebar nav item for a cancer domain's static/baseline table (diagnosis snapshot + baseline characteristics). Previously labeled 'Diagnosis & Baseline'.",
    category: "data_panel",
    maxLength: 25,
    needsTranslation: true,
  },
  "table_sidebar.events": {
    text: "History",
    context: "Sidebar nav item for a cancer domain's clinical events timeline table. Full form: 'History (Procedures, Treatments & Follow-up)'. Previously labeled 'Patient Journey' / 'Events'.",
    category: "data_panel",
    maxLength: 25,
    needsTranslation: true,
  },
  "table_sidebar.add_event": {
    text: "Add event",
    context: "Tooltip for the + button in the sidebar to add a new clinical event under Patient Journey.",
    category: "data_panel",
    maxLength: 15,
    needsTranslation: true,
  },
  "table_sidebar.add_record": {
    text: "Add record",
    context: "Tooltip for the + button on the Medications / Adverse Events sidebar sections that creates a new record. These are multi-row event-style tables, mirroring the domain History 'Add event' button.",
    category: "data_panel",
    maxLength: 15,
    needsTranslation: true,
  },

  // ============================================================================
  // AUTH (useAuth.tsx)
  // ============================================================================
  "auth.login_success": {
    text: "Login successful!",
    context: "Success notification shown after the user logs in.",
    category: "auth",
    maxLength: 25,
    needsTranslation: true,
  },
  "auth.social_duplicate": {
    text: "This email is already registered with another account. Please log in using your original method.",
    context: "Warning shown when a social login email is already linked to a different account.",
    category: "auth",
    maxLength: 120,
    needsTranslation: true,
  },
  "auth.social_failed": {
    text: "Social login failed. Please try again.",
    context: "Error notification when social login (Google/Microsoft/LinkedIn) fails.",
    category: "auth",
    maxLength: 50,
    needsTranslation: true,
  },
  "auth.ok": {
    text: "OK",
    context: "Button label in the snackbar action for social login duplicate warning.",
    category: "auth",
    maxLength: 5,
    needsTranslation: true,
  },

  // ============================================================================
  // ELIGIBLE TRIALS PANEL (EligibleTrialsPanel.tsx) — new keys only
  // Pre-existing keys: network.connection_restored, network.offline,
  //   trials.eligible_title, trials.location_placeholder,
  //   trials.change_location_tooltip, trials.worldwide, trials.enrolled
  // ============================================================================
  "trials.search_more": {
    text: "Search More",
    context: "Button to search for additional clinical trial matches.",
    category: "trials",
    maxLength: 15,
    needsTranslation: true,
  },
  "trials.empty_title": {
    text: "Find recruiting clinical trials that match this patient's profile.",
    context: "Empty state message encouraging the user to search for trial matches.",
    category: "trials",
    maxLength: 80,
    needsTranslation: true,
  },
  "trials.find_matches": {
    text: "Find Matches",
    context: "Button label to start clinical trial matching.",
    category: "trials",
    maxLength: 20,
    needsTranslation: true,
  },
  "trials.analyzing": {
    text: "Analysing patient profile and matching against ClinicalTrials.gov...",
    context: "Loading message shown while the system searches for matching clinical trials.",
    category: "trials",
    maxLength: 80,
    needsTranslation: true,
  },
  "trials.re_evaluate": {
    text: "Re-evaluate",
    context: "Button to re-run clinical trial matching with updated patient data.",
    category: "trials",
    maxLength: 15,
    needsTranslation: true,
  },
  "trials.incomplete_warning": {
    text: "{{count}} of {{total}} trials could not be fully evaluated. Results may be incomplete.",
    context: "Warning when some trials could not be fully evaluated. {{count}} and {{total}} are numbers.",
    category: "trials",
    maxLength: 100,
    needsTranslation: true,
  },
  "trials.no_matches": {
    text: "No matching trials found.",
    context: "Message shown when no clinical trials match the patient's profile.",
    category: "trials",
    maxLength: 35,
    needsTranslation: true,
  },
  "trials.retry": {
    text: "Retry",
    context: "Button to retry clinical trial matching after a failure.",
    category: "trials",
    maxLength: 10,
    needsTranslation: true,
  },
  "trials.refresh": {
    text: "Refresh Matches",
    context: "Button to refresh clinical trial matching results.",
    category: "trials",
    maxLength: 20,
    needsTranslation: true,
  },
  "trials.confidence": {
    text: "{{percent}}% confidence",
    context: "Tooltip showing the confidence score for a trial match. {{percent}} is a number.",
    category: "trials",
    maxLength: 25,
    needsTranslation: true,
  },
  "trials.enrolling": {
    text: "Enrolling...",
    context: "Button label shown while enrollment is in progress.",
    category: "trials",
    maxLength: 15,
    needsTranslation: true,
  },
  "trials.enroll": {
    text: "Enroll",
    context: "Button label to enroll a patient in a clinical trial.",
    category: "trials",
    maxLength: 10,
    needsTranslation: true,
  },
  "trials.hide_details": {
    text: "Hide Details",
    context: "Button to collapse the details section of a trial match card.",
    category: "trials",
    maxLength: 15,
    needsTranslation: true,
  },
  "trials.show_details": {
    text: "Details",
    context: "Button to expand the details section of a trial match card.",
    category: "trials",
    maxLength: 10,
    needsTranslation: true,
  },
  "trials.criteria_met": {
    text: "Met",
    context: "Label for eligibility criteria that the patient meets.",
    category: "trials",
    maxLength: 10,
    needsTranslation: true,
  },
  "trials.criteria_unmet": {
    text: "Unmet",
    context: "Label for eligibility criteria that the patient does not meet.",
    category: "trials",
    maxLength: 10,
    needsTranslation: true,
  },
  "trials.criteria_unclear": {
    text: "Unclear",
    context: "Label for eligibility criteria that cannot be determined.",
    category: "trials",
    maxLength: 10,
    needsTranslation: true,
  },
  "trials.full_criteria": {
    text: "Full Eligibility Criteria",
    context: "Section header for the complete list of eligibility criteria.",
    category: "trials",
    maxLength: 30,
    needsTranslation: true,
  },
  "trials.no_criteria": {
    text: "No eligibility criteria available.",
    context: "Message shown when a trial has no listed eligibility criteria.",
    category: "trials",
    maxLength: 40,
    needsTranslation: true,
  },
  "trials.more_locations": {
    text: "+{{count}} more",
    context: "Overflow text showing how many additional trial locations are not displayed. {{count}} is a number.",
    category: "trials",
    maxLength: 15,
    needsTranslation: true,
  },
  "trials.likely_eligible": {
    text: "Likely Eligible",
    context: "Verdict label when a patient is likely eligible for a trial.",
    category: "trials",
    maxLength: 20,
    needsTranslation: true,
  },
  "trials.unlikely": {
    text: "Unlikely",
    context: "Verdict label when a patient is unlikely eligible for a trial.",
    category: "trials",
    maxLength: 15,
    needsTranslation: true,
  },
  "trials.verdict_unclear": {
    text: "Unclear",
    context: "Verdict label when eligibility for a trial cannot be determined.",
    category: "trials",
    maxLength: 15,
    needsTranslation: true,
  },

  // ============================================================================
  // BILLING (UsageDashboard, BillingSettings)
  // ============================================================================
  "billing.llm_tokens": {
    text: "LLM Tokens",
    context: "Usage metric label for language model token consumption.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.voice_minutes": {
    text: "Voice Minutes",
    context: "Usage metric label for voice transcription minutes.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.upgrade_plan": {
    text: "Upgrade Plan",
    context: "Button label to upgrade the subscription plan.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.upgrade_now": {
    text: "Upgrade Now",
    context: "CTA button to upgrade immediately.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.free_trial_remaining": {
    text: "Free Trial: {{days}} days remaining. Upgrade to continue using all features.",
    context: "Alert text for users on a free trial. {{days}} is the number of remaining days.",
    category: "billing",
    maxLength: 100,
    needsTranslation: true,
  },
  "billing.approaching_limits": {
    text: "You're approaching your usage limits. Consider upgrading your plan for more capacity.",
    context: "Warning alert when usage is close to the plan limit.",
    category: "billing",
    maxLength: 100,
    needsTranslation: true,
  },
  "billing.billing_period": {
    text: "Billing period:",
    context: "Label before the billing period date range.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.days_remaining": {
    text: "days remaining in this period",
    context: "Text following the number of remaining days in the billing period.",
    category: "billing",
    maxLength: 40,
    needsTranslation: true,
  },
  "billing.usage_history": {
    text: "Usage History",
    context: "Section header for the usage history chart.",
    category: "billing",
    maxLength: 20,
    needsTranslation: true,
  },
  "billing.free_fallback": {
    text: "Free",
    context: "Fallback tier name when no subscription tier is set.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.cancel_subscription_failed": {
    text: "Failed to cancel subscription",
    context: "Error notification when subscription cancellation fails.",
    category: "billing",
    maxLength: 40,
    needsTranslation: true,
  },
  "billing.cancel_confirm_title": {
    text: "Cancel Subscription",
    context: "Title of the subscription cancellation confirmation dialog.",
    category: "billing",
    maxLength: 25,
    needsTranslation: true,
  },
  "billing.cancel_confirm_body": {
    text: "Are you sure you want to cancel your {{tier}} subscription?",
    context: "Confirmation dialog body for cancelling a subscription. {{tier}} is the plan name.",
    category: "billing",
    maxLength: 80,
    needsTranslation: true,
  },
  "billing.cancel_stays_active": {
    text: "Your subscription stays active until the end of the current billing period.",
    context: "Informational text in the cancellation dialog.",
    category: "billing",
    maxLength: 80,
    needsTranslation: true,
  },
  "billing.cancel_downgrade": {
    text: "After that, your account will be moved to the Free plan.",
    context: "Informational text explaining what happens after cancellation.",
    category: "billing",
    maxLength: 70,
    needsTranslation: true,
  },
  "billing.role_owner": {
    text: "Owner",
    context: "User role label in billing/environment settings.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.role_admin": {
    text: "Admin",
    context: "User role label in billing/environment settings.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.role_editor": {
    text: "Editor",
    context: "User role label in billing/environment settings.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.role_contributor": {
    text: "Contributor",
    context: "User role label in billing/environment settings.",
    category: "billing",
    maxLength: 15,
    needsTranslation: true,
  },
  "billing.role_viewer": {
    text: "Viewer",
    context: "User role label in billing/environment settings.",
    category: "billing",
    maxLength: 10,
    needsTranslation: true,
  },
  "billing.role_owner_desc": {
    text: "Full control, can delete environment",
    context: "Description for the Owner role in billing/environment settings.",
    category: "billing",
    maxLength: 50,
    needsTranslation: true,
  },
  "billing.role_admin_desc": {
    text: "Full control except delete",
    context: "Description for the Admin role in billing/environment settings.",
    category: "billing",
    maxLength: 40,
    needsTranslation: true,
  },
  "billing.role_editor_desc": {
    text: "Can view, add, and edit",
    context: "Description for the Editor role in billing/environment settings.",
    category: "billing",
    maxLength: 40,
    needsTranslation: true,
  },
  "billing.role_contributor_desc": {
    text: "Can view and add only",
    context: "Description for the Contributor role in billing/environment settings.",
    category: "billing",
    maxLength: 40,
    needsTranslation: true,
  },
  "billing.role_viewer_desc": {
    text: "Read-only access",
    context: "Description for the Viewer role in billing/environment settings.",
    category: "billing",
    maxLength: 30,
    needsTranslation: true,
  },

  // ============================================================================
  // ADMIN PANELS — new keys only
  // Pre-existing: admin.config_saved, admin.config_save_failed
  // ============================================================================
  "admin.refresh": {
    text: "Refresh",
    context: "Tooltip for the refresh button in admin panels.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_key": {
    text: "Key",
    context: "Column header in the system configuration table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_value": {
    text: "Value",
    context: "Column header in the system configuration table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_description": {
    text: "Description",
    context: "Column header in the system configuration table.",
    category: "admin",
    maxLength: 15,
    needsTranslation: true,
  },
  "admin.col_last_updated": {
    text: "Last Updated",
    context: "Column header in the system configuration table.",
    category: "admin",
    maxLength: 15,
    needsTranslation: true,
  },
  "admin.col_date": {
    text: "Date",
    context: "Column header in admin tables.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_environment": {
    text: "Environment",
    context: "Column header in admin tables.",
    category: "admin",
    maxLength: 15,
    needsTranslation: true,
  },
  "admin.col_status": {
    text: "Status",
    context: "Column header in admin tables.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_size": {
    text: "Size",
    context: "Column header in admin tables.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_duration": {
    text: "Duration",
    context: "Column header in admin tables.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_patients": {
    text: "Patients",
    context: "Column header in admin tables.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_time": {
    text: "Time",
    context: "Column header in admin activity table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_user": {
    text: "User",
    context: "Column header in admin activity table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_action": {
    text: "Action",
    context: "Column header in admin activity table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_resource": {
    text: "Resource",
    context: "Column header in admin activity table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_detail": {
    text: "Detail",
    context: "Column header in admin activity table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.modified": {
    text: "modified",
    context: "Chip label indicating a configuration value has been modified.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.all_filter": {
    text: "All",
    context: "Filter option to show all items in an admin table.",
    category: "admin",
    maxLength: 5,
    needsTranslation: true,
  },
  "admin.no_data": {
    text: "No data available",
    context: "Empty state message in admin analytics tables.",
    category: "admin",
    maxLength: 25,
    needsTranslation: true,
  },
  "admin.col_path": {
    text: "Path",
    context: "Column header for URL path in analytics table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_views": {
    text: "Views",
    context: "Column header for page view count in analytics table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_country": {
    text: "Country",
    context: "Column header for country in analytics table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.col_referrer": {
    text: "Referrer",
    context: "Column header for referrer URL in analytics table.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.period": {
    text: "Period",
    context: "Label for the time period filter in analytics.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.7_days": {
    text: "7 days",
    context: "Time period option in analytics filter.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.14_days": {
    text: "14 days",
    context: "Time period option in analytics filter.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.30_days": {
    text: "30 days",
    context: "Time period option in analytics filter.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },
  "admin.90_days": {
    text: "90 days",
    context: "Time period option in analytics filter.",
    category: "admin",
    maxLength: 10,
    needsTranslation: true,
  },

  // ============================================================================
  // MANAGE ENVIRONMENTS
  // ============================================================================
  "environments.backup_failed": {
    text: "Backup failed",
    context: "Error notification when environment backup fails.",
    category: "environments",
    maxLength: 20,
    needsTranslation: true,
  },
  "environments.preview_failed": {
    text: "Preview failed",
    context: "Error notification when restore preview fails.",
    category: "environments",
    maxLength: 20,
    needsTranslation: true,
  },
  "environments.restore_failed": {
    text: "Restore failed",
    context: "Error notification when environment restore fails.",
    category: "environments",
    maxLength: 20,
    needsTranslation: true,
  },
  "environments.skip_shared": {
    text: "Skip shared (safest)",
    context: "Menu option for restore mode that skips shared records.",
    category: "environments",
    maxLength: 30,
    needsTranslation: true,
  },
  "environments.overwrite_shared": {
    text: "Overwrite shared",
    context: "Menu option for restore mode that overwrites shared records.",
    category: "environments",
    maxLength: 25,
    needsTranslation: true,
  },
  "environments.shared_warning": {
    text: "This will modify data visible to users in other environments.",
    context: "Warning shown when the user selects the overwrite shared restore mode.",
    category: "environments",
    maxLength: 80,
    needsTranslation: true,
  },

  // ============================================================================
  // NOTIFICATIONS
  // ============================================================================
  "notifications.accept_failed": {
    text: "Failed to accept invitation",
    context: "Error notification when accepting an environment invitation fails.",
    category: "notifications",
    maxLength: 35,
    needsTranslation: true,
  },

  // ============================================================================
  // DOCUMENT VIEWER
  // ============================================================================
  "document.loading": {
    text: "Loading document...",
    context: "Loading state message shown while a document is being loaded.",
    category: "document",
    maxLength: 25,
    needsTranslation: true,
  },

  // ============================================================================
  // PATIENT DETAILS LAYOUT
  // ============================================================================
  "patient_details.expand_chat": {
    text: "Expand chat",
    context: "Tooltip for the button that expands the chat panel.",
    category: "patient_details",
    maxLength: 15,
    needsTranslation: true,
  },
};

export default en;
